// ─── FIELD-SPECIFIC STARTER HABITS ─────────────────────────────────────────────
// Every one of the ~324 specific courses (COLLEGE_FIELDS) and jobs
// (WORK_INDUSTRIES) gets its own 10 hand-written, first-person habit
// suggestions with a fitting emoji each — not a shared field-level template
// with the item's name spliced in. Keyed by [field/industry id][exact course
// or job label] so cross-listed items (e.g. "Political Science" under both
// Social Sciences and Law) can carry field-appropriate phrasing.
//
// Genuine overlap between related items in the same field is expected (a
// Nurse and a Physician both get "review patient notes") — that mirrors how
// the obstacle-tagged pool already works — but every entry was written with
// that specific course/job in mind, not generated from a template.

export const COLLEGE_COURSE_HABITS = {
  business: {
    "Business Administration": [
      { name: "Review a business case study", emoji: "📊" },
      { name: "Brainstorm a business idea", emoji: "💡" },
      { name: "Plan tomorrow's study block", emoji: "🗓️" },
      { name: "Follow a business/industry leader or blog", emoji: "📈" },
      { name: "Read one management case study", emoji: "📁" },
      { name: "Practice a SWOT analysis", emoji: "🧩" },
      { name: "Network with a classmate about career plans", emoji: "🤝" },
      { name: "Review a company's annual report", emoji: "📑" },
      { name: "Practice a business presentation", emoji: "🎤" },
      { name: "Update my resume/portfolio", emoji: "📄" },
    ],
    "Accountancy": [
      { name: "Practice a set of journal entries", emoji: "🧮" },
      { name: "Review today's accounting lecture", emoji: "📚" },
      { name: "Reconcile a sample ledger", emoji: "🧾" },
      { name: "Review a filing/deadline calendar", emoji: "📅" },
      { name: "Solve a set of adjusting entries", emoji: "🧾" },
      { name: "Review a tax computation rule", emoji: "🧑‍💼" },
      { name: "Practice a trial balance exercise", emoji: "⚖️" },
      { name: "Read up on an accounting standard update", emoji: "📰" },
      { name: "Prep for my board exam review session", emoji: "📖" },
      { name: "Time myself on a mock exam question", emoji: "⏱️" },
    ],
    "Accounting Information System": [
      { name: "Practice a spreadsheet/accounting software skill", emoji: "💻" },
      { name: "Review today's journal entries", emoji: "🧮" },
      { name: "Read up on a data-security concept", emoji: "🔐" },
      { name: "Audit a sample dataset for errors", emoji: "🔍" },
      { name: "Map a transaction cycle flowchart", emoji: "🔀" },
      { name: "Practice a database query exercise", emoji: "🗃️" },
      { name: "Review an internal-controls concept", emoji: "🔒" },
      { name: "Explore a new accounting software feature", emoji: "🧭" },
      { name: "Read up on a fraud-detection case", emoji: "🕵️" },
      { name: "Back up a sample project file", emoji: "💾" },
    ],
    "Management Accounting": [
      { name: "Work through a cost-accounting problem", emoji: "📈" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Practice a budgeting exercise", emoji: "🧮" },
      { name: "Analyze a cost-variance report", emoji: "📉" },
      { name: "Practice a break-even analysis", emoji: "📐" },
      { name: "Review a standard-costing concept", emoji: "🏷️" },
      { name: "Build a sample budget forecast", emoji: "📆" },
      { name: "Read a management-accounting case", emoji: "📖" },
      { name: "Practice a decision-making exercise", emoji: "🧠" },
      { name: "Review today's problem-set corrections", emoji: "✅" },
    ],
    "Marketing Management": [
      { name: "Analyze one brand's marketing campaign", emoji: "📣" },
      { name: "Sketch a mini marketing plan", emoji: "💡" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Define a target-audience profile", emoji: "🎯" },
      { name: "Study a pricing-strategy example", emoji: "💲" },
      { name: "Draft a sample social media caption", emoji: "📱" },
      { name: "Practice a SWOT for a brand", emoji: "🧩" },
      { name: "Watch a marketing case-study video", emoji: "🎬" },
      { name: "Review the 4Ps for a chosen product", emoji: "🛍️" },
      { name: "Interview a peer about buying habits", emoji: "🗣️" },
    ],
    "Human Resource Management": [
      { name: "Practice a mock interview question", emoji: "🧑‍🤝‍🧑" },
      { name: "Review today's HR case study", emoji: "📚" },
      { name: "Draft a sample job description", emoji: "📝" },
      { name: "Review a sample employee policy", emoji: "📋" },
      { name: "Study a labor-law provision", emoji: "📘" },
      { name: "Practice a performance-review script", emoji: "🎯" },
      { name: "Read an employee-engagement article", emoji: "📰" },
      { name: "Draft a training-plan outline", emoji: "🗂️" },
      { name: "Review a compensation/benefits concept", emoji: "💵" },
      { name: "Practice active-listening for an hour", emoji: "👂" },
    ],
    "Financial Management": [
      { name: "Review a stock or market update", emoji: "💹" },
      { name: "Work through a finance problem set", emoji: "🧮" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Track a personal budget as practice", emoji: "💵" },
      { name: "Practice a ratio-analysis exercise", emoji: "📐" },
      { name: "Review a capital-budgeting concept", emoji: "🏗️" },
      { name: "Read a company's financial statement", emoji: "📄" },
      { name: "Watch one finance explainer video", emoji: "🎬" },
      { name: "Practice a time-value-of-money problem", emoji: "⏳" },
      { name: "Review today's problem-set corrections", emoji: "✅" },
    ],
    "Entrepreneurship": [
      { name: "Jot down a business idea", emoji: "💡" },
      { name: "Work on my business plan", emoji: "📊" },
      { name: "Practice my pitch", emoji: "🎤" },
      { name: "Reach out to a potential mentor/investor", emoji: "🤝" },
      { name: "Research a competitor for my idea", emoji: "🔎" },
      { name: "Sketch a lean canvas draft", emoji: "🗺️" },
      { name: "Talk to one potential customer", emoji: "🗣️" },
      { name: "Estimate my startup costs", emoji: "🧮" },
      { name: "Read a founder's story or interview", emoji: "📖" },
      { name: "Post an update about my project", emoji: "📣" },
    ],
    "Economics": [
      { name: "Read one economics news story", emoji: "📰" },
      { name: "Work through a graph/problem set", emoji: "📈" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Compare two countries' economic indicators", emoji: "🌐" },
      { name: "Plot a supply-and-demand curve", emoji: "📉" },
      { name: "Review an inflation/interest-rate update", emoji: "💹" },
      { name: "Practice an elasticity problem", emoji: "🧮" },
      { name: "Read a central-bank policy note", emoji: "🏦" },
      { name: "Discuss an economic issue with a peer", emoji: "🗣️" },
      { name: "Review today's problem-set corrections", emoji: "✅" },
    ],
    "Business Economics": [
      { name: "Read one business/economic news story", emoji: "📰" },
      { name: "Work through a problem set", emoji: "📈" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Track a market indicator this week", emoji: "📉" },
      { name: "Review a market-structure concept", emoji: "🏭" },
      { name: "Practice a cost-curve problem", emoji: "📈" },
      { name: "Read a business-cycle update", emoji: "🔄" },
      { name: "Track a local business's pricing changes", emoji: "🏷️" },
      { name: "Discuss an economic issue with a peer", emoji: "🗣️" },
      { name: "Review today's problem-set corrections", emoji: "✅" },
    ],
    "Office Administration": [
      { name: "Practice a filing/organization task", emoji: "🗂️" },
      { name: "Improve my typing/software skills", emoji: "⌨️" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Practice a professional phone/email script", emoji: "📞" },
      { name: "Practice a business-letter format", emoji: "✉️" },
      { name: "Organize a sample digital filing system", emoji: "🗂️" },
      { name: "Practice a scheduling/calendar exercise", emoji: "🗓️" },
      { name: "Review a records-management concept", emoji: "📋" },
      { name: "Practice a data-entry speed drill", emoji: "⌨️" },
      { name: "Review a workplace-etiquette tip", emoji: "🤝" },
    ],
    "Hospitality Management": [
      { name: "Practice a guest-service scenario", emoji: "🛎️" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Research a hospitality trend", emoji: "🍽️" },
      { name: "Practice a housekeeping/service standard", emoji: "🧹" },
      { name: "Study a front-desk workflow", emoji: "🗝️" },
      { name: "Practice a complaint-resolution script", emoji: "🗣️" },
      { name: "Review a food-and-beverage service standard", emoji: "🍷" },
      { name: "Read about a hotel brand's guest experience", emoji: "🏨" },
      { name: "Practice a reservation-system exercise", emoji: "💻" },
      { name: "Review today's practicum feedback", emoji: "📝" },
    ],
    "Tourism Management": [
      { name: "Research a travel destination", emoji: "✈️" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Plan a sample itinerary", emoji: "🗺️" },
      { name: "Research a tourism marketing campaign", emoji: "📸" },
      { name: "Study a tour-package pricing example", emoji: "💰" },
      { name: "Read about a local tourism spot", emoji: "🏝️" },
      { name: "Practice a tour-guiding script", emoji: "🎤" },
      { name: "Review a travel-safety protocol", emoji: "🛡️" },
      { name: "Study a country's visa/travel requirement", emoji: "🛂" },
      { name: "Review today's practicum feedback", emoji: "📝" },
    ],
    "Real Estate Management": [
      { name: "Research a property listing", emoji: "🏠" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Study a market trend", emoji: "📈" },
      { name: "Practice a property valuation exercise", emoji: "📐" },
      { name: "Study a lease/contract clause", emoji: "📜" },
      { name: "Practice a property-pitch script", emoji: "🗣️" },
      { name: "Review a zoning/land-use concept", emoji: "🗺️" },
      { name: "Study a mortgage/financing concept", emoji: "🏦" },
      { name: "Compare two neighborhoods' listings", emoji: "🏘️" },
      { name: "Review today's problem-set corrections", emoji: "✅" },
    ],
    "Customs Administration": [
      { name: "Review today's customs procedures notes", emoji: "📦" },
      { name: "Study for my next exam", emoji: "📚" },
      { name: "Read a trade/logistics news update", emoji: "📰" },
      { name: "Review an import/export tariff rule", emoji: "🛃" },
      { name: "Study a harmonized-system code", emoji: "🔢" },
      { name: "Review a customs-valuation rule", emoji: "💲" },
      { name: "Practice a documentation exercise", emoji: "📋" },
      { name: "Read up on a trade-agreement update", emoji: "🌐" },
      { name: "Review a prohibited/restricted-goods list", emoji: "🚫" },
      { name: "Time myself on a mock exam question", emoji: "⏱️" },
    ],
    "Legal Management": [
      { name: "Read one legal case or article", emoji: "⚖️" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Practice legal writing", emoji: "📝" },
      { name: "Draft a sample contract clause", emoji: "📜" },
      { name: "Study a business-law provision", emoji: "📘" },
      { name: "Practice a legal-memo outline", emoji: "📝" },
      { name: "Review a compliance-checklist item", emoji: "✅" },
      { name: "Read a recent legal-management case", emoji: "📰" },
      { name: "Practice a contract-review exercise", emoji: "🔍" },
      { name: "Review today's problem-set corrections", emoji: "✅" },
    ],
    "International Business": [
      { name: "Read one global business news story", emoji: "🌏" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Practice a cross-cultural communication tip", emoji: "🗣️" },
      { name: "Track a foreign exchange rate", emoji: "💱" },
      { name: "Study an incoterm/trade-term", emoji: "🚢" },
      { name: "Read about a trade agreement", emoji: "🌐" },
      { name: "Practice a cross-border pricing exercise", emoji: "💱" },
      { name: "Research a foreign market entry case", emoji: "🗺️" },
      { name: "Learn one phrase in a new language", emoji: "🗣️" },
      { name: "Review today's problem-set corrections", emoji: "✅" },
    ],
    "Supply Chain / Logistics Management": [
      { name: "Review a supply-chain case study", emoji: "📦" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Research a logistics trend", emoji: "🚚" },
      { name: "Map out a sample delivery route", emoji: "🗺️" },
      { name: "Study an inventory-management concept", emoji: "📦" },
      { name: "Practice a demand-forecast exercise", emoji: "📈" },
      { name: "Review a warehousing/storage concept", emoji: "🏭" },
      { name: "Read up on a supply-chain disruption case", emoji: "🚨" },
      { name: "Compare two shipping methods' cost/time", emoji: "🚛" },
      { name: "Review today's problem-set corrections", emoji: "✅" },
    ],
    "Operations Management": [
      { name: "Work through a process/operations problem", emoji: "⚙️" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Study a process-improvement case", emoji: "📊" },
      { name: "Review a workflow diagram", emoji: "🔄" },
      { name: "Study a capacity-planning concept", emoji: "📊" },
      { name: "Practice a quality-control exercise", emoji: "✅" },
      { name: "Review a lean/six-sigma concept", emoji: "🔁" },
      { name: "Read an operations-efficiency case", emoji: "📖" },
      { name: "Map a sample production process", emoji: "🏭" },
      { name: "Review today's problem-set corrections", emoji: "✅" },
    ],
    "Public Administration": [
      { name: "Read up on a public policy issue", emoji: "🏛️" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Practice a policy brief outline", emoji: "📝" },
      { name: "Read up on a local governance issue", emoji: "🗳️" },
      { name: "Study a government-budgeting concept", emoji: "💰" },
      { name: "Read a public-service delivery case", emoji: "🏛️" },
      { name: "Practice a policy-analysis exercise", emoji: "📊" },
      { name: "Review an administrative-law provision", emoji: "📘" },
      { name: "Follow a local government initiative", emoji: "🗞️" },
      { name: "Review today's problem-set corrections", emoji: "✅" },
    ],
  },

  tech: {
    "Computer Science": [
      { name: "Solve one coding problem", emoji: "💻" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Read a CS concept article", emoji: "🧠" },
      { name: "Review a data structure/algorithm concept", emoji: "🧠" },
      { name: "Practice a whiteboard coding problem", emoji: "🧠" },
      { name: "Read documentation for a new library", emoji: "📄" },
      { name: "Trace through a sample algorithm by hand", emoji: "✍️" },
      { name: "Contribute to or explore an open-source repo", emoji: "🌱" },
      { name: "Review my code for a cleanup pass", emoji: "🧹" },
      { name: "Watch a computer-science lecture recording", emoji: "🎥" },
    ],
    "Information Technology": [
      { name: "Practice a hands-on IT task", emoji: "🖥️" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Troubleshoot a sample tech issue", emoji: "🔧" },
      { name: "Set up or maintain a sample system", emoji: "🖨️" },
      { name: "Practice a command-line exercise", emoji: "⌨️" },
      { name: "Review a networking fundamentals concept", emoji: "🌐" },
      { name: "Back up a sample system/config", emoji: "💾" },
      { name: "Read about a new IT certification topic", emoji: "🎓" },
      { name: "Document a troubleshooting step I learned", emoji: "📝" },
      { name: "Practice a helpdesk-ticket scenario", emoji: "🎫" },
    ],
    "Information Systems": [
      { name: "Review a database/systems concept", emoji: "🗄️" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Practice a systems exercise", emoji: "💻" },
      { name: "Diagram a systems workflow", emoji: "🔗" },
      { name: "Practice an ER-diagram exercise", emoji: "🗺️" },
      { name: "Review a systems-analysis concept", emoji: "🔍" },
      { name: "Read about an enterprise-systems case", emoji: "🏢" },
      { name: "Practice a requirements-gathering exercise", emoji: "📋" },
      { name: "Review a project-management concept", emoji: "📆" },
      { name: "Review today's lab exercise corrections", emoji: "✅" },
    ],
    "Computer Engineering": [
      { name: "Review a circuits/hardware concept", emoji: "🔌" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Work on a hardware project", emoji: "🛠️" },
      { name: "Study a processor/architecture concept", emoji: "🧩" },
      { name: "Simulate a logic circuit", emoji: "🔢" },
      { name: "Practice an assembly/low-level exercise", emoji: "🧮" },
      { name: "Read up on an embedded-systems topic", emoji: "🔩" },
      { name: "Solder or wire a practice component", emoji: "🪛" },
      { name: "Review a microcontroller datasheet", emoji: "📄" },
      { name: "Review today's lab exercise corrections", emoji: "✅" },
    ],
    "Software Engineering": [
      { name: "Code for 20 minutes on a project", emoji: "💻" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Practice a design-pattern exercise", emoji: "🧩" },
      { name: "Write a unit test for my project", emoji: "🧪" },
      { name: "Refactor a piece of my own code", emoji: "🧹" },
      { name: "Practice a version-control exercise (git)", emoji: "🌿" },
      { name: "Review a software-architecture concept", emoji: "🏗️" },
      { name: "Read a code-review checklist", emoji: "📋" },
      { name: "Estimate a sample project's scope", emoji: "🗓️" },
      { name: "Watch a software-engineering talk", emoji: "🎥" },
    ],
    "Data Science": [
      { name: "Practice a data-analysis exercise", emoji: "📊" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Work through a stats problem", emoji: "🧮" },
      { name: "Visualize a sample dataset", emoji: "📉" },
      { name: "Clean a messy sample dataset", emoji: "🧹" },
      { name: "Practice a SQL query exercise", emoji: "🗄️" },
      { name: "Read about a machine-learning concept", emoji: "🤖" },
      { name: "Build one chart from a public dataset", emoji: "📊" },
      { name: "Review a statistics fundamentals concept", emoji: "🧮" },
      { name: "Watch a data-science case-study video", emoji: "🎥" },
    ],
    "Cybersecurity": [
      { name: "Read up on a security concept", emoji: "🔐" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Practice a security exercise", emoji: "🛡️" },
      { name: "Practice a penetration-testing exercise", emoji: "🕵️" },
      { name: "Solve a beginner CTF challenge", emoji: "🚩" },
      { name: "Review a common-vulnerability concept", emoji: "🐛" },
      { name: "Practice setting up a firewall rule", emoji: "🧱" },
      { name: "Read a recent breach case study", emoji: "📰" },
      { name: "Review a password/authentication best practice", emoji: "🔑" },
      { name: "Review today's lab exercise corrections", emoji: "✅" },
    ],
    "Game Development / Game Design": [
      { name: "Work on a game project for 20 min", emoji: "🎮" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Sketch a game mechanic idea", emoji: "✏️" },
      { name: "Playtest my project and take notes", emoji: "🕹️" },
      { name: "Prototype one small game mechanic", emoji: "🕹️" },
      { name: "Study a level-design reference", emoji: "🗺️" },
      { name: "Balance a sample gameplay stat", emoji: "⚖️" },
      { name: "Read a game post-mortem article", emoji: "📖" },
      { name: "Playtest someone else's game for feedback", emoji: "🎮" },
      { name: "Watch a game-dev devlog", emoji: "🎥" },
    ],
    "Multimedia Arts": [
      { name: "Work on a design project", emoji: "🎨" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Practice a design tool skill", emoji: "🖌️" },
      { name: "Explore a new multimedia tool", emoji: "🎞️" },
      { name: "Practice a compositing/editing technique", emoji: "🎞️" },
      { name: "Explore a new plugin or effect", emoji: "✨" },
      { name: "Study color theory for a project", emoji: "🎨" },
      { name: "Organize my project assets/files", emoji: "🗂️" },
      { name: "Render and review a work-in-progress", emoji: "🖥️" },
      { name: "Watch a multimedia-production tutorial", emoji: "🎥" },
    ],
    "Library and Information Science": [
      { name: "Practice a cataloging/organization exercise", emoji: "📖" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Research an information-science topic", emoji: "🔎" },
      { name: "Practice a metadata/tagging exercise", emoji: "🏷️" },
      { name: "Practice a classification-system exercise", emoji: "🔠" },
      { name: "Study a digital-archiving concept", emoji: "🗄️" },
      { name: "Read about an information-literacy topic", emoji: "📖" },
      { name: "Practice a reference-interview scenario", emoji: "🗣️" },
      { name: "Review a database-searching technique", emoji: "🔎" },
      { name: "Review today's lab exercise corrections", emoji: "✅" },
    ],
    "Network Technology": [
      { name: "Review a networking concept", emoji: "🌐" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Practice a network configuration exercise", emoji: "🔧" },
      { name: "Diagram a sample network topology", emoji: "🛰️" },
      { name: "Practice a subnetting exercise", emoji: "🔢" },
      { name: "Review a routing/switching concept", emoji: "🔀" },
      { name: "Set up a sample virtual network", emoji: "🖧" },
      { name: "Read about a network-security concept", emoji: "🔐" },
      { name: "Study a network-protocol datasheet", emoji: "📄" },
      { name: "Review today's lab exercise corrections", emoji: "✅" },
    ],
    "Web Development": [
      { name: "Code for 20 minutes on a project", emoji: "💻" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Learn a new web framework/tool", emoji: "🌐" },
      { name: "Test my project on a different device/browser", emoji: "📱" },
      { name: "Practice a CSS layout exercise", emoji: "🎨" },
      { name: "Build a small component from scratch", emoji: "🧩" },
      { name: "Read documentation for a new framework", emoji: "📄" },
      { name: "Review my project's accessibility", emoji: "♿" },
      { name: "Optimize a page's load speed", emoji: "⚡" },
      { name: "Watch a web-dev tutorial", emoji: "🎥" },
    ],
    "Animation": [
      { name: "Work on an animation project", emoji: "🎬" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Sketch a character/frame", emoji: "✏️" },
      { name: "Study a reference for movement/timing", emoji: "🕺" },
      { name: "Practice a walk-cycle exercise", emoji: "🚶" },
      { name: "Study the 12 principles of animation", emoji: "📖" },
      { name: "Block out a scene's key poses", emoji: "🕺" },
      { name: "Review yesterday's frames for polish", emoji: "🔍" },
      { name: "Watch an animated film scene critically", emoji: "🎬" },
      { name: "Explore a new rigging/tool technique", emoji: "🧰" },
    ],
    "Digital Arts": [
      { name: "Work on a digital art piece", emoji: "🎨" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Practice a new digital-art technique", emoji: "🖌️" },
      { name: "Explore a new brush/technique", emoji: "🎨" },
      { name: "Study color/light in a reference image", emoji: "🌈" },
      { name: "Practice a value-study sketch", emoji: "⚫" },
      { name: "Explore a new digital brush set", emoji: "🖌️" },
      { name: "Redraw an old piece to see my progress", emoji: "🔄" },
      { name: "Review an artist's portfolio for inspiration", emoji: "🖼️" },
      { name: "Watch a digital-art speedpaint", emoji: "🎥" },
    ],
  },

  engineering: {
    "Civil Engineering": [
      { name: "Work through a problem set", emoji: "📐" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Sketch a structural concept", emoji: "🏗️" },
      { name: "Study a materials-strength concept", emoji: "🧱" },
      { name: "Practice a load/stress calculation", emoji: "📐" },
      { name: "Review a building-code provision", emoji: "📘" },
      { name: "Study a foundation-design concept", emoji: "🏗️" },
      { name: "Read a case study of a structural failure", emoji: "📰" },
      { name: "Practice a surveying exercise", emoji: "🗺️" },
      { name: "Review today's problem-set corrections", emoji: "✅" },
    ],
    "Electrical Engineering": [
      { name: "Work through a circuits problem", emoji: "⚡" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Sketch a circuit diagram", emoji: "🔌" },
      { name: "Study a power-systems concept", emoji: "🔋" },
      { name: "Practice a power-systems calculation", emoji: "🔋" },
      { name: "Simulate a sample circuit", emoji: "💻" },
      { name: "Review an electrical-code provision", emoji: "📘" },
      { name: "Study a transformer/motor concept", emoji: "🧲" },
      { name: "Read about a renewable-energy system", emoji: "☀️" },
      { name: "Review today's problem-set corrections", emoji: "✅" },
    ],
    "Electronics Engineering": [
      { name: "Work through a problem set", emoji: "🔧" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Study for my board exam", emoji: "📟" },
      { name: "Study a signal-processing concept", emoji: "📡" },
      { name: "Simulate a sample circuit", emoji: "💻" },
      { name: "Practice a component-datasheet reading", emoji: "📄" },
      { name: "Review an embedded-systems concept", emoji: "🔩" },
      { name: "Solder or wire a practice component", emoji: "🪛" },
      { name: "Study a filter/amplifier concept", emoji: "📡" },
      { name: "Time myself on a mock exam question", emoji: "⏱️" },
    ],
    "Mechanical Engineering": [
      { name: "Work through a problem set", emoji: "⚙️" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Sketch a mechanism/design", emoji: "🛠️" },
      { name: "Study a thermodynamics concept", emoji: "🌀" },
      { name: "Practice a stress/strain calculation", emoji: "📐" },
      { name: "Sketch a machine part in CAD", emoji: "🖥️" },
      { name: "Review a fluid-mechanics concept", emoji: "💧" },
      { name: "Study a manufacturing-process concept", emoji: "🏭" },
      { name: "Read a case study of a mechanical failure", emoji: "📰" },
      { name: "Review today's problem-set corrections", emoji: "✅" },
    ],
    "Industrial Engineering": [
      { name: "Work through a process problem", emoji: "📊" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Study a time-and-motion case", emoji: "⏱️" },
      { name: "Study a facility-layout concept", emoji: "📐" },
      { name: "Practice a linear-programming problem", emoji: "🧮" },
      { name: "Map a workflow for bottlenecks", emoji: "🔀" },
      { name: "Review a quality-control concept", emoji: "✅" },
      { name: "Study an ergonomics concept", emoji: "🪑" },
      { name: "Read a lean-manufacturing case", emoji: "📖" },
      { name: "Review today's problem-set corrections", emoji: "✅" },
    ],
    "Chemical Engineering": [
      { name: "Work through a problem set", emoji: "🧪" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Review a lab procedure", emoji: "⚗️" },
      { name: "Review a plant-safety concept", emoji: "🧯" },
      { name: "Practice a mass-balance problem", emoji: "⚖️" },
      { name: "Review a reactor-design concept", emoji: "🧪" },
      { name: "Study a distillation/separation concept", emoji: "🧫" },
      { name: "Read a process-safety case study", emoji: "🧯" },
      { name: "Review a thermodynamics chart", emoji: "🌡️" },
      { name: "Review today's problem-set corrections", emoji: "✅" },
    ],
    "Computer Engineering": [
      { name: "Review a circuits/hardware concept", emoji: "🔌" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Work on a hardware project", emoji: "🛠️" },
      { name: "Study a processor/architecture concept", emoji: "🧩" },
      { name: "Simulate a logic circuit", emoji: "🔢" },
      { name: "Practice an assembly/low-level exercise", emoji: "🧮" },
      { name: "Read up on an embedded-systems topic", emoji: "🔩" },
      { name: "Solder or wire a practice component", emoji: "🪛" },
      { name: "Review a microcontroller datasheet", emoji: "📄" },
      { name: "Review today's lab exercise corrections", emoji: "✅" },
    ],
    "Aerospace Engineering": [
      { name: "Work through a problem set", emoji: "✈️" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Read up on an aerospace concept", emoji: "🚀" },
      { name: "Study an aerodynamics concept", emoji: "🛰️" },
      { name: "Practice an aerodynamics calculation", emoji: "🛩️" },
      { name: "Review a propulsion-systems concept", emoji: "🚀" },
      { name: "Study a flight-control concept", emoji: "🎛️" },
      { name: "Read about a spacecraft/aircraft case", emoji: "📖" },
      { name: "Review a materials-in-aerospace concept", emoji: "🧱" },
      { name: "Review today's problem-set corrections", emoji: "✅" },
    ],
    "Mining Engineering": [
      { name: "Review today's lecture notes", emoji: "⛏️" },
      { name: "Study for my next exam", emoji: "📚" },
      { name: "Read up on a mining concept", emoji: "🪨" },
      { name: "Review a mine-safety concept", emoji: "⛑️" },
      { name: "Study an ore-extraction method", emoji: "⛏️" },
      { name: "Practice a mine-planning exercise", emoji: "🗺️" },
      { name: "Review a rock-mechanics concept", emoji: "🪨" },
      { name: "Read about a mine-rehabilitation case", emoji: "🌱" },
      { name: "Review a ventilation-systems concept", emoji: "💨" },
      { name: "Time myself on a mock exam question", emoji: "⏱️" },
    ],
    "Metallurgical Engineering": [
      { name: "Review today's lecture notes", emoji: "🔩" },
      { name: "Study for my next exam", emoji: "📚" },
      { name: "Review a materials concept", emoji: "⚗️" },
      { name: "Review a heat-treatment concept", emoji: "🔥" },
      { name: "Study a phase-diagram concept", emoji: "📊" },
      { name: "Review a smelting/refining process", emoji: "🔥" },
      { name: "Practice a materials-testing calculation", emoji: "🧮" },
      { name: "Read about an alloy-development case", emoji: "📖" },
      { name: "Review a corrosion-prevention concept", emoji: "🛡️" },
      { name: "Time myself on a mock exam question", emoji: "⏱️" },
    ],
    "Geodetic Engineering": [
      { name: "Practice a survey/mapping exercise", emoji: "🗺️" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Review a measurement concept", emoji: "📏" },
      { name: "Study a GPS/positioning concept", emoji: "🛰️" },
      { name: "Practice a coordinate-computation exercise", emoji: "📍" },
      { name: "Review a datum/projection concept", emoji: "🗺️" },
      { name: "Study a land-titling procedure", emoji: "📜" },
      { name: "Read about a remote-sensing technique", emoji: "🛰️" },
      { name: "Practice reading a topographic map", emoji: "🧭" },
      { name: "Review today's problem-set corrections", emoji: "✅" },
    ],
    "Sanitary Engineering": [
      { name: "Review today's lecture notes", emoji: "💧" },
      { name: "Study for my next exam", emoji: "📚" },
      { name: "Read up on a sanitation concept", emoji: "🚰" },
      { name: "Review a wastewater-treatment concept", emoji: "🏗️" },
      { name: "Study a water-treatment process step", emoji: "🚿" },
      { name: "Practice a hydraulics calculation", emoji: "📐" },
      { name: "Review a sewage-system design concept", emoji: "🏗️" },
      { name: "Read about a public-health engineering case", emoji: "🏥" },
      { name: "Review a sanitation-code provision", emoji: "📘" },
      { name: "Time myself on a mock exam question", emoji: "⏱️" },
    ],
    "Mechatronics Engineering": [
      { name: "Work on a project for 20 min", emoji: "🤖" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Review a systems concept", emoji: "⚙️" },
      { name: "Study a robotics/automation concept", emoji: "🤖" },
      { name: "Wire a small sensor/actuator circuit", emoji: "🔌" },
      { name: "Practice a control-systems exercise", emoji: "🎛️" },
      { name: "Program a simple automation routine", emoji: "💻" },
      { name: "Study a PLC/microcontroller concept", emoji: "🧩" },
      { name: "Read about a robotics case study", emoji: "🤖" },
      { name: "Review today's problem-set corrections", emoji: "✅" },
    ],
    "Petroleum Engineering": [
      { name: "Review today's lecture notes", emoji: "🛢️" },
      { name: "Study for my next exam", emoji: "📚" },
      { name: "Read up on a petroleum concept", emoji: "📊" },
      { name: "Study a drilling/extraction concept", emoji: "⛽" },
      { name: "Study a well-completion concept", emoji: "🛢️" },
      { name: "Practice a reservoir-estimation problem", emoji: "🧮" },
      { name: "Review a drilling-fluids concept", emoji: "💧" },
      { name: "Read about an oilfield-safety case", emoji: "🧯" },
      { name: "Review a production-forecast chart", emoji: "📈" },
      { name: "Time myself on a mock exam question", emoji: "⏱️" },
    ],
    "Agricultural and Biosystems Engineering": [
      { name: "Review today's lecture notes", emoji: "🌱" },
      { name: "Study for my next exam", emoji: "📚" },
      { name: "Read up on a farm-systems concept", emoji: "🚜" },
      { name: "Study an irrigation-systems concept", emoji: "🌾" },
      { name: "Study a machinery-design concept", emoji: "🚜" },
      { name: "Practice an irrigation-calculation exercise", emoji: "💧" },
      { name: "Review a post-harvest processing concept", emoji: "🌾" },
      { name: "Read about a farm-automation case", emoji: "🤖" },
      { name: "Review a soil/water-conservation concept", emoji: "🌱" },
      { name: "Review today's problem-set corrections", emoji: "✅" },
    ],
    "Environmental Engineering": [
      { name: "Review today's lecture notes", emoji: "🌎" },
      { name: "Study for my next exam", emoji: "📚" },
      { name: "Read up on a sustainability concept", emoji: "♻️" },
      { name: "Study a pollution-control concept", emoji: "🌫️" },
      { name: "Study a waste-treatment process step", emoji: "♻️" },
      { name: "Practice an emissions calculation", emoji: "🧮" },
      { name: "Review an environmental-impact concept", emoji: "🌎" },
      { name: "Read about a remediation case study", emoji: "📖" },
      { name: "Review an environmental regulation update", emoji: "📘" },
      { name: "Review today's problem-set corrections", emoji: "✅" },
    ],
    "Materials Engineering": [
      { name: "Review today's lecture notes", emoji: "🔬" },
      { name: "Study for my next exam", emoji: "📚" },
      { name: "Read up on a materials concept", emoji: "🧱" },
      { name: "Study a materials-testing concept", emoji: "🧱" },
      { name: "Study a materials-microstructure concept", emoji: "🔬" },
      { name: "Practice a materials-selection exercise", emoji: "🧩" },
      { name: "Review a heat-treatment process step", emoji: "🔥" },
      { name: "Read about a new-material case study", emoji: "📖" },
      { name: "Review a fatigue/failure-analysis concept", emoji: "⚙️" },
      { name: "Review today's problem-set corrections", emoji: "✅" },
    ],
  },

  health: {
    "Nursing": [
      { name: "Review today's clinical notes", emoji: "🩺" },
      { name: "Rest properly before my next duty", emoji: "💤" },
      { name: "Study for my next exam", emoji: "📚" },
      { name: "Double-check today's medication chart", emoji: "💊" },
      { name: "Practice a vital-signs assessment", emoji: "🌡️" },
      { name: "Review a nursing-care plan format", emoji: "📋" },
      { name: "Practice a patient-communication script", emoji: "🗣️" },
      { name: "Review a drug-dosage calculation", emoji: "🧮" },
      { name: "Read about a common clinical condition", emoji: "📖" },
      { name: "Practice hand-hygiene/infection-control steps", emoji: "🧼" },
    ],
    "Medicine": [
      { name: "Review today's clinical notes", emoji: "🩺" },
      { name: "Study for my next exam", emoji: "📚" },
      { name: "Rest properly before my next duty", emoji: "💤" },
      { name: "Read one medical case study", emoji: "📖" },
      { name: "Review a differential-diagnosis approach", emoji: "🩻" },
      { name: "Practice a physical-exam technique", emoji: "🩺" },
      { name: "Review a drug-interaction concept", emoji: "💊" },
      { name: "Read a clinical-guideline update", emoji: "📘" },
      { name: "Practice a patient-history-taking script", emoji: "🗣️" },
      { name: "Review today's rounds feedback", emoji: "📝" },
    ],
    "Pharmacy": [
      { name: "Review a drug/dosage concept", emoji: "💊" },
      { name: "Study for my next exam", emoji: "📚" },
      { name: "Review a compounding procedure", emoji: "🧪" },
      { name: "Review a drug-interaction chart", emoji: "🧾" },
      { name: "Practice a dosage-calculation exercise", emoji: "🧮" },
      { name: "Review a drug-classification chart", emoji: "💊" },
      { name: "Study a pharmacokinetics concept", emoji: "🧪" },
      { name: "Read about a new drug approval", emoji: "📰" },
      { name: "Practice a patient-counseling script", emoji: "🗣️" },
      { name: "Review today's dispensing-log corrections", emoji: "✅" },
    ],
    "Medical Technology / Medical Laboratory Science": [
      { name: "Review a lab procedure", emoji: "🔬" },
      { name: "Study for my next exam", emoji: "📚" },
      { name: "Log a lab observation", emoji: "🧫" },
      { name: "Calibrate/check lab equipment", emoji: "🧪" },
      { name: "Practice a specimen-handling procedure", emoji: "🧫" },
      { name: "Review a normal-values reference range", emoji: "📊" },
      { name: "Study a staining/testing technique", emoji: "🔬" },
      { name: "Read about a lab-diagnostic case", emoji: "📖" },
      { name: "Practice a quality-control check", emoji: "✅" },
      { name: "Review today's lab-report corrections", emoji: "📝" },
    ],
    "Physical Therapy": [
      { name: "Practice a therapy technique", emoji: "🏃" },
      { name: "Study for my next exam", emoji: "📚" },
      { name: "Review today's clinical notes", emoji: "🧍" },
      { name: "Learn a new stretching/rehab technique", emoji: "🤸" },
      { name: "Practice a range-of-motion assessment", emoji: "🦵" },
      { name: "Review a rehabilitation-protocol step", emoji: "📋" },
      { name: "Study a musculoskeletal condition", emoji: "🦴" },
      { name: "Practice a manual-therapy technique", emoji: "✋" },
      { name: "Read a case study on patient recovery", emoji: "📖" },
      { name: "Review today's clinical feedback", emoji: "📝" },
    ],
    "Occupational Therapy": [
      { name: "Practice a therapy technique", emoji: "✋" },
      { name: "Study for my next exam", emoji: "📚" },
      { name: "Review today's clinical notes", emoji: "🧍" },
      { name: "Learn a new adaptive-equipment technique", emoji: "🖐️" },
      { name: "Practice a functional-assessment exercise", emoji: "🧍" },
      { name: "Review an adaptive-equipment option", emoji: "🦽" },
      { name: "Study a sensory-integration concept", emoji: "🧠" },
      { name: "Practice a fine-motor activity idea", emoji: "✋" },
      { name: "Read a case study on patient independence", emoji: "📖" },
      { name: "Review today's clinical feedback", emoji: "📝" },
    ],
    "Radiologic Technology": [
      { name: "Review today's clinical notes", emoji: "🩻" },
      { name: "Study for my next exam", emoji: "📚" },
      { name: "Review an imaging concept", emoji: "🖥️" },
      { name: "Review a radiation-safety protocol", emoji: "☢️" },
      { name: "Review a positioning technique", emoji: "📐" },
      { name: "Study an imaging-modality concept", emoji: "🩻" },
      { name: "Practice reading a sample image", emoji: "🖥️" },
      { name: "Review a contrast-media protocol", emoji: "💉" },
      { name: "Read about an imaging-technology update", emoji: "📰" },
      { name: "Review today's clinical feedback", emoji: "📝" },
    ],
    "Respiratory Therapy": [
      { name: "Review today's clinical notes", emoji: "🫁" },
      { name: "Study for my next exam", emoji: "📚" },
      { name: "Rest properly before my next duty", emoji: "💤" },
      { name: "Review a ventilator-settings concept", emoji: "💨" },
      { name: "Study a ventilator-mode concept", emoji: "🫁" },
      { name: "Practice an airway-management technique", emoji: "🌬️" },
      { name: "Review a blood-gas interpretation exercise", emoji: "🩸" },
      { name: "Read about a respiratory-condition case", emoji: "📖" },
      { name: "Review a nebulizer/oxygen-therapy protocol", emoji: "💨" },
      { name: "Review today's clinical feedback", emoji: "📝" },
    ],
    "Dentistry": [
      { name: "Review today's clinical notes", emoji: "🦷" },
      { name: "Study for my next exam", emoji: "📚" },
      { name: "Review a sterilization procedure", emoji: "🧼" },
      { name: "Practice a dental-charting exercise", emoji: "🦷" },
      { name: "Practice a tooth-numbering exercise", emoji: "🦷" },
      { name: "Review an anesthesia-technique concept", emoji: "💉" },
      { name: "Study a common oral-pathology case", emoji: "📖" },
      { name: "Practice an instrument-handling drill", emoji: "🪥" },
      { name: "Review a radiograph-reading exercise", emoji: "🩻" },
      { name: "Review today's clinical feedback", emoji: "📝" },
    ],
    "Optometry": [
      { name: "Review today's clinical notes", emoji: "👁️" },
      { name: "Study for my next exam", emoji: "📚" },
      { name: "Practice an eye-exam technique", emoji: "🔬" },
      { name: "Practice a lens-prescription exercise", emoji: "👓" },
      { name: "Practice a visual-acuity test", emoji: "👁️" },
      { name: "Review a refraction technique", emoji: "🔬" },
      { name: "Study an eye-disease case", emoji: "📖" },
      { name: "Practice a lens-fitting exercise", emoji: "👓" },
      { name: "Review a contact-lens care protocol", emoji: "🧴" },
      { name: "Review today's clinical feedback", emoji: "📝" },
    ],
    "Veterinary Medicine": [
      { name: "Review today's clinical notes", emoji: "🐾" },
      { name: "Study for my next exam", emoji: "📚" },
      { name: "Review a treatment procedure", emoji: "💉" },
      { name: "Read up on an animal-health case", emoji: "🐕" },
      { name: "Practice a physical-exam technique", emoji: "🩺" },
      { name: "Review a species-specific care concept", emoji: "🐾" },
      { name: "Study a common animal-disease case", emoji: "📖" },
      { name: "Practice a restraint/handling technique", emoji: "🤲" },
      { name: "Review a dosage-calculation exercise", emoji: "🧮" },
      { name: "Review today's clinical feedback", emoji: "📝" },
    ],
    "Nutrition and Dietetics": [
      { name: "Plan a sample meal plan", emoji: "🥗" },
      { name: "Study for my next exam", emoji: "📚" },
      { name: "Log a nutrition case study", emoji: "🍎" },
      { name: "Calculate a sample nutrient breakdown", emoji: "⚖️" },
      { name: "Study a diet-therapy concept", emoji: "🥦" },
      { name: "Practice a food-exchange calculation", emoji: "🧮" },
      { name: "Read about a nutrition-related disease case", emoji: "📖" },
      { name: "Review a food-labeling regulation", emoji: "🏷️" },
      { name: "Practice a client-counseling script", emoji: "🗣️" },
      { name: "Review today's clinical feedback", emoji: "📝" },
    ],
    "Midwifery": [
      { name: "Review today's clinical notes", emoji: "🤰" },
      { name: "Study for my next exam", emoji: "📚" },
      { name: "Rest properly before my next duty", emoji: "💤" },
      { name: "Review a prenatal-care checklist", emoji: "👶" },
      { name: "Practice a prenatal-assessment technique", emoji: "🤰" },
      { name: "Review a labor-stage concept", emoji: "📋" },
      { name: "Study a newborn-care procedure", emoji: "👶" },
      { name: "Practice a patient-communication script", emoji: "🗣️" },
      { name: "Read about a maternal-health case", emoji: "📖" },
      { name: "Review today's clinical feedback", emoji: "📝" },
    ],
    "Public Health": [
      { name: "Read up on a public-health issue", emoji: "🏥" },
      { name: "Study for my next exam", emoji: "📚" },
      { name: "Review a health-data case study", emoji: "📊" },
      { name: "Draft a health-awareness message", emoji: "📢" },
      { name: "Study an epidemiology concept", emoji: "📊" },
      { name: "Review a disease-surveillance case", emoji: "🦠" },
      { name: "Draft a health-education material", emoji: "📋" },
      { name: "Read a public-health policy update", emoji: "📘" },
      { name: "Practice a community-needs-assessment exercise", emoji: "🧭" },
      { name: "Review today's fieldwork feedback", emoji: "📝" },
    ],
    "Speech-Language Pathology": [
      { name: "Practice a therapy technique", emoji: "🗣️" },
      { name: "Study for my next exam", emoji: "📚" },
      { name: "Review today's clinical notes", emoji: "🧍" },
      { name: "Practice a communication exercise", emoji: "🗨️" },
      { name: "Practice an articulation exercise", emoji: "👄" },
      { name: "Review a language-development milestone", emoji: "📈" },
      { name: "Study a communication-disorder case", emoji: "📖" },
      { name: "Practice a swallowing-assessment technique", emoji: "🍽️" },
      { name: "Design a therapy activity idea", emoji: "🧩" },
      { name: "Review today's clinical feedback", emoji: "📝" },
    ],
    "Psychology (Clinical track)": [
      { name: "Review today's case notes", emoji: "🧠" },
      { name: "Study for my next exam", emoji: "📚" },
      { name: "Practice a session-notes exercise", emoji: "📝" },
      { name: "Review a therapy-technique reading", emoji: "📔" },
      { name: "Study a diagnostic-criteria concept", emoji: "📘" },
      { name: "Practice an intake-interview script", emoji: "🗣️" },
      { name: "Review a psychological-testing technique", emoji: "📝" },
      { name: "Read a case study on a therapy approach", emoji: "📖" },
      { name: "Reflect on a supervision note", emoji: "🪞" },
      { name: "Review today's clinical feedback", emoji: "📝" },
    ],
  },

  sciences: {
    "Biology": [
      { name: "Log a lab observation", emoji: "🔬" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Read one journal article", emoji: "📖" },
      { name: "Diagram a biological process", emoji: "🧬" },
      { name: "Practice a specimen-identification exercise", emoji: "🔎" },
      { name: "Study a cell-biology concept", emoji: "🧫" },
      { name: "Sketch a labeled diagram from memory", emoji: "✏️" },
      { name: "Read about a recent biology discovery", emoji: "📰" },
      { name: "Review a taxonomy classification", emoji: "🗂️" },
      { name: "Practice using a microscope technique", emoji: "🔬" },
    ],
    "Chemistry": [
      { name: "Review a lab procedure", emoji: "🧪" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Work through a problem set", emoji: "⚗️" },
      { name: "Balance a sample chemical equation", emoji: "⚛️" },
      { name: "Practice a stoichiometry problem", emoji: "🧮" },
      { name: "Review a periodic-table trend", emoji: "🧪" },
      { name: "Study a reaction-mechanism concept", emoji: "⚗️" },
      { name: "Read about a chemistry breakthrough", emoji: "📰" },
      { name: "Practice a titration calculation", emoji: "🧫" },
      { name: "Review today's lab-report corrections", emoji: "✅" },
    ],
    "Physics": [
      { name: "Work through a problem set", emoji: "📐" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Read up on a physics concept", emoji: "🔭" },
      { name: "Read about a real-world physics phenomenon", emoji: "🌀" },
      { name: "Practice a kinematics problem", emoji: "🏃" },
      { name: "Review a force-diagram exercise", emoji: "➡️" },
      { name: "Study an energy-conservation concept", emoji: "🔋" },
      { name: "Read about a physics discovery", emoji: "📰" },
      { name: "Derive a formula from first principles", emoji: "✍️" },
      { name: "Review today's problem-set corrections", emoji: "✅" },
    ],
    "Mathematics": [
      { name: "Work through a problem set", emoji: "➗" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Practice a proof/derivation", emoji: "🧮" },
      { name: "Explore a new mathematical concept", emoji: "📐" },
      { name: "Practice a calculus problem", emoji: "∫" },
      { name: "Review a proof technique", emoji: "📜" },
      { name: "Study a linear-algebra concept", emoji: "🧮" },
      { name: "Solve a puzzle for logical practice", emoji: "🧩" },
      { name: "Read about a famous unsolved problem", emoji: "📖" },
      { name: "Review today's problem-set corrections", emoji: "✅" },
    ],
    "Statistics": [
      { name: "Work through a stats problem", emoji: "📊" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Practice a data exercise", emoji: "🧮" },
      { name: "Analyze a real-world dataset", emoji: "🎲" },
      { name: "Practice a hypothesis-testing problem", emoji: "🧪" },
      { name: "Review a probability-distribution concept", emoji: "📈" },
      { name: "Clean a sample dataset for analysis", emoji: "🧹" },
      { name: "Read about a statistics-in-the-news story", emoji: "📰" },
      { name: "Practice a regression exercise", emoji: "📉" },
      { name: "Review today's problem-set corrections", emoji: "✅" },
    ],
    "Environmental Science": [
      { name: "Log a field observation", emoji: "🌎" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Read up on a sustainability topic", emoji: "♻️" },
      { name: "Track a local environmental indicator", emoji: "🌡️" },
      { name: "Study a biogeochemical-cycle concept", emoji: "🔄" },
      { name: "Practice a water/soil-testing exercise", emoji: "🧪" },
      { name: "Read about a conservation success story", emoji: "🌳" },
      { name: "Review an environmental-policy update", emoji: "📘" },
      { name: "Map a local ecosystem observation", emoji: "🗺️" },
      { name: "Review today's field-report corrections", emoji: "✅" },
    ],
    "Marine Biology": [
      { name: "Log a lab/field observation", emoji: "🐠" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Read up on a marine-science topic", emoji: "🌊" },
      { name: "Read about a marine species/ecosystem", emoji: "🐋" },
      { name: "Study a marine-ecosystem concept", emoji: "🪸" },
      { name: "Practice a species-identification exercise", emoji: "🐟" },
      { name: "Read about ocean conservation efforts", emoji: "🌊" },
      { name: "Review a water-quality testing method", emoji: "🧪" },
      { name: "Sketch a labeled marine organism", emoji: "✏️" },
      { name: "Review today's field-report corrections", emoji: "✅" },
    ],
    "Microbiology": [
      { name: "Log a lab observation", emoji: "🧫" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Review a lab procedure", emoji: "🔬" },
      { name: "Read about a microorganism/pathogen", emoji: "🦠" },
      { name: "Practice an aseptic-technique drill", emoji: "🧤" },
      { name: "Study a microbial-growth concept", emoji: "📈" },
      { name: "Review a staining-technique procedure", emoji: "🎨" },
      { name: "Read about an infectious-disease case", emoji: "📖" },
      { name: "Practice identifying a culture result", emoji: "🧫" },
      { name: "Review today's lab-report corrections", emoji: "✅" },
    ],
    "Molecular Biology and Biotechnology": [
      { name: "Log a lab observation", emoji: "🧬" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Review a lab procedure", emoji: "🔬" },
      { name: "Read about a biotech breakthrough", emoji: "🧫" },
      { name: "Study a gene-expression concept", emoji: "🧬" },
      { name: "Practice a PCR/lab-technique explanation", emoji: "🔬" },
      { name: "Read about a biotech innovation", emoji: "📰" },
      { name: "Review a protein-synthesis pathway", emoji: "🧫" },
      { name: "Sketch a molecular-process diagram", emoji: "✏️" },
      { name: "Review today's lab-report corrections", emoji: "✅" },
    ],
    "Geology": [
      { name: "Log a field observation", emoji: "🪨" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Review a mapping exercise", emoji: "🗺️" },
      { name: "Read about a geological formation/event", emoji: "🌋" },
      { name: "Practice a rock/mineral-identification exercise", emoji: "🪨" },
      { name: "Study a plate-tectonics concept", emoji: "🌍" },
      { name: "Read about a recent geological event", emoji: "🌋" },
      { name: "Review a geologic-time-scale reference", emoji: "🕰️" },
      { name: "Sketch a cross-section diagram", emoji: "✏️" },
      { name: "Review today's field-report corrections", emoji: "✅" },
    ],
    "Meteorology": [
      { name: "Check today's weather data", emoji: "🌦️" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Review a forecasting exercise", emoji: "📊" },
      { name: "Track a storm/weather system", emoji: "🌪️" },
      { name: "Practice reading a weather map", emoji: "🗺️" },
      { name: "Study an atmospheric-pressure concept", emoji: "🌡️" },
      { name: "Read about a recent weather event", emoji: "🌪️" },
      { name: "Review a satellite-imagery source", emoji: "🛰️" },
      { name: "Practice a forecast-accuracy check", emoji: "✅" },
      { name: "Review today's problem-set corrections", emoji: "✅" },
    ],
    "Food Science and Technology": [
      { name: "Log a lab/food-trial observation", emoji: "🍽️" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Review a lab procedure", emoji: "🧪" },
      { name: "Study a food-preservation technique", emoji: "🍞" },
      { name: "Study a food-processing method", emoji: "🏭" },
      { name: "Practice a sensory-evaluation exercise", emoji: "👅" },
      { name: "Review a food-safety regulation", emoji: "📘" },
      { name: "Read about a food-innovation story", emoji: "📰" },
      { name: "Review a shelf-life testing concept", emoji: "📅" },
      { name: "Review today's lab-report corrections", emoji: "✅" },
    ],
    "Forensic Science": [
      { name: "Review a case study", emoji: "🔍" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Review a lab procedure", emoji: "🧪" },
      { name: "Study an evidence-analysis technique", emoji: "🕵️" },
      { name: "Practice an evidence-documentation exercise", emoji: "📸" },
      { name: "Study a forensic-analysis technique", emoji: "🔬" },
      { name: "Read about a solved cold case", emoji: "📰" },
      { name: "Review a chain-of-custody procedure", emoji: "📋" },
      { name: "Practice a crime-scene sketching exercise", emoji: "✏️" },
      { name: "Review today's lab-report corrections", emoji: "✅" },
    ],
  },

  humanities: {
    "English Literature / Literature": [
      { name: "Read a chapter of my assigned text", emoji: "📖" },
      { name: "Free-write for 10 minutes", emoji: "✍️" },
      { name: "Review today's discussion notes", emoji: "📚" },
      { name: "Explore a new author or work", emoji: "📚" },
      { name: "Annotate a passage closely", emoji: "🖊️" },
      { name: "Write a short literary-analysis paragraph", emoji: "📝" },
      { name: "Compare two characters' arcs", emoji: "🔀" },
      { name: "Read about the era of my current text", emoji: "📖" },
      { name: "Discuss a reading with a classmate", emoji: "🗣️" },
      { name: "Memorize a favorite quote or passage", emoji: "💭" },
    ],
    "Philosophy": [
      { name: "Reflect on today's reading", emoji: "🤔" },
      { name: "Read one philosophy text", emoji: "📖" },
      { name: "Free-write my thoughts", emoji: "✍️" },
      { name: "Debate an ethical question with a peer", emoji: "💭" },
      { name: "Outline an argument's premises/conclusion", emoji: "🧩" },
      { name: "Write a short reflection on a thesis", emoji: "📝" },
      { name: "Read a summary of a philosopher's work", emoji: "📖" },
      { name: "Practice spotting a logical fallacy", emoji: "🔍" },
      { name: "Discuss a philosophical question with a peer", emoji: "🗣️" },
      { name: "Journal about a value/belief I hold", emoji: "📔" },
    ],
    "History": [
      { name: "Read a chapter of my assigned text", emoji: "📖" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Note one historical event I learned", emoji: "🗓️" },
      { name: "Research a historical figure", emoji: "🗿" },
      { name: "Build a mini timeline of an era", emoji: "🗓️" },
      { name: "Compare two historians' interpretations", emoji: "🔀" },
      { name: "Read a primary-source excerpt", emoji: "📜" },
      { name: "Map out a historical event's key figures", emoji: "🗺️" },
      { name: "Discuss a historical event with a peer", emoji: "🗣️" },
      { name: "Write a short summary of today's reading", emoji: "📝" },
    ],
    "Creative Writing": [
      { name: "Free-write for 10 minutes", emoji: "✍️" },
      { name: "Read a chapter of my assigned text", emoji: "📖" },
      { name: "Revise a piece I'm working on", emoji: "📝" },
      { name: "Journal a story idea", emoji: "📓" },
      { name: "Try a writing prompt for practice", emoji: "💡" },
      { name: "Read a peer's piece and give feedback", emoji: "🗣️" },
      { name: "Study a technique in a favorite author's work", emoji: "📚" },
      { name: "Outline a plot or poem structure", emoji: "🗺️" },
      { name: "Submit a piece somewhere (contest/blog/zine)", emoji: "📤" },
      { name: "Keep a writer's notebook entry", emoji: "📓" },
    ],
    "Fine Arts": [
      { name: "Sketch or paint for 20 minutes", emoji: "🎨" },
      { name: "Review today's critique notes", emoji: "📚" },
      { name: "Research an art reference", emoji: "🖼️" },
      { name: "Visit a gallery or view art online", emoji: "🖼️" },
      { name: "Do a quick gesture-drawing warm-up", emoji: "✏️" },
      { name: "Study a master painting's composition", emoji: "🖼️" },
      { name: "Experiment with a new medium", emoji: "🎨" },
      { name: "Photograph my work-in-progress for records", emoji: "📸" },
      { name: "Get feedback from a peer or mentor", emoji: "🗣️" },
      { name: "Clean and organize my art supplies", emoji: "🧹" },
    ],
    "Theater Arts": [
      { name: "Rehearse my lines/scene", emoji: "🎭" },
      { name: "Review today's rehearsal notes", emoji: "📚" },
      { name: "Warm up before practice", emoji: "🔥" },
      { name: "Watch a play or performance", emoji: "🎫" },
      { name: "Do a vocal/physical warm-up", emoji: "🔥" },
      { name: "Analyze my character's motivation", emoji: "🎭" },
      { name: "Practice a cold-reading exercise", emoji: "📄" },
      { name: "Watch a performance for technique ideas", emoji: "🎬" },
      { name: "Journal about today's rehearsal", emoji: "📓" },
      { name: "Practice a stage-blocking exercise", emoji: "🚶" },
    ],
    "Linguistics": [
      { name: "Review today's lecture notes", emoji: "🗣️" },
      { name: "Read a chapter of my assigned text", emoji: "📖" },
      { name: "Practice a language-analysis exercise", emoji: "📝" },
      { name: "Compare two languages' grammar rules", emoji: "🌐" },
      { name: "Transcribe a short audio using IPA", emoji: "🔤" },
      { name: "Study a syntax-tree exercise", emoji: "🌳" },
      { name: "Read about an endangered language", emoji: "📖" },
      { name: "Practice a phonetics drill", emoji: "🗣️" },
      { name: "Compare a dialect's unique features", emoji: "🌐" },
      { name: "Review today's discussion feedback", emoji: "📝" },
    ],
    "Religious Studies": [
      { name: "Read a chapter of my assigned text", emoji: "📖" },
      { name: "Reflect on today's reading", emoji: "🤔" },
      { name: "Review today's discussion notes", emoji: "📚" },
      { name: "Explore a different faith tradition", emoji: "🕊️" },
      { name: "Read a primary religious text excerpt", emoji: "📖" },
      { name: "Study a comparative-religion concept", emoji: "🕊️" },
      { name: "Journal about a spiritual/ethical question", emoji: "📔" },
      { name: "Attend or research a religious practice", emoji: "🙏" },
      { name: "Discuss a reading with a classmate", emoji: "🗣️" },
      { name: "Write a short summary of today's reading", emoji: "📝" },
    ],
    "Filipino / Panitikan": [
      { name: "Read a chapter of my assigned text", emoji: "📖" },
      { name: "Free-write for 10 minutes", emoji: "✍️" },
      { name: "Review today's discussion notes", emoji: "📚" },
      { name: "Explore a Filipino author or work", emoji: "📚" },
      { name: "Bumasa ng isang tula o akda", emoji: "📖" },
      { name: "Sumulat ng maikling pagsusuri", emoji: "📝" },
      { name: "Alamin ang kontekstong pangkasaysayan", emoji: "🗓️" },
      { name: "Talakayin ang isang akda kasama ang kaklase", emoji: "🗣️" },
      { name: "Suriin ang estilo ng isang manunulat", emoji: "🔍" },
      { name: "Mag-journal tungkol sa binasa", emoji: "📔" },
    ],
    "Cultural Studies": [
      { name: "Read one relevant article", emoji: "📖" },
      { name: "Review today's discussion notes", emoji: "📚" },
      { name: "Reflect on today's reading", emoji: "🤔" },
      { name: "Explore a different culture's tradition", emoji: "🌍" },
      { name: "Study a cultural-theory concept", emoji: "🧩" },
      { name: "Read about a subculture/community", emoji: "📖" },
      { name: "Analyze a media artifact critically", emoji: "🔍" },
      { name: "Interview someone about their traditions", emoji: "🗣️" },
      { name: "Discuss a reading with a classmate", emoji: "🗣️" },
      { name: "Write a short summary of today's reading", emoji: "📝" },
    ],
  },

  "social-sciences": {
    "Psychology": [
      { name: "Read one relevant article", emoji: "🧠" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Reflect on today's case discussion", emoji: "📝" },
      { name: "Read about a psychology experiment", emoji: "🧪" },
      { name: "Study a theory/perspective in psychology", emoji: "🧠" },
      { name: "Practice explaining a concept simply", emoji: "🗣️" },
      { name: "Reflect on a behavior I noticed today", emoji: "🪞" },
      { name: "Read a summary of a classic study", emoji: "📖" },
      { name: "Review today's discussion feedback", emoji: "📝" },
      { name: "Watch a psychology explainer video", emoji: "🎥" },
    ],
    "Sociology": [
      { name: "Read one relevant article", emoji: "🧑‍🤝‍🧑" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Reflect on today's case discussion", emoji: "📝" },
      { name: "Observe a social pattern in my community", emoji: "🏙️" },
      { name: "Study a sociological theory", emoji: "📘" },
      { name: "Analyze a social-media trend sociologically", emoji: "📱" },
      { name: "Read about a social-issue case study", emoji: "📖" },
      { name: "Interview someone about a social experience", emoji: "🗣️" },
      { name: "Review today's discussion feedback", emoji: "📝" },
      { name: "Watch a sociology documentary clip", emoji: "🎥" },
    ],
    "Political Science": [
      { name: "Read up on a current political issue", emoji: "🏛️" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Reflect on today's discussion", emoji: "📝" },
      { name: "Follow a policy debate", emoji: "🗳️" },
      { name: "Study a governance/political-theory concept", emoji: "🏛️" },
      { name: "Compare two countries' political systems", emoji: "🔀" },
      { name: "Read a policy paper or op-ed", emoji: "📰" },
      { name: "Discuss a current event with a peer", emoji: "🗣️" },
      { name: "Review today's discussion feedback", emoji: "📝" },
      { name: "Watch a political-analysis video", emoji: "🎥" },
    ],
    "Anthropology": [
      { name: "Read one relevant article", emoji: "🌍" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Reflect on today's case discussion", emoji: "📝" },
      { name: "Research a cultural practice", emoji: "🏺" },
      { name: "Study a kinship/social-structure concept", emoji: "👪" },
      { name: "Read an ethnography excerpt", emoji: "📖" },
      { name: "Observe and log a cultural practice", emoji: "📝" },
      { name: "Compare two cultures' customs", emoji: "🔀" },
      { name: "Review today's discussion feedback", emoji: "📝" },
      { name: "Watch an anthropology documentary clip", emoji: "🎥" },
    ],
    "Social Work": [
      { name: "Review today's case notes", emoji: "🤝" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Reflect on today's fieldwork", emoji: "📝" },
      { name: "Research a community resource", emoji: "🧭" },
      { name: "Study a case-management concept", emoji: "📋" },
      { name: "Practice an active-listening exercise", emoji: "👂" },
      { name: "Read a social-welfare policy update", emoji: "📘" },
      { name: "Research a client-referral resource", emoji: "🧭" },
      { name: "Review today's fieldwork feedback", emoji: "📝" },
      { name: "Reflect on today's practicum experience", emoji: "🪞" },
    ],
    "International Studies": [
      { name: "Read one global affairs article", emoji: "🌏" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Reflect on today's discussion", emoji: "📝" },
      { name: "Follow an international relations story", emoji: "🕊️" },
      { name: "Study an international-relations theory", emoji: "🌍" },
      { name: "Follow a global news story closely", emoji: "🌐" },
      { name: "Compare two countries' foreign policies", emoji: "🔀" },
      { name: "Read a diplomatic case study", emoji: "📖" },
      { name: "Review today's discussion feedback", emoji: "📝" },
      { name: "Learn a fact about a country's culture", emoji: "🗺️" },
    ],
    "Development Studies": [
      { name: "Read one relevant article", emoji: "🌱" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Reflect on today's case discussion", emoji: "📝" },
      { name: "Research a development project", emoji: "🌾" },
      { name: "Study a development-theory concept", emoji: "🌱" },
      { name: "Read about a community-development project", emoji: "📖" },
      { name: "Review a poverty/inequality data source", emoji: "📊" },
      { name: "Research an NGO's program model", emoji: "🧭" },
      { name: "Review today's discussion feedback", emoji: "📝" },
      { name: "Reflect on today's fieldwork experience", emoji: "🪞" },
    ],
    "Geography": [
      { name: "Review a map/spatial exercise", emoji: "🗺️" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Read one relevant article", emoji: "📖" },
      { name: "Explore a country's geography online", emoji: "🌐" },
      { name: "Practice a map-reading exercise", emoji: "🧭" },
      { name: "Study a human-geography concept", emoji: "🏙️" },
      { name: "Read about a geopolitical case study", emoji: "📖" },
      { name: "Track a climate/weather pattern", emoji: "🌦️" },
      { name: "Review today's discussion feedback", emoji: "📝" },
      { name: "Explore a region using satellite imagery", emoji: "🛰️" },
    ],
    "Behavioral Science": [
      { name: "Read one relevant article", emoji: "🧠" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Reflect on today's case discussion", emoji: "📝" },
      { name: "Observe a behavioral pattern", emoji: "🔍" },
      { name: "Study a behavioral-economics concept", emoji: "🧠" },
      { name: "Design a mini behavior-change experiment", emoji: "🧪" },
      { name: "Read about a nudge/intervention case", emoji: "📖" },
      { name: "Track one of my own habits/behaviors today", emoji: "📝" },
      { name: "Review today's discussion feedback", emoji: "📝" },
      { name: "Watch a behavioral-science talk", emoji: "🎥" },
    ],
  },

  education: {
    "Elementary Education": [
      { name: "Review my lesson plan for practicum", emoji: "🍎" },
      { name: "Reflect on today's classroom observation", emoji: "📚" },
      { name: "Prep materials for my next demo", emoji: "✏️" },
      { name: "Design a fun classroom activity", emoji: "🎨" },
      { name: "Practice a storytelling technique", emoji: "📚" },
      { name: "Study a child-development milestone", emoji: "🧒" },
      { name: "Design a simple visual aid", emoji: "🖼️" },
      { name: "Read about a classroom-management strategy", emoji: "📖" },
      { name: "Practice giving clear, simple instructions", emoji: "🗣️" },
      { name: "Review today's practicum feedback", emoji: "📝" },
    ],
    "Secondary Education": [
      { name: "Review my lesson plan for practicum", emoji: "🍎" },
      { name: "Reflect on today's classroom observation", emoji: "📚" },
      { name: "Prep materials for my next demo", emoji: "✏️" },
      { name: "Design an assessment/quiz", emoji: "📊" },
      { name: "Practice explaining a concept two ways", emoji: "🔁" },
      { name: "Study a subject-specific teaching strategy", emoji: "📖" },
      { name: "Draft a discussion question for class", emoji: "❓" },
      { name: "Read about a classroom-management strategy", emoji: "📖" },
      { name: "Design a rubric for an assignment", emoji: "📋" },
      { name: "Review today's practicum feedback", emoji: "📝" },
    ],
    "Early Childhood Education": [
      { name: "Review my lesson plan for practicum", emoji: "🧸" },
      { name: "Reflect on today's classroom observation", emoji: "📚" },
      { name: "Prep materials for my next demo", emoji: "✏️" },
      { name: "Design a play-based activity", emoji: "🧸" },
      { name: "Practice a play-based learning idea", emoji: "🧸" },
      { name: "Study an early-childhood milestone", emoji: "🍼" },
      { name: "Design a sensory-play activity", emoji: "🖐️" },
      { name: "Read about a positive-guidance strategy", emoji: "📖" },
      { name: "Practice a simple song or fingerplay", emoji: "🎵" },
      { name: "Review today's practicum feedback", emoji: "📝" },
    ],
    "Special Education": [
      { name: "Review my lesson plan for practicum", emoji: "💛" },
      { name: "Reflect on today's classroom observation", emoji: "📚" },
      { name: "Prep materials for my next demo", emoji: "✏️" },
      { name: "Research an inclusive-teaching strategy", emoji: "🧩" },
      { name: "Study an IEP/accommodation concept", emoji: "📋" },
      { name: "Read about an assistive-technology tool", emoji: "🦽" },
      { name: "Practice a differentiated-instruction idea", emoji: "🧩" },
      { name: "Research a sensory-friendly strategy", emoji: "🎧" },
      { name: "Journal about a student's small win today", emoji: "📔" },
      { name: "Review today's practicum feedback", emoji: "📝" },
    ],
    "Physical Education": [
      { name: "Review my lesson plan for practicum", emoji: "🏃" },
      { name: "Reflect on today's classroom observation", emoji: "📚" },
      { name: "Prep an activity for my next demo", emoji: "🤸" },
      { name: "Design a physical activity/game", emoji: "⚽" },
      { name: "Plan a warm-up drill sequence", emoji: "🏃" },
      { name: "Study a motor-skill development concept", emoji: "🤸" },
      { name: "Design a game with a learning objective", emoji: "⚽" },
      { name: "Read about an inclusive-PE strategy", emoji: "📖" },
      { name: "Practice a demonstration for a new skill", emoji: "🏋️" },
      { name: "Review today's practicum feedback", emoji: "📝" },
    ],
    "Technology and Livelihood Education": [
      { name: "Review my lesson plan for practicum", emoji: "🛠️" },
      { name: "Reflect on today's classroom observation", emoji: "📚" },
      { name: "Prep materials for my next demo", emoji: "✏️" },
      { name: "Practice a hands-on skill demo", emoji: "🔨" },
      { name: "Practice a hands-on trade skill", emoji: "🔨" },
      { name: "Study a workplace-safety concept", emoji: "🦺" },
      { name: "Design a step-by-step skill demo", emoji: "📋" },
      { name: "Read about a livelihood-program idea", emoji: "📖" },
      { name: "Maintain/organize a set of tools", emoji: "🧰" },
      { name: "Review today's practicum feedback", emoji: "📝" },
    ],
    "Values Education": [
      { name: "Review my lesson plan for practicum", emoji: "💛" },
      { name: "Reflect on today's classroom observation", emoji: "📚" },
      { name: "Prep materials for my next demo", emoji: "✏️" },
      { name: "Prepare a values-discussion prompt", emoji: "💬" },
      { name: "Prepare a values-reflection story", emoji: "💬" },
      { name: "Study a moral-development concept", emoji: "🧭" },
      { name: "Design a group-discussion activity", emoji: "🧑‍🤝‍🧑" },
      { name: "Read about a character-education strategy", emoji: "📖" },
      { name: "Reflect on a value I want to model", emoji: "🪞" },
      { name: "Review today's practicum feedback", emoji: "📝" },
    ],
    "Educational Management": [
      { name: "Read up on a school-leadership topic", emoji: "🏫" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Reflect on today's case discussion", emoji: "📝" },
      { name: "Review a school-performance metric", emoji: "📈" },
      { name: "Study a school-governance concept", emoji: "🏫" },
      { name: "Read about an education-policy update", emoji: "📘" },
      { name: "Practice a budget/resource-planning exercise", emoji: "💰" },
      { name: "Review a school-improvement case study", emoji: "📈" },
      { name: "Draft a stakeholder-communication note", emoji: "📝" },
      { name: "Review today's case discussion feedback", emoji: "✅" },
    ],
  },

  law: {
    "Political Science": [
      { name: "Read up on a current political issue", emoji: "🏛️" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Reflect on today's discussion", emoji: "📝" },
      { name: "Follow a policy debate", emoji: "🗳️" },
      { name: "Study a governance/political-theory concept", emoji: "🏛️" },
      { name: "Compare two countries' political systems", emoji: "🔀" },
      { name: "Read a policy paper or op-ed", emoji: "📰" },
      { name: "Discuss a current event with a peer", emoji: "🗣️" },
      { name: "Review today's discussion feedback", emoji: "📝" },
      { name: "Watch a political-analysis video", emoji: "🎥" },
    ],
    "Legal Management": [
      { name: "Read one legal case or article", emoji: "⚖️" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Practice legal writing", emoji: "📝" },
      { name: "Draft a sample contract clause", emoji: "📜" },
      { name: "Study a business-law provision", emoji: "📘" },
      { name: "Practice a legal-memo outline", emoji: "📝" },
      { name: "Review a compliance-checklist item", emoji: "✅" },
      { name: "Read a recent legal-management case", emoji: "📰" },
      { name: "Practice a contract-review exercise", emoji: "🔍" },
      { name: "Review today's problem-set corrections", emoji: "✅" },
    ],
    "Law (Juris Doctor)": [
      { name: "Read one case or statute", emoji: "⚖️" },
      { name: "Review my case briefs", emoji: "📚" },
      { name: "Practice recitation", emoji: "🎤" },
      { name: "Review a landmark case ruling", emoji: "⚖️" },
      { name: "Outline a case digest", emoji: "🗂️" },
      { name: "Practice a legal-research exercise", emoji: "🔎" },
      { name: "Review a rule of law with an example", emoji: "📘" },
      { name: "Time myself on a mock bar question", emoji: "⏱️" },
      { name: "Discuss a legal issue with a study group", emoji: "🗣️" },
      { name: "Update my digest/reviewer notes", emoji: "📝" },
    ],
    "Criminology": [
      { name: "Review today's case study notes", emoji: "🔍" },
      { name: "Study for my board exam prep", emoji: "📚" },
      { name: "Practice a report-writing exercise", emoji: "📝" },
      { name: "Study a criminal-justice case", emoji: "🔍" },
      { name: "Study a crime-theory concept", emoji: "🧠" },
      { name: "Practice a crime-scene procedure", emoji: "🔍" },
      { name: "Review a penal-code provision", emoji: "📘" },
      { name: "Read about a solved case study", emoji: "📰" },
      { name: "Practice a physical-fitness drill for exams", emoji: "🏃" },
      { name: "Time myself on a mock board question", emoji: "⏱️" },
    ],
    "Public Administration": [
      { name: "Read up on a public policy issue", emoji: "🏛️" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Practice a policy brief outline", emoji: "📝" },
      { name: "Read up on a local governance issue", emoji: "🗳️" },
      { name: "Study a government-budgeting concept", emoji: "💰" },
      { name: "Read a public-service delivery case", emoji: "🏛️" },
      { name: "Practice a policy-analysis exercise", emoji: "📊" },
      { name: "Review an administrative-law provision", emoji: "📘" },
      { name: "Follow a local government initiative", emoji: "🗞️" },
      { name: "Review today's problem-set corrections", emoji: "✅" },
    ],
    "International Relations": [
      { name: "Read one global affairs article", emoji: "🌏" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Reflect on today's discussion", emoji: "📝" },
      { name: "Follow an international relations story", emoji: "🕊️" },
      { name: "Study an international-relations theory", emoji: "🌍" },
      { name: "Follow a global news story closely", emoji: "🌐" },
      { name: "Compare two countries' foreign policies", emoji: "🔀" },
      { name: "Read a diplomatic case study", emoji: "📖" },
      { name: "Review today's discussion feedback", emoji: "📝" },
      { name: "Learn a fact about a country's culture", emoji: "🗺️" },
    ],
  },

  architecture: {
    "Architecture": [
      { name: "Sketch or render for 20 minutes", emoji: "✏️" },
      { name: "Review today's studio critique notes", emoji: "📚" },
      { name: "Research a design reference", emoji: "🏛️" },
      { name: "Study a building/structure reference", emoji: "🏛️" },
      { name: "Study a building-code provision", emoji: "📘" },
      { name: "Practice a scale/proportion exercise", emoji: "📏" },
      { name: "Analyze a floor-plan layout", emoji: "🗺️" },
      { name: "Build/refine a physical or digital model", emoji: "🏗️" },
      { name: "Photograph a building for reference", emoji: "📸" },
      { name: "Review today's studio critique feedback", emoji: "📝" },
    ],
    "Interior Design": [
      { name: "Sketch or render for 20 minutes", emoji: "🛋️" },
      { name: "Review today's studio critique notes", emoji: "📚" },
      { name: "Research a design reference", emoji: "🎨" },
      { name: "Explore a furniture/material catalog", emoji: "🪑" },
      { name: "Study a space-planning concept", emoji: "🗺️" },
      { name: "Practice a color-palette selection", emoji: "🎨" },
      { name: "Sketch a room layout from a brief", emoji: "✏️" },
      { name: "Research a lighting-design technique", emoji: "💡" },
      { name: "Build a small material moodboard", emoji: "🧵" },
      { name: "Review today's studio critique feedback", emoji: "📝" },
    ],
    "Landscape Architecture": [
      { name: "Sketch or render for 20 minutes", emoji: "🌳" },
      { name: "Review today's studio critique notes", emoji: "📚" },
      { name: "Research a design reference", emoji: "🏞️" },
      { name: "Study a park/landscape reference", emoji: "🌿" },
      { name: "Study a native-plant selection", emoji: "🌿" },
      { name: "Practice a site-analysis exercise", emoji: "🗺️" },
      { name: "Sketch a planting-plan concept", emoji: "🌳" },
      { name: "Research a sustainable-landscape technique", emoji: "♻️" },
      { name: "Photograph a green space for reference", emoji: "📸" },
      { name: "Review today's studio critique feedback", emoji: "📝" },
    ],
    "Urban and Regional Planning": [
      { name: "Review a planning case study", emoji: "🏙️" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Sketch a planning concept", emoji: "🗺️" },
      { name: "Study a city-planning case", emoji: "🚦" },
      { name: "Study a zoning-ordinance concept", emoji: "📘" },
      { name: "Practice a land-use mapping exercise", emoji: "🗺️" },
      { name: "Read about a city-planning success story", emoji: "🏙️" },
      { name: "Review a transportation-planning concept", emoji: "🚦" },
      { name: "Analyze a neighborhood's walkability", emoji: "🚶" },
      { name: "Review today's studio critique feedback", emoji: "📝" },
    ],
    "Industrial Design": [
      { name: "Sketch or render for 20 minutes", emoji: "🛠️" },
      { name: "Review today's studio critique notes", emoji: "📚" },
      { name: "Research a design reference", emoji: "💡" },
      { name: "Study a product-design reference", emoji: "⚙️" },
      { name: "Sketch multiple concept iterations", emoji: "✏️" },
      { name: "Study a materials/manufacturing concept", emoji: "⚙️" },
      { name: "Build a quick prototype/mockup", emoji: "🛠️" },
      { name: "Research a product's user experience", emoji: "🔍" },
      { name: "Photograph my prototype for records", emoji: "📸" },
      { name: "Review today's studio critique feedback", emoji: "📝" },
    ],
    "Fashion Design": [
      { name: "Sketch a design for 20 minutes", emoji: "👗" },
      { name: "Review today's studio critique notes", emoji: "📚" },
      { name: "Research a fashion reference", emoji: "🧵" },
      { name: "Explore a fabric/material swatch", emoji: "🧵" },
      { name: "Sketch a garment flat/technical drawing", emoji: "✏️" },
      { name: "Study a pattern-making technique", emoji: "📐" },
      { name: "Research a trend for my next piece", emoji: "🔍" },
      { name: "Practice a sewing/construction technique", emoji: "🧵" },
      { name: "Build a small mood/inspiration board", emoji: "🖼️" },
      { name: "Review today's studio critique feedback", emoji: "📝" },
    ],
    "Graphic Design": [
      { name: "Work on a design project", emoji: "🎨" },
      { name: "Review today's critique notes", emoji: "📚" },
      { name: "Research a design reference", emoji: "🖌️" },
      { name: "Study a typography/layout reference", emoji: "🔤" },
      { name: "Practice a typography pairing exercise", emoji: "🔤" },
      { name: "Study a grid/layout system", emoji: "🗂️" },
      { name: "Recreate a design for practice", emoji: "🔁" },
      { name: "Explore a new color-palette tool", emoji: "🎨" },
      { name: "Get feedback on a work-in-progress", emoji: "📝" },
      { name: "Review today's studio critique feedback", emoji: "📝" },
    ],
  },

  agriculture: {
    "Agriculture": [
      { name: "Log today's field observations", emoji: "🌱" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Check in on my project/plot", emoji: "🚜" },
      { name: "Read about a crop-management technique", emoji: "🌾" },
      { name: "Study a crop-rotation concept", emoji: "🌾" },
      { name: "Practice a soil-testing exercise", emoji: "🧪" },
      { name: "Read about a pest-management technique", emoji: "🐛" },
      { name: "Review a planting-calendar reference", emoji: "📅" },
      { name: "Log my plot's growth progress", emoji: "📈" },
      { name: "Review today's fieldwork corrections", emoji: "✅" },
    ],
    "Agribusiness": [
      { name: "Log today's field observations", emoji: "🌾" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Read up on a market trend", emoji: "📊" },
      { name: "Track a commodity price", emoji: "💰" },
      { name: "Study a farm-financing concept", emoji: "💰" },
      { name: "Review a supply-and-demand pattern for crops", emoji: "📈" },
      { name: "Read an agribusiness case study", emoji: "📖" },
      { name: "Practice a cost-benefit calculation", emoji: "🧮" },
      { name: "Research a value-chain improvement idea", emoji: "🔗" },
      { name: "Review today's fieldwork corrections", emoji: "✅" },
    ],
    "Forestry": [
      { name: "Log today's field observations", emoji: "🌲" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Read up on a forestry topic", emoji: "🌳" },
      { name: "Read about a forest-conservation technique", emoji: "🪵" },
      { name: "Study a silviculture concept", emoji: "🌲" },
      { name: "Practice a tree-measurement exercise", emoji: "📏" },
      { name: "Read about a reforestation success story", emoji: "🌱" },
      { name: "Review a forest-inventory technique", emoji: "📋" },
      { name: "Map a forest plot observation", emoji: "🗺️" },
      { name: "Review today's fieldwork corrections", emoji: "✅" },
    ],
    "Fisheries": [
      { name: "Log today's field observations", emoji: "🐟" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Read up on an aquaculture topic", emoji: "🌊" },
      { name: "Read about a sustainable-fishing technique", emoji: "🐠" },
      { name: "Study a fish-breeding/culture concept", emoji: "🐟" },
      { name: "Practice a water-quality testing exercise", emoji: "🧪" },
      { name: "Read about a sustainable-aquaculture case", emoji: "🌊" },
      { name: "Review a fish-stocking density guideline", emoji: "📊" },
      { name: "Log a pond/site observation", emoji: "📝" },
      { name: "Review today's fieldwork corrections", emoji: "✅" },
    ],
    "Agricultural Engineering": [
      { name: "Work through a problem set", emoji: "🚜" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Review a farm-systems concept", emoji: "⚙️" },
      { name: "Study a farm-machinery concept", emoji: "🚜" },
      { name: "Study a machinery-design concept", emoji: "🚜" },
      { name: "Practice an irrigation-calculation exercise", emoji: "💧" },
      { name: "Review a post-harvest processing concept", emoji: "🌾" },
      { name: "Read about a farm-automation case", emoji: "🤖" },
      { name: "Review a soil/water-conservation concept", emoji: "🌱" },
      { name: "Review today's problem-set corrections", emoji: "✅" },
    ],
    "Environmental Science": [
      { name: "Log a field observation", emoji: "🌎" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Read up on a sustainability topic", emoji: "♻️" },
      { name: "Track a local environmental indicator", emoji: "🌡️" },
      { name: "Study a biogeochemical-cycle concept", emoji: "🔄" },
      { name: "Practice a water/soil-testing exercise", emoji: "🧪" },
      { name: "Read about a conservation success story", emoji: "🌳" },
      { name: "Review an environmental-policy update", emoji: "📘" },
      { name: "Map a local ecosystem observation", emoji: "🗺️" },
      { name: "Review today's field-report corrections", emoji: "✅" },
    ],
    "Veterinary Medicine": [
      { name: "Review today's clinical notes", emoji: "🐾" },
      { name: "Study for my next exam", emoji: "📚" },
      { name: "Review a treatment procedure", emoji: "💉" },
      { name: "Read up on an animal-health case", emoji: "🐕" },
      { name: "Practice a physical-exam technique", emoji: "🩺" },
      { name: "Review a species-specific care concept", emoji: "🐾" },
      { name: "Study a common animal-disease case", emoji: "📖" },
      { name: "Practice a restraint/handling technique", emoji: "🤲" },
      { name: "Review a dosage-calculation exercise", emoji: "🧮" },
      { name: "Review today's clinical feedback", emoji: "📝" },
    ],
    "Horticulture": [
      { name: "Log today's field observations", emoji: "🌷" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Check in on my project/plot", emoji: "🪴" },
      { name: "Research a plant-propagation technique", emoji: "🌸" },
      { name: "Study a propagation technique", emoji: "🌱" },
      { name: "Practice a pruning/grafting exercise", emoji: "✂️" },
      { name: "Read about an ornamental-plant trend", emoji: "🌷" },
      { name: "Review a soil-amendment concept", emoji: "🪴" },
      { name: "Photograph my plot's progress", emoji: "📸" },
      { name: "Review today's fieldwork corrections", emoji: "✅" },
    ],
  },

  music: {
    "Music": [
      { name: "Practice my instrument/voice for 20 min", emoji: "🎵" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Learn a new piece", emoji: "🎼" },
      { name: "Analyze a piece by ear", emoji: "🎧" },
      { name: "Practice a scale/technical exercise", emoji: "🎼" },
      { name: "Sight-read a new piece", emoji: "🎶" },
      { name: "Record myself and listen back critically", emoji: "🎙️" },
      { name: "Study a music-theory concept", emoji: "📘" },
      { name: "Listen actively to a new genre/artist", emoji: "🎧" },
      { name: "Review today's lesson feedback", emoji: "📝" },
    ],
    "Music Education": [
      { name: "Practice my instrument/voice for 20 min", emoji: "🎵" },
      { name: "Review my lesson plan for practicum", emoji: "📚" },
      { name: "Prep materials for my next demo", emoji: "🎼" },
      { name: "Design a music lesson activity", emoji: "🎼" },
      { name: "Practice a scale/technical exercise", emoji: "🎼" },
      { name: "Study a music-pedagogy method", emoji: "📘" },
      { name: "Design a fun rhythm activity", emoji: "🥁" },
      { name: "Record myself and listen back critically", emoji: "🎙️" },
      { name: "Read about a classroom-music strategy", emoji: "📖" },
      { name: "Review today's practicum feedback", emoji: "📝" },
    ],
    "Theater Arts": [
      { name: "Rehearse my lines/scene", emoji: "🎭" },
      { name: "Review today's rehearsal notes", emoji: "📚" },
      { name: "Warm up before practice", emoji: "🔥" },
      { name: "Watch a play or performance", emoji: "🎫" },
      { name: "Do a vocal/physical warm-up", emoji: "🔥" },
      { name: "Analyze my character's motivation", emoji: "🎭" },
      { name: "Practice a cold-reading exercise", emoji: "📄" },
      { name: "Watch a performance for technique ideas", emoji: "🎬" },
      { name: "Journal about today's rehearsal", emoji: "📓" },
      { name: "Practice a stage-blocking exercise", emoji: "🚶" },
    ],
    "Dance": [
      { name: "Rehearse my routine", emoji: "💃" },
      { name: "Review today's rehearsal notes", emoji: "📚" },
      { name: "Warm up before practice", emoji: "🔥" },
      { name: "Study a choreography reference", emoji: "🩰" },
      { name: "Practice a technique drill (barre/isolations)", emoji: "🩰" },
      { name: "Record my routine and review the footage", emoji: "🎥" },
      { name: "Stretch and do a cooldown", emoji: "🧘" },
      { name: "Study a choreography reference video", emoji: "📺" },
      { name: "Practice a routine's tricky section slowly", emoji: "🐢" },
      { name: "Review today's rehearsal feedback", emoji: "📝" },
    ],
    "Musical Theater": [
      { name: "Rehearse my piece or lines", emoji: "🎤" },
      { name: "Review today's rehearsal notes", emoji: "📚" },
      { name: "Warm up before practice", emoji: "🔥" },
      { name: "Watch a musical for reference", emoji: "🎬" },
      { name: "Do a vocal warm-up", emoji: "🎤" },
      { name: "Rehearse a dance combo", emoji: "🩰" },
      { name: "Practice a monologue or scene", emoji: "🎭" },
      { name: "Record myself and review the footage", emoji: "🎥" },
      { name: "Study a cast recording for phrasing ideas", emoji: "🎧" },
      { name: "Review today's rehearsal feedback", emoji: "📝" },
    ],
    "Film / Cinema": [
      { name: "Watch/analyze one film critically", emoji: "🎬" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Work on a project for 20 min", emoji: "🎥" },
      { name: "Study a film's cinematography", emoji: "🎞️" },
      { name: "Storyboard a short scene", emoji: "🎬" },
      { name: "Practice a shot-composition exercise", emoji: "📷" },
      { name: "Edit a short clip for practice", emoji: "✂️" },
      { name: "Write a scene or treatment idea", emoji: "📝" },
      { name: "Watch a film and note the editing choices", emoji: "🎥" },
      { name: "Review today's project feedback", emoji: "📝" },
    ],
    "Broadcasting": [
      { name: "Practice a broadcast script", emoji: "🎙️" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Review today's recording/feedback", emoji: "🎧" },
      { name: "Listen to/analyze a broadcast segment", emoji: "📻" },
      { name: "Practice enunciation/diction drills", emoji: "🗣️" },
      { name: "Record and review my delivery", emoji: "🎙️" },
      { name: "Study a broadcast format/rundown", emoji: "📋" },
      { name: "Practice ad-libbing on a topic", emoji: "💬" },
      { name: "Listen to a broadcast and take notes", emoji: "🎧" },
      { name: "Review today's studio feedback", emoji: "📝" },
    ],
  },

  communication: {
    "Communication Arts": [
      { name: "Draft or edit a piece of content", emoji: "📝" },
      { name: "Review today's assignment feedback", emoji: "📚" },
      { name: "Practice a presentation", emoji: "🎤" },
      { name: "Practice a public-speaking exercise", emoji: "🎤" },
      { name: "Practice a persuasive-speech outline", emoji: "🎤" },
      { name: "Analyze an ad or campaign's messaging", emoji: "🔍" },
      { name: "Write a short opinion piece", emoji: "📝" },
      { name: "Practice a media-interview scenario", emoji: "🎙️" },
      { name: "Record a mock presentation and review it", emoji: "🎥" },
      { name: "Study a communication-theory concept", emoji: "📘" },
    ],
    "Journalism": [
      { name: "Draft or edit a story", emoji: "📰" },
      { name: "Review today's assignment feedback", emoji: "📚" },
      { name: "Research a story lead", emoji: "🔍" },
      { name: "Practice a photojournalism shot", emoji: "📸" },
      { name: "Practice an interview-question list", emoji: "🗒️" },
      { name: "Fact-check a sample story", emoji: "✅" },
      { name: "Read two news outlets on the same story", emoji: "📰" },
      { name: "Study a journalism-ethics concept", emoji: "📘" },
      { name: "Pitch a story idea", emoji: "💡" },
      { name: "Study a news outlet's editorial style", emoji: "📰" },
    ],
    "Broadcasting": [
      { name: "Practice a broadcast script", emoji: "🎙️" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Review today's recording/feedback", emoji: "🎧" },
      { name: "Listen to/analyze a broadcast segment", emoji: "📻" },
      { name: "Practice enunciation/diction drills", emoji: "🗣️" },
      { name: "Record and review my delivery", emoji: "🎙️" },
      { name: "Study a broadcast format/rundown", emoji: "📋" },
      { name: "Practice ad-libbing on a topic", emoji: "💬" },
      { name: "Listen to a broadcast and take notes", emoji: "🎧" },
      { name: "Review today's studio feedback", emoji: "📝" },
    ],
    "Advertising": [
      { name: "Analyze one ad campaign", emoji: "📣" },
      { name: "Sketch a campaign idea", emoji: "💡" },
      { name: "Review today's assignment feedback", emoji: "📚" },
      { name: "Design a mock ad visual", emoji: "🖼️" },
      { name: "Study a successful ad campaign's strategy", emoji: "📣" },
      { name: "Write three headline variations", emoji: "✍️" },
      { name: "Practice a client-pitch outline", emoji: "📋" },
      { name: "Research a target-audience persona", emoji: "🎯" },
      { name: "Pitch a campaign concept to a peer", emoji: "🗣️" },
      { name: "Sketch a storyboard for a concept", emoji: "🖼️" },
    ],
    "Public Relations": [
      { name: "Draft a press release/pitch", emoji: "📝" },
      { name: "Review today's assignment feedback", emoji: "📚" },
      { name: "Read one PR case study", emoji: "📰" },
      { name: "Track a brand in the news", emoji: "📰" },
      { name: "Study a crisis-communication case", emoji: "🚨" },
      { name: "Draft a media list/pitch angle", emoji: "📋" },
      { name: "Read a PR industry update", emoji: "📰" },
      { name: "Practice a talking-points outline", emoji: "🗣️" },
      { name: "Draft a stakeholder-communication note", emoji: "📝" },
      { name: "Monitor a brand's media coverage", emoji: "📊" },
    ],
    "Film / Cinema": [
      { name: "Watch/analyze one film critically", emoji: "🎬" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Work on a project for 20 min", emoji: "🎥" },
      { name: "Study a film's cinematography", emoji: "🎞️" },
      { name: "Storyboard a short scene", emoji: "🎬" },
      { name: "Practice a shot-composition exercise", emoji: "📷" },
      { name: "Edit a short clip for practice", emoji: "✂️" },
      { name: "Write a scene or treatment idea", emoji: "📝" },
      { name: "Watch a film and note the editing choices", emoji: "🎥" },
      { name: "Review today's project feedback", emoji: "📝" },
    ],
    "Digital Media": [
      { name: "Draft or edit a piece of content", emoji: "📱" },
      { name: "Review today's assignment feedback", emoji: "📚" },
      { name: "Review today's content performance", emoji: "📊" },
      { name: "Study a viral post's strategy", emoji: "📊" },
      { name: "Study a platform's algorithm update", emoji: "📱" },
      { name: "Practice a short-form video edit", emoji: "🎬" },
      { name: "Analyze a viral post's structure", emoji: "🔍" },
      { name: "Draft a content calendar idea", emoji: "🗓️" },
      { name: "Get feedback on a work-in-progress", emoji: "📝" },
      { name: "Study a design trend for digital content", emoji: "🎨" },
    ],
  },

  hospitality: {
    "Hotel and Restaurant Management": [
      { name: "Practice a guest-service scenario", emoji: "🛎️" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Research a hospitality trend", emoji: "🍽️" },
      { name: "Study a hotel's service standard", emoji: "🏨" },
      { name: "Study a front-desk workflow", emoji: "🗝️" },
      { name: "Practice a complaint-resolution script", emoji: "🗣️" },
      { name: "Review a food-and-beverage service standard", emoji: "🍷" },
      { name: "Read about a hotel brand's guest experience", emoji: "🏨" },
      { name: "Practice a reservation-system exercise", emoji: "💻" },
      { name: "Review today's practicum feedback", emoji: "📝" },
    ],
    "Tourism Management": [
      { name: "Research a travel destination", emoji: "✈️" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Plan a sample itinerary", emoji: "🗺️" },
      { name: "Research a tourism marketing campaign", emoji: "📸" },
      { name: "Study a tour-package pricing example", emoji: "💰" },
      { name: "Read about a local tourism spot", emoji: "🏝️" },
      { name: "Practice a tour-guiding script", emoji: "🎤" },
      { name: "Review a travel-safety protocol", emoji: "🛡️" },
      { name: "Study a country's visa/travel requirement", emoji: "🛂" },
      { name: "Review today's practicum feedback", emoji: "📝" },
    ],
    "Culinary Arts": [
      { name: "Practice a recipe/technique", emoji: "🍳" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Review today's kitchen notes", emoji: "🔪" },
      { name: "Plate a dish for presentation practice", emoji: "🍽️" },
      { name: "Practice a knife-skill drill", emoji: "🔪" },
      { name: "Study a mother-sauce/technique concept", emoji: "🍲" },
      { name: "Cost out a sample recipe", emoji: "🧮" },
      { name: "Practice a plating composition", emoji: "🍽️" },
      { name: "Taste-test and refine a seasoning", emoji: "👅" },
      { name: "Review today's kitchen feedback", emoji: "📝" },
    ],
    "Cruise Line Operations": [
      { name: "Review today's lecture notes", emoji: "🚢" },
      { name: "Study for my next exam", emoji: "📚" },
      { name: "Practice a guest-service scenario", emoji: "🛎️" },
      { name: "Study a cruise itinerary/route", emoji: "🚢" },
      { name: "Study a shipboard safety drill", emoji: "🛟" },
      { name: "Practice a shipboard etiquette scenario", emoji: "🛎️" },
      { name: "Review a cruise-itinerary logistics case", emoji: "🗺️" },
      { name: "Study a maritime-hospitality standard", emoji: "🚢" },
      { name: "Practice an emergency-procedure drill", emoji: "🚨" },
      { name: "Review today's practicum feedback", emoji: "📝" },
    ],
    "Airline Operations / Aviation": [
      { name: "Review today's lecture notes", emoji: "✈️" },
      { name: "Study for my next exam", emoji: "📚" },
      { name: "Review a flight-ops concept", emoji: "🛫" },
      { name: "Study an airline safety procedure", emoji: "🛫" },
      { name: "Study an aviation-safety procedure", emoji: "🛫" },
      { name: "Practice a boarding/check-in scenario", emoji: "🎫" },
      { name: "Review a flight-operations concept", emoji: "✈️" },
      { name: "Study a customer-service recovery script", emoji: "🗣️" },
      { name: "Review an aviation-regulation update", emoji: "📘" },
      { name: "Review today's practicum feedback", emoji: "📝" },
    ],
  },

  maritime: {
    "Marine Transportation (Deck)": [
      { name: "Review today's navigation notes", emoji: "⚓" },
      { name: "Log my watch/training hours", emoji: "📚" },
      { name: "Study for my next certification exam", emoji: "🧭" },
      { name: "Study a maritime-law concept", emoji: "🌊" },
      { name: "Practice a knot-tying drill", emoji: "🪢" },
      { name: "Study a navigation-rules concept", emoji: "🧭" },
      { name: "Review a chart-plotting exercise", emoji: "🗺️" },
      { name: "Study a collision-regulation provision", emoji: "⚓" },
      { name: "Practice a watch-keeping scenario", emoji: "👀" },
      { name: "Review today's training feedback", emoji: "📝" },
    ],
    "Marine Engineering": [
      { name: "Review today's engine room notes", emoji: "⚙️" },
      { name: "Log my watch/training hours", emoji: "📚" },
      { name: "Study for my next certification exam", emoji: "🔧" },
      { name: "Study a vessel-systems concept", emoji: "⚓" },
      { name: "Study an engine-room systems concept", emoji: "⚙️" },
      { name: "Practice a maintenance-checklist exercise", emoji: "🔧" },
      { name: "Review a marine-safety procedure", emoji: "🦺" },
      { name: "Study a propulsion-system diagram", emoji: "🚢" },
      { name: "Practice a troubleshooting scenario", emoji: "🛠️" },
      { name: "Review today's training feedback", emoji: "📝" },
    ],
  },

  criminology: {
    "Criminology": [
      { name: "Review today's case study notes", emoji: "🔍" },
      { name: "Study for my board exam prep", emoji: "📚" },
      { name: "Practice a report-writing exercise", emoji: "📝" },
      { name: "Study a criminal-justice case", emoji: "🔍" },
      { name: "Study a crime-theory concept", emoji: "🧠" },
      { name: "Practice a crime-scene procedure", emoji: "🔍" },
      { name: "Review a penal-code provision", emoji: "📘" },
      { name: "Read about a solved case study", emoji: "📰" },
      { name: "Practice a physical-fitness drill for exams", emoji: "🏃" },
      { name: "Time myself on a mock board question", emoji: "⏱️" },
    ],
    "Forensic Science": [
      { name: "Review a case study", emoji: "🔬" },
      { name: "Review today's lecture notes", emoji: "📚" },
      { name: "Review a lab procedure", emoji: "🧪" },
      { name: "Study an evidence-analysis technique", emoji: "🕵️" },
      { name: "Practice an evidence-documentation exercise", emoji: "📸" },
      { name: "Study a forensic-analysis technique", emoji: "🔬" },
      { name: "Read about a solved cold case", emoji: "📰" },
      { name: "Review a chain-of-custody procedure", emoji: "📋" },
      { name: "Practice a crime-scene sketching exercise", emoji: "✏️" },
      { name: "Review today's lab-report corrections", emoji: "✅" },
    ],
    "Industrial Security Management": [
      { name: "Review today's case study notes", emoji: "🛡️" },
      { name: "Study for my board exam prep", emoji: "📚" },
      { name: "Practice a report-writing exercise", emoji: "📝" },
      { name: "Review a security-protocol case", emoji: "🎥" },
      { name: "Study a security-risk-assessment concept", emoji: "🛡️" },
      { name: "Practice a patrol/inspection checklist", emoji: "📋" },
      { name: "Review an access-control procedure", emoji: "🔐" },
      { name: "Read about a security-breach case", emoji: "📰" },
      { name: "Practice an incident-report exercise", emoji: "📝" },
      { name: "Time myself on a mock board question", emoji: "⏱️" },
    ],
  },
};

export const WORK_JOB_HABITS = {
  "biz-finance": {
    "Accountant": [
      { name: "Review today's ledger/report", emoji: "🧮" },
      { name: "Read up on an accounting standard", emoji: "📚" },
      { name: "Reconcile a sample account", emoji: "📝" },
      { name: "Review an upcoming filing deadline", emoji: "📅" },
      { name: "Read up on a new tax regulation", emoji: "📘" },
      { name: "Double-check a client's supporting documents", emoji: "📎" },
      { name: "Practice a spreadsheet shortcut/formula", emoji: "⌨️" },
      { name: "Organize my filing folder for the month", emoji: "🗂️" },
      { name: "Review a variance between actual and budget", emoji: "📊" },
      { name: "Back up today's work files", emoji: "💾" },
    ],
    "Auditor": [
      { name: "Review today's audit notes", emoji: "🔍" },
      { name: "Read up on an auditing standard", emoji: "📚" },
      { name: "Practice a checklist review", emoji: "📝" },
      { name: "Review an audit checklist template", emoji: "📋" },
      { name: "Sample-test a set of transactions", emoji: "🎯" },
      { name: "Update my audit working papers", emoji: "📝" },
      { name: "Read up on an auditing-standard change", emoji: "📘" },
      { name: "Review a risk-assessment matrix", emoji: "⚠️" },
      { name: "Follow up on an open audit finding", emoji: "📌" },
      { name: "Back up today's work files", emoji: "💾" },
    ],
    "Financial Analyst": [
      { name: "Review today's key numbers/report", emoji: "📈" },
      { name: "Read a market update", emoji: "📊" },
      { name: "Practice a financial model", emoji: "📝" },
      { name: "Build a quick financial projection", emoji: "📉" },
      { name: "Update a financial model with new data", emoji: "🧮" },
      { name: "Read a company earnings report", emoji: "📈" },
      { name: "Benchmark a competitor's metrics", emoji: "📊" },
      { name: "Review today's variance analysis", emoji: "📉" },
      { name: "Prep a slide/summary for stakeholders", emoji: "📑" },
      { name: "Back up today's work files", emoji: "💾" },
    ],
    "Bookkeeper": [
      { name: "Log today's transactions", emoji: "🧾" },
      { name: "Reconcile a sample ledger", emoji: "🧮" },
      { name: "Read up on a bookkeeping tip", emoji: "📚" },
      { name: "Organize a batch of receipts/records", emoji: "🗃️" },
      { name: "Categorize today's transactions", emoji: "🏷️" },
      { name: "Match receipts to entries", emoji: "🧾" },
      { name: "Follow up on an overdue invoice", emoji: "📞" },
      { name: "Review a client's cash-flow snapshot", emoji: "💵" },
      { name: "Update my bookkeeping checklist", emoji: "📋" },
      { name: "Back up today's work files", emoji: "💾" },
    ],
    "Bank Teller": [
      { name: "Review today's transaction notes", emoji: "💵" },
      { name: "Double-check today's cash count", emoji: "🧮" },
      { name: "Read up on a banking policy", emoji: "📚" },
      { name: "Review a new banking product/policy", emoji: "💳" },
      { name: "Review today's transaction log for errors", emoji: "🔍" },
      { name: "Restock counter supplies/forms", emoji: "🗂️" },
      { name: "Practice explaining a product to a customer", emoji: "🗣️" },
      { name: "Review an anti-fraud checklist", emoji: "🔐" },
      { name: "Confirm tomorrow's shift schedule", emoji: "📅" },
      { name: "Rest properly between shifts", emoji: "💤" },
    ],
    "Loan Officer": [
      { name: "Review today's applications", emoji: "📄" },
      { name: "Read up on a lending policy", emoji: "📊" },
      { name: "Follow up with one client", emoji: "📝" },
      { name: "Review a lending-risk case", emoji: "📊" },
      { name: "Update a client's application status", emoji: "📄" },
      { name: "Review a credit-risk factor checklist", emoji: "📊" },
      { name: "Follow up on pending documents", emoji: "📞" },
      { name: "Read up on a lending-rate update", emoji: "📈" },
      { name: "Prep for tomorrow's client meetings", emoji: "📅" },
      { name: "Back up today's work files", emoji: "💾" },
    ],
    "Investment Analyst": [
      { name: "Read a market update", emoji: "📈" },
      { name: "Review today's portfolio notes", emoji: "📊" },
      { name: "Practice a valuation exercise", emoji: "📝" },
      { name: "Track a stock/fund's performance", emoji: "💼" },
      { name: "Update a watchlist with today's moves", emoji: "📈" },
      { name: "Read one market/industry report", emoji: "📰" },
      { name: "Build a quick comparable-company table", emoji: "📊" },
      { name: "Review a portfolio's risk exposure", emoji: "⚠️" },
      { name: "Prep notes for tomorrow's review", emoji: "📝" },
      { name: "Back up today's work files", emoji: "💾" },
    ],
    "Business Analyst": [
      { name: "Review today's project notes", emoji: "📊" },
      { name: "Draft a process/requirements note", emoji: "📝" },
      { name: "Read up on a BA technique", emoji: "📚" },
      { name: "Map a business process flow", emoji: "📈" },
      { name: "Update a requirements document", emoji: "📝" },
      { name: "Review today's stakeholder feedback", emoji: "🗣️" },
      { name: "Sketch a process-flow diagram", emoji: "🔀" },
      { name: "Read up on a BA framework/technique", emoji: "📘" },
      { name: "Prep an agenda for tomorrow's meeting", emoji: "📅" },
      { name: "Back up today's work files", emoji: "💾" },
    ],
    "Administrative Assistant": [
      { name: "Clear my inbox before logging off", emoji: "🗂️" },
      { name: "Plan tomorrow's schedule", emoji: "📅" },
      { name: "Tidy my task list", emoji: "📝" },
      { name: "Confirm tomorrow's appointments/meetings", emoji: "📞" },
      { name: "Organize a shared drive/folder", emoji: "🗂️" },
      { name: "Draft tomorrow's meeting agenda", emoji: "📝" },
      { name: "Order/restock office supplies", emoji: "📦" },
      { name: "Update a contact/records list", emoji: "📇" },
      { name: "Follow up on an outstanding request", emoji: "📞" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Executive Assistant": [
      { name: "Plan tomorrow's schedule", emoji: "📅" },
      { name: "Clear my inbox before logging off", emoji: "🗂️" },
      { name: "Prep notes for a meeting", emoji: "📝" },
      { name: "Confirm a travel/meeting arrangement", emoji: "✈️" },
      { name: "Reconcile the exec's calendar for conflicts", emoji: "📅" },
      { name: "Draft a meeting-briefing note", emoji: "📝" },
      { name: "Confirm tomorrow's first appointment", emoji: "📞" },
      { name: "Organize expense receipts", emoji: "🧾" },
      { name: "Follow up on an outstanding request", emoji: "📞" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Office Manager": [
      { name: "Review today's office tasks", emoji: "🗂️" },
      { name: "Plan tomorrow's priorities", emoji: "📅" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "📝" },
      { name: "Check office supply inventory", emoji: "📦" },
      { name: "Check the office supply/equipment inventory", emoji: "📦" },
      { name: "Follow up on a maintenance request", emoji: "🛠️" },
      { name: "Update the office calendar/bookings", emoji: "📅" },
      { name: "Check in with one team member", emoji: "🧑‍🤝‍🧑" },
      { name: "Review this month's office budget", emoji: "💰" },
      { name: "Read an industry newsletter/update", emoji: "📰" },
    ],
    "HR Generalist": [
      { name: "Review today's HR tasks", emoji: "🧑‍🤝‍🧑" },
      { name: "Follow up on one open item", emoji: "📝" },
      { name: "Read up on an HR policy", emoji: "📚" },
      { name: "Review an onboarding checklist", emoji: "📋" },
      { name: "Update an employee file/record", emoji: "📇" },
      { name: "Follow up on a pending HR request", emoji: "📞" },
      { name: "Read up on a labor-law update", emoji: "📘" },
      { name: "Draft an internal HR announcement", emoji: "📣" },
      { name: "Review this week's attendance/leave log", emoji: "📅" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "HR Recruiter": [
      { name: "Review today's candidate pipeline", emoji: "📄" },
      { name: "Follow up with one candidate", emoji: "🧑‍🤝‍🧑" },
      { name: "Prep for an interview", emoji: "📝" },
      { name: "Post or refresh a job listing", emoji: "📢" },
      { name: "Screen a batch of applications", emoji: "📄" },
      { name: "Update my candidate tracker", emoji: "📊" },
      { name: "Draft interview questions for a role", emoji: "❓" },
      { name: "Follow up with a hiring manager", emoji: "📞" },
      { name: "Refresh a job description", emoji: "📝" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Payroll Specialist": [
      { name: "Review today's payroll notes", emoji: "💰" },
      { name: "Double-check a sample computation", emoji: "🧮" },
      { name: "Read up on a payroll policy", emoji: "📚" },
      { name: "Review the payroll calendar", emoji: "📅" },
      { name: "Double-check a batch of timesheets", emoji: "⏱️" },
      { name: "Reconcile a payroll discrepancy", emoji: "🔍" },
      { name: "Read up on a tax/contribution update", emoji: "📘" },
      { name: "Update the payroll processing checklist", emoji: "✅" },
      { name: "File a batch of payroll records", emoji: "🗂️" },
      { name: "Back up today's work files", emoji: "💾" },
    ],
    "Operations Manager": [
      { name: "Review today's operations report", emoji: "⚙️" },
      { name: "Plan tomorrow's priorities", emoji: "📅" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "📝" },
      { name: "Review a process-improvement idea", emoji: "🔄" },
      { name: "Review today's KPI dashboard", emoji: "📊" },
      { name: "Check in with one team lead", emoji: "🧑‍🤝‍🧑" },
      { name: "Follow up on a blocked process", emoji: "🚧" },
      { name: "Read up on an ops-efficiency idea", emoji: "📘" },
      { name: "Update the operations tracker", emoji: "📝" },
      { name: "Tidy my digital workspace/files", emoji: "🖥️" },
    ],
    "Project Manager": [
      { name: "Review today's project status", emoji: "📋" },
      { name: "Plan tomorrow's priorities", emoji: "📅" },
      { name: "Update my project tracker", emoji: "📝" },
      { name: "Review today's project risks", emoji: "⚠️" },
      { name: "Update the project risk register", emoji: "⚠️" },
      { name: "Follow up on a blocked task", emoji: "🚧" },
      { name: "Prep tomorrow's stand-up notes", emoji: "📋" },
      { name: "Review this week's milestone progress", emoji: "📈" },
      { name: "Check in with one team member", emoji: "🧑‍🤝‍🧑" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Management Consultant": [
      { name: "Review today's client notes", emoji: "📊" },
      { name: "Draft a recommendation/insight", emoji: "📝" },
      { name: "Read up on an industry trend", emoji: "📚" },
      { name: "Benchmark against an industry standard", emoji: "📊" },
      { name: "Update a client deliverable draft", emoji: "📝" },
      { name: "Read an industry benchmark report", emoji: "📊" },
      { name: "Prep talking points for a client call", emoji: "🗣️" },
      { name: "Refine a slide in my deck", emoji: "📑" },
      { name: "Review today's client feedback", emoji: "📋" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Compliance Officer": [
      { name: "Review today's compliance checklist", emoji: "📋" },
      { name: "Read up on a regulation update", emoji: "📚" },
      { name: "Log a compliance note", emoji: "📝" },
      { name: "Log today's audit trail", emoji: "📝" },
      { name: "Review a policy for a needed update", emoji: "📘" },
      { name: "Follow up on an open compliance item", emoji: "📌" },
      { name: "Read up on a regulatory-change alert", emoji: "🚨" },
      { name: "Update the compliance-tracking log", emoji: "📝" },
      { name: "Prep a compliance-training note", emoji: "🎓" },
      { name: "Back up today's work files", emoji: "💾" },
    ],
    "Insurance Agent": [
      { name: "Follow up with one client/lead", emoji: "📄" },
      { name: "Read up on a policy update", emoji: "📚" },
      { name: "Review today's leads", emoji: "📝" },
      { name: "Review a policy renewal", emoji: "📄" },
      { name: "Update my client contact list", emoji: "📇" },
      { name: "Prep a quote for a prospect", emoji: "📄" },
      { name: "Read up on a policy/product update", emoji: "📘" },
      { name: "Follow up on a pending claim", emoji: "📞" },
      { name: "Review my monthly sales target", emoji: "📊" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Real Estate Agent / Broker": [
      { name: "Follow up with one client/lead", emoji: "🏠" },
      { name: "Review today's listings", emoji: "📸" },
      { name: "Research a market trend", emoji: "📝" },
      { name: "Compare comparable property prices", emoji: "📐" },
      { name: "Update my property listings", emoji: "🏠" },
      { name: "Follow up with a prospective buyer", emoji: "📞" },
      { name: "Research comparable sales in an area", emoji: "📊" },
      { name: "Schedule a property viewing", emoji: "📅" },
      { name: "Refresh my listing photos/descriptions", emoji: "📸" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
  },

  tech: {
    "Software Engineer / Developer": [
      { name: "Review my pull requests/tickets", emoji: "💻" },
      { name: "Learn something new in my stack", emoji: "📚" },
      { name: "Take a proper screen break", emoji: "🖥️" },
      { name: "Read a peer's code for practice", emoji: "📖" },
      { name: "Clean up a section of legacy code", emoji: "🧹" },
      { name: "Write or update a test case", emoji: "🧪" },
      { name: "Document a function/module I built", emoji: "📄" },
      { name: "Pair with a teammate on a problem", emoji: "🧑‍💻" },
      { name: "Review today's standup notes", emoji: "📋" },
      { name: "Explore a new tool or library", emoji: "🧭" },
    ],
    "Web Developer": [
      { name: "Review my pull requests/tickets", emoji: "💻" },
      { name: "Learn a new web tool/framework", emoji: "🌐" },
      { name: "Take a proper screen break", emoji: "🖥️" },
      { name: "Check my site's responsive design", emoji: "🎨" },
      { name: "Fix one accessibility issue", emoji: "♿" },
      { name: "Optimize a page's load speed", emoji: "⚡" },
      { name: "Cross-browser test a recent change", emoji: "🌐" },
      { name: "Document a component I built", emoji: "📄" },
      { name: "Review today's standup notes", emoji: "📋" },
      { name: "Explore a new front-end tool", emoji: "🧭" },
    ],
    "Mobile App Developer": [
      { name: "Review my pull requests/tickets", emoji: "📱" },
      { name: "Learn something new in my stack", emoji: "📚" },
      { name: "Take a proper screen break", emoji: "🖥️" },
      { name: "Test my app on a real device", emoji: "📲" },
      { name: "Fix one crash/bug report", emoji: "🐞" },
      { name: "Test my app on an older device", emoji: "📱" },
      { name: "Document a feature I built", emoji: "📄" },
      { name: "Review today's app-store feedback", emoji: "⭐" },
      { name: "Review today's standup notes", emoji: "📋" },
      { name: "Explore a new SDK/library update", emoji: "🧭" },
    ],
    "QA / Test Engineer": [
      { name: "Review today's bug reports", emoji: "🐞" },
      { name: "Run through a test checklist", emoji: "✅" },
      { name: "Take a proper screen break", emoji: "🖥️" },
      { name: "Write a new test case", emoji: "🧩" },
      { name: "Automate one manual test case", emoji: "🤖" },
      { name: "Regression-test a recent release", emoji: "🔁" },
      { name: "Update the test-case documentation", emoji: "📄" },
      { name: "Triage today's incoming bugs", emoji: "🐞" },
      { name: "Review today's standup notes", emoji: "📋" },
      { name: "Explore a new testing tool", emoji: "🧭" },
    ],
    "DevOps Engineer": [
      { name: "Review today's pipeline/deploy notes", emoji: "⚙️" },
      { name: "Learn something new in my stack", emoji: "📚" },
      { name: "Take a proper screen break", emoji: "🖥️" },
      { name: "Check today's system monitoring dashboard", emoji: "📉" },
      { name: "Review today's deployment logs", emoji: "📜" },
      { name: "Optimize a build/pipeline step", emoji: "⚙️" },
      { name: "Check today's infrastructure alerts", emoji: "🚨" },
      { name: "Document an infra change I made", emoji: "📄" },
      { name: "Review today's standup notes", emoji: "📋" },
      { name: "Explore a new DevOps tool", emoji: "🧭" },
    ],
    "Data Analyst": [
      { name: "Review today's data/report", emoji: "📊" },
      { name: "Practice a data-analysis exercise", emoji: "📈" },
      { name: "Take a proper screen break", emoji: "🖥️" },
      { name: "Build a quick data visualization", emoji: "📊" },
      { name: "Clean a messy data source", emoji: "🧹" },
      { name: "Refresh a recurring report", emoji: "🔄" },
      { name: "Write a SQL query for a new question", emoji: "🗄️" },
      { name: "Document a metric's definition", emoji: "📄" },
      { name: "Review today's stakeholder feedback", emoji: "🗣️" },
      { name: "Explore a new data-viz technique", emoji: "📊" },
    ],
    "Data Scientist": [
      { name: "Review today's model/data notes", emoji: "📊" },
      { name: "Practice a data exercise", emoji: "📈" },
      { name: "Take a proper screen break", emoji: "🖥️" },
      { name: "Read up on a new modeling technique", emoji: "🧠" },
      { name: "Tune a model's parameters", emoji: "🎛️" },
      { name: "Validate a model against new data", emoji: "✅" },
      { name: "Document my experiment's results", emoji: "📄" },
      { name: "Read a paper relevant to my project", emoji: "📖" },
      { name: "Review today's stakeholder feedback", emoji: "🗣️" },
      { name: "Explore a new modeling technique", emoji: "🧭" },
    ],
    "Data Engineer": [
      { name: "Review today's pipeline notes", emoji: "🗄️" },
      { name: "Learn something new in my stack", emoji: "📚" },
      { name: "Take a proper screen break", emoji: "🖥️" },
      { name: "Check today's data pipeline health", emoji: "🔄" },
      { name: "Check today's pipeline run logs", emoji: "📜" },
      { name: "Fix a data-quality issue", emoji: "🧹" },
      { name: "Document a pipeline I built", emoji: "📄" },
      { name: "Optimize a slow query/job", emoji: "⚡" },
      { name: "Review today's standup notes", emoji: "📋" },
      { name: "Explore a new data tool", emoji: "🧭" },
    ],
    "IT Support Specialist": [
      { name: "Review today's tickets", emoji: "🛠️" },
      { name: "Troubleshoot a sample issue", emoji: "🔧" },
      { name: "Take a proper screen break", emoji: "🖥️" },
      { name: "Update my troubleshooting knowledge base", emoji: "📚" },
      { name: "Resolve one open ticket fully", emoji: "✅" },
      { name: "Document a fix for the knowledge base", emoji: "📄" },
      { name: "Check today's system-health alerts", emoji: "🚨" },
      { name: "Follow up on an unresolved issue", emoji: "📞" },
      { name: "Review today's ticket queue", emoji: "🎫" },
      { name: "Explore a new support tool", emoji: "🧭" },
    ],
    "Systems Administrator": [
      { name: "Review today's system logs/notes", emoji: "🖧" },
      { name: "Run a routine systems check", emoji: "🔧" },
      { name: "Take a proper screen break", emoji: "🖥️" },
      { name: "Review today's access/security logs", emoji: "🔐" },
      { name: "Apply pending system updates", emoji: "🔄" },
      { name: "Review today's backup logs", emoji: "💾" },
      { name: "Document a config change I made", emoji: "📄" },
      { name: "Check today's server-health alerts", emoji: "🚨" },
      { name: "Review today's access requests", emoji: "🔐" },
      { name: "Explore a new sysadmin tool", emoji: "🧭" },
    ],
    "Network Engineer": [
      { name: "Review today's network notes", emoji: "🌐" },
      { name: "Practice a network configuration exercise", emoji: "🔧" },
      { name: "Take a proper screen break", emoji: "🖥️" },
      { name: "Check today's network performance", emoji: "📶" },
      { name: "Review today's network alerts", emoji: "🚨" },
      { name: "Document a network change I made", emoji: "📄" },
      { name: "Check bandwidth/latency reports", emoji: "📶" },
      { name: "Run a routine connectivity test", emoji: "🔌" },
      { name: "Review today's standup notes", emoji: "📋" },
      { name: "Explore a new networking tool", emoji: "🧭" },
    ],
    "Cybersecurity Analyst": [
      { name: "Review today's security alerts", emoji: "🔐" },
      { name: "Read up on a security concept", emoji: "🛡️" },
      { name: "Take a proper screen break", emoji: "🖥️" },
      { name: "Practice a threat-detection drill", emoji: "🕵️" },
      { name: "Triage today's security alerts", emoji: "🚨" },
      { name: "Review a vulnerability scan result", emoji: "🔍" },
      { name: "Document an incident's findings", emoji: "📄" },
      { name: "Read a threat-intelligence update", emoji: "📰" },
      { name: "Review today's access logs", emoji: "🔐" },
      { name: "Explore a new security tool", emoji: "🧭" },
    ],
    "Database Administrator": [
      { name: "Review today's database notes", emoji: "🗄️" },
      { name: "Run a routine backup/check", emoji: "🔧" },
      { name: "Take a proper screen break", emoji: "🖥️" },
      { name: "Optimize a slow query", emoji: "🗃️" },
      { name: "Review today's database logs", emoji: "📜" },
      { name: "Check today's backup integrity", emoji: "💾" },
      { name: "Document a schema change I made", emoji: "📄" },
      { name: "Set one clear goal for tomorrow", emoji: "🎯" },
      { name: "Review today's standup notes", emoji: "📋" },
      { name: "Explore a new database tool", emoji: "🧭" },
    ],
    "UX/UI Designer": [
      { name: "Review today's design feedback", emoji: "🎨" },
      { name: "Work on a design file", emoji: "🖌️" },
      { name: "Take a proper screen break", emoji: "🖥️" },
      { name: "Run a quick usability check", emoji: "🧪" },
      { name: "Update a design-system component", emoji: "🧩" },
      { name: "Check in with a colleague", emoji: "🧑‍🤝‍🧑" },
      { name: "Document a design decision", emoji: "📄" },
      { name: "Explore a new design tool/plugin", emoji: "🧭" },
      { name: "Review today's stakeholder feedback", emoji: "🗣️" },
      { name: "Study a competitor's UX flow", emoji: "🔍" },
    ],
    "Product Manager (Tech)": [
      { name: "Review today's product notes", emoji: "📋" },
      { name: "Plan tomorrow's priorities", emoji: "📅" },
      { name: "Take a proper screen break", emoji: "🖥️" },
      { name: "Gather one piece of user feedback", emoji: "🗣️" },
      { name: "Update the product roadmap", emoji: "🗺️" },
      { name: "Write a user story/spec", emoji: "📝" },
      { name: "Review today's usage/analytics data", emoji: "📊" },
      { name: "Prep for tomorrow's planning meeting", emoji: "📅" },
      { name: "Review my calendar for conflicts", emoji: "📅" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Cloud Engineer": [
      { name: "Review today's infrastructure notes", emoji: "☁️" },
      { name: "Learn something new in my stack", emoji: "📚" },
      { name: "Take a proper screen break", emoji: "🖥️" },
      { name: "Review today's cloud-cost report", emoji: "💰" },
      { name: "Note one lesson learned today", emoji: "🪞" },
      { name: "Check today's infrastructure alerts", emoji: "🚨" },
      { name: "Document an infra change I made", emoji: "📄" },
      { name: "Optimize a resource for cost/performance", emoji: "⚙️" },
      { name: "Review today's standup notes", emoji: "📋" },
      { name: "Explore a new cloud service", emoji: "🧭" },
    ],
    "AI/ML Engineer": [
      { name: "Review today's model results", emoji: "🤖" },
      { name: "Read up on an ML concept", emoji: "📚" },
      { name: "Take a proper screen break", emoji: "🖥️" },
      { name: "Evaluate a model's accuracy metrics", emoji: "📊" },
      { name: "Silence notifications for a focused hour", emoji: "🔕" },
      { name: "Retrain a model with fresh data", emoji: "🔄" },
      { name: "Document my experiment's results", emoji: "📄" },
      { name: "Read a paper relevant to my project", emoji: "📖" },
      { name: "Review today's standup notes", emoji: "📋" },
      { name: "Explore a new ML framework/tool", emoji: "🧭" },
    ],
    "Game Developer": [
      { name: "Review my pull requests/tickets", emoji: "🎮" },
      { name: "Learn something new in my stack", emoji: "📚" },
      { name: "Take a proper screen break", emoji: "🖥️" },
      { name: "Playtest my game and take notes", emoji: "🎮" },
      { name: "Fix one bug/glitch report", emoji: "🐞" },
      { name: "Update my skills/learning list", emoji: "📚" },
      { name: "Document a feature I built", emoji: "📄" },
      { name: "Balance a gameplay stat/mechanic", emoji: "⚖️" },
      { name: "Review today's standup notes", emoji: "📋" },
      { name: "Explore a new engine feature/tool", emoji: "🧭" },
    ],
  },

  engineering: {
    "Civil Engineer": [
      { name: "Review today's site/project notes", emoji: "🏗️" },
      { name: "Check in on my project timeline", emoji: "📐" },
      { name: "Review safety checklist before starting", emoji: "🦺" },
      { name: "Review a structural drawing", emoji: "📏" },
      { name: "Review today's site progress report", emoji: "📋" },
      { name: "Update a structural drawing revision", emoji: "📐" },
      { name: "Check materials delivered on-site", emoji: "📦" },
      { name: "Read up on a building-code update", emoji: "📘" },
      { name: "Review a subcontractor's work quality", emoji: "🔍" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Mechanical Engineer": [
      { name: "Review today's project notes", emoji: "⚙️" },
      { name: "Check in on my project timeline", emoji: "🛠️" },
      { name: "Review safety checklist before starting", emoji: "🦺" },
      { name: "Inspect a machine/part design", emoji: "🔩" },
      { name: "Review today's design/spec notes", emoji: "📋" },
      { name: "Take a short walk to reset", emoji: "🚶" },
      { name: "Update a CAD drawing revision", emoji: "🖥️" },
      { name: "Read up on a materials/standard update", emoji: "📘" },
      { name: "Review a test/inspection result", emoji: "🔍" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Electrical Engineer": [
      { name: "Review today's project notes", emoji: "⚡" },
      { name: "Check in on my project timeline", emoji: "🔌" },
      { name: "Review safety checklist before starting", emoji: "🦺" },
      { name: "Review a wiring diagram", emoji: "⚡" },
      { name: "Write down three things that went well", emoji: "✍️" },
      { name: "Update an electrical-load calculation", emoji: "🧮" },
      { name: "Check today's site/panel inspection notes", emoji: "🔍" },
      { name: "Read up on a code/standard update", emoji: "📘" },
      { name: "Review a subcontractor's work quality", emoji: "🔍" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Electronics Engineer": [
      { name: "Review today's project notes", emoji: "🔧" },
      { name: "Check in on my project timeline", emoji: "📟" },
      { name: "Review safety checklist before starting", emoji: "🦺" },
      { name: "Test a circuit prototype", emoji: "🔬" },
      { name: "Declutter one shelf/drawer/folder", emoji: "🗂️" },
      { name: "Update a schematic revision", emoji: "📟" },
      { name: "Review today's test results", emoji: "📊" },
      { name: "Read up on a component datasheet", emoji: "📄" },
      { name: "Troubleshoot a failed unit", emoji: "🔧" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Chemical Engineer": [
      { name: "Review today's process notes", emoji: "🧪" },
      { name: "Check in on my project timeline", emoji: "⚗️" },
      { name: "Review safety checklist before starting", emoji: "🦺" },
      { name: "Review a process-safety data sheet", emoji: "🧯" },
      { name: "Read up on a trend in my field", emoji: "🔍" },
      { name: "Check today's process readings/logs", emoji: "📊" },
      { name: "Update a batch/process record", emoji: "📝" },
      { name: "Read up on a process-improvement idea", emoji: "📘" },
      { name: "Review a lab-test result", emoji: "🔬" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Industrial Engineer": [
      { name: "Review today's process notes", emoji: "📊" },
      { name: "Check in on my project timeline", emoji: "⏱️" },
      { name: "Review safety checklist before starting", emoji: "🦺" },
      { name: "Time a process for efficiency", emoji: "⏱️" },
      { name: "Message a mentor or peer for advice", emoji: "💬" },
      { name: "Update a workflow/layout diagram", emoji: "🔀" },
      { name: "Review today's productivity metrics", emoji: "📊" },
      { name: "Read up on a lean/process-improvement idea", emoji: "📘" },
      { name: "Walk the floor for a process observation", emoji: "🚶" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Structural Engineer": [
      { name: "Review today's calculations/notes", emoji: "🏗️" },
      { name: "Check in on my project timeline", emoji: "📐" },
      { name: "Review safety checklist before starting", emoji: "🦺" },
      { name: "Review a load-bearing calculation", emoji: "📏" },
      { name: "Review a goal I set this month", emoji: "📈" },
      { name: "Update a structural-drawing revision", emoji: "📐" },
      { name: "Check today's site inspection notes", emoji: "🔍" },
      { name: "Read up on a design-code update", emoji: "📘" },
      { name: "Review a materials-test result", emoji: "🧱" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Environmental Engineer": [
      { name: "Review today's project notes", emoji: "🌎" },
      { name: "Check in on my project timeline", emoji: "♻️" },
      { name: "Log today's monitoring data", emoji: "📊" },
      { name: "Review an emissions report", emoji: "🌫️" },
      { name: "Review an emissions/monitoring report", emoji: "🌫️" },
      { name: "Update today's monitoring log", emoji: "📊" },
      { name: "Read up on an environmental-regulation update", emoji: "📘" },
      { name: "Check a site's compliance checklist", emoji: "✅" },
      { name: "Review a remediation-progress report", emoji: "♻️" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Project Engineer": [
      { name: "Review today's project status", emoji: "📋" },
      { name: "Plan tomorrow's priorities", emoji: "📅" },
      { name: "Review safety checklist before starting", emoji: "🦺" },
      { name: "Review today's project risks", emoji: "⚠️" },
      { name: "Update the project risk register", emoji: "⚠️" },
      { name: "Follow up on a blocked task", emoji: "🚧" },
      { name: "Try a small process improvement today", emoji: "🔁" },
      { name: "Check today's site/deliverable progress", emoji: "🏗️" },
      { name: "Prep tomorrow's coordination notes", emoji: "📅" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Quality Engineer": [
      { name: "Review today's QA checklist", emoji: "✅" },
      { name: "Log a quality-control note", emoji: "📊" },
      { name: "Review safety checklist before starting", emoji: "🦺" },
      { name: "Review a defect-rate trend", emoji: "📊" },
      { name: "Jot down an idea worth revisiting", emoji: "💡" },
      { name: "Update the QA/QC checklist", emoji: "✅" },
      { name: "Investigate a nonconformance report", emoji: "🔍" },
      { name: "Read up on a quality-standard update", emoji: "📘" },
      { name: "Review a supplier's quality report", emoji: "📄" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Manufacturing Engineer": [
      { name: "Review today's production notes", emoji: "🏭" },
      { name: "Check in on my project timeline", emoji: "⚙️" },
      { name: "Review safety checklist before starting", emoji: "🦺" },
      { name: "Review today's production line notes", emoji: "🏭" },
      { name: "Review today's production-line notes", emoji: "🏭" },
      { name: "Time a process for efficiency", emoji: "⏱️" },
      { name: "Check a machine's maintenance log", emoji: "🔧" },
      { name: "Read up on a process-improvement idea", emoji: "📘" },
      { name: "Review today's yield/defect data", emoji: "📊" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Mining Engineer": [
      { name: "Review today's site notes", emoji: "⛏️" },
      { name: "Review safety checklist before starting", emoji: "🦺" },
      { name: "Log today's monitoring data", emoji: "📊" },
      { name: "Review a mine-safety concept", emoji: "⛑️" },
      { name: "Review today's production/output report", emoji: "⛏️" },
      { name: "Check today's mine-safety checklist", emoji: "🦺" },
      { name: "Update a mine-plan revision", emoji: "🗺️" },
      { name: "Read up on a mining-regulation update", emoji: "📘" },
      { name: "Review a ventilation/monitoring report", emoji: "💨" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
  },

  healthcare: {
    "Nurse": [
      { name: "Review today's patient/case notes", emoji: "🩺" },
      { name: "Do a quick self-care reset after my shift", emoji: "💤" },
      { name: "Restock/check my supplies", emoji: "📋" },
      { name: "Double-check today's medication chart", emoji: "💊" },
      { name: "Update a patient's care plan", emoji: "📋" },
      { name: "Review a medication-administration record", emoji: "💊" },
      { name: "Practice hand-off/endorsement notes", emoji: "🗣️" },
      { name: "Read up on a clinical-practice update", emoji: "📘" },
      { name: "Stretch/hydrate during a break", emoji: "💧" },
      { name: "Rest properly before my next duty", emoji: "💤" },
    ],
    "Physician / Doctor": [
      { name: "Review today's patient/case notes", emoji: "🩺" },
      { name: "Read up on a clinical update", emoji: "📚" },
      { name: "Do a quick self-care reset after my shift", emoji: "💤" },
      { name: "Read one medical case study", emoji: "📖" },
      { name: "Update a patient's chart/notes", emoji: "📋" },
      { name: "Review a lab/imaging result", emoji: "🔬" },
      { name: "Read a clinical-guideline update", emoji: "📘" },
      { name: "Follow up on a referral/consult", emoji: "📞" },
      { name: "Prep for tomorrow's patient list", emoji: "📅" },
      { name: "Double-check tomorrow's first task", emoji: "✅" },
    ],
    "Pharmacist": [
      { name: "Review today's dispensing notes", emoji: "💊" },
      { name: "Read up on a drug update", emoji: "📚" },
      { name: "Double-check a sample prescription", emoji: "📋" },
      { name: "Review a drug-interaction chart", emoji: "🧾" },
      { name: "Double-check a dispensed prescription", emoji: "✅" },
      { name: "Review a drug-interaction alert", emoji: "🚨" },
      { name: "Read up on a new-drug update", emoji: "📰" },
      { name: "Restock/check inventory levels", emoji: "📦" },
      { name: "Counsel a patient on medication use", emoji: "🗣️" },
      { name: "Do a quick self-care reset after my shift", emoji: "💤" },
    ],
    "Medical Technologist": [
      { name: "Review today's lab results", emoji: "🔬" },
      { name: "Log a lab observation", emoji: "🧫" },
      { name: "Review a lab procedure", emoji: "📋" },
      { name: "Calibrate/check lab equipment", emoji: "🧪" },
      { name: "Take five minutes to stretch", emoji: "🤸" },
      { name: "Review today's abnormal results", emoji: "🔍" },
      { name: "Log a quality-control check", emoji: "✅" },
      { name: "Restock lab supplies", emoji: "📦" },
      { name: "Read up on a lab-testing update", emoji: "📘" },
      { name: "Do a quick self-care reset after my shift", emoji: "💤" },
    ],
    "Physical Therapist": [
      { name: "Review today's patient/case notes", emoji: "🏃" },
      { name: "Practice a therapy technique", emoji: "🧍" },
      { name: "Do a quick self-care reset after my shift", emoji: "💤" },
      { name: "Learn a new stretching/rehab technique", emoji: "🤸" },
      { name: "Update a patient's progress notes", emoji: "📋" },
      { name: "Review a treatment-plan adjustment", emoji: "🔄" },
      { name: "Review feedback I received recently", emoji: "📝" },
      { name: "Read up on a rehab-practice update", emoji: "📘" },
      { name: "Prep equipment for tomorrow's sessions", emoji: "🧰" },
      { name: "Read about a best practice in my role", emoji: "📘" },
    ],
    "Occupational Therapist": [
      { name: "Review today's patient/case notes", emoji: "✋" },
      { name: "Practice a therapy technique", emoji: "🧍" },
      { name: "Do a quick self-care reset after my shift", emoji: "💤" },
      { name: "Learn a new adaptive-equipment technique", emoji: "🖐️" },
      { name: "Update a patient's progress notes", emoji: "📋" },
      { name: "Prep an adaptive-equipment recommendation", emoji: "🦽" },
      { name: "Learn a new intervention technique", emoji: "🖐️" },
      { name: "Read up on an OT-practice update", emoji: "📘" },
      { name: "Prep materials for tomorrow's sessions", emoji: "🧰" },
      { name: "Update a personal reference/cheat sheet", emoji: "📄" },
    ],
    "Radiologic Technologist": [
      { name: "Review today's imaging notes", emoji: "🩻" },
      { name: "Review an imaging concept", emoji: "🖥️" },
      { name: "Do a quick self-care reset after my shift", emoji: "💤" },
      { name: "Review a radiation-safety protocol", emoji: "☢️" },
      { name: "Review today's imaging quality checks", emoji: "🩻" },
      { name: "Log equipment maintenance/checks", emoji: "🔧" },
      { name: "Recharge with a proper lunch break", emoji: "🍱" },
      { name: "Read up on an imaging-technique update", emoji: "📘" },
      { name: "Restock/check supplies", emoji: "📦" },
      { name: "Reflect on one challenge I handled well", emoji: "🪞" },
    ],
    "Dentist": [
      { name: "Review today's patient/case notes", emoji: "🦷" },
      { name: "Review a sterilization procedure", emoji: "🧼" },
      { name: "Do a quick self-care reset after my shift", emoji: "💤" },
      { name: "Practice a dental-charting exercise", emoji: "🦷" },
      { name: "Update a patient's treatment plan", emoji: "📋" },
      { name: "Review a sterilization log", emoji: "🧼" },
      { name: "Read up on a dental-practice update", emoji: "📘" },
      { name: "Restock/check supplies", emoji: "📦" },
      { name: "Prep for tomorrow's patient list", emoji: "📅" },
      { name: "Reach out to reconnect with a contact", emoji: "📞" },
    ],
    "Caregiver": [
      { name: "Review today's care notes", emoji: "🧑‍⚕️" },
      { name: "Do a quick self-care reset after my shift", emoji: "💤" },
      { name: "Restock/check my supplies", emoji: "📋" },
      { name: "Prep a meal or care routine for tomorrow", emoji: "🧑‍🍳" },
      { name: "Update a client's daily-care log", emoji: "📋" },
      { name: "Prep tomorrow's medication/routine schedule", emoji: "📅" },
      { name: "Restock care/hygiene supplies", emoji: "📦" },
      { name: "Read up on a caregiving tip", emoji: "📘" },
      { name: "Check in with a client's family member", emoji: "📞" },
      { name: "Review my week's priorities", emoji: "🗓️" },
    ],
    "Midwife": [
      { name: "Review today's patient/case notes", emoji: "🤰" },
      { name: "Do a quick self-care reset after my shift", emoji: "💤" },
      { name: "Restock/check my supplies", emoji: "📋" },
      { name: "Review a prenatal-care checklist", emoji: "👶" },
      { name: "Update a patient's prenatal chart", emoji: "📋" },
      { name: "Review a birth-plan checklist", emoji: "📝" },
      { name: "Restock/check delivery supplies", emoji: "📦" },
      { name: "Read up on a maternal-care update", emoji: "📘" },
      { name: "Prep for tomorrow's patient list", emoji: "📅" },
      { name: "Learn one new shortcut/technique", emoji: "🧭" },
    ],
    "Nutritionist-Dietitian": [
      { name: "Review today's patient/case notes", emoji: "🥗" },
      { name: "Plan a sample meal plan", emoji: "🍎" },
      { name: "Read up on a nutrition update", emoji: "📚" },
      { name: "Calculate a sample nutrient breakdown", emoji: "⚖️" },
      { name: "Update a client's meal-plan progress", emoji: "📋" },
      { name: "Prep a nutrition-education handout", emoji: "📄" },
      { name: "Read up on a nutrition-guideline update", emoji: "📘" },
      { name: "Follow up with one client", emoji: "📞" },
      { name: "Review today's consult notes", emoji: "📝" },
      { name: "Do a quick self-care reset after my shift", emoji: "💤" },
    ],
    "Psychologist": [
      { name: "Review today's session notes", emoji: "🧠" },
      { name: "Prep for my next session", emoji: "📝" },
      { name: "Read up on a clinical update", emoji: "📚" },
      { name: "Review a therapy-technique reading", emoji: "📔" },
      { name: "Update today's session notes", emoji: "📋" },
      { name: "Prep materials for my next session", emoji: "📝" },
      { name: "Read up on a therapy-technique update", emoji: "📘" },
      { name: "Reflect on today's sessions", emoji: "🪞" },
      { name: "Follow up on a referral", emoji: "📞" },
      { name: "Do a quick self-care reset after my shift", emoji: "💤" },
    ],
    "Psychiatrist": [
      { name: "Review today's patient/case notes", emoji: "🧠" },
      { name: "Read up on a clinical update", emoji: "📚" },
      { name: "Do a quick self-care reset after my shift", emoji: "💤" },
      { name: "Review a medication-management update", emoji: "💊" },
      { name: "Update a patient's chart/notes", emoji: "📋" },
      { name: "Give a colleague a quick compliment", emoji: "🙌" },
      { name: "Read up on a clinical-practice update", emoji: "📘" },
      { name: "Follow up on a referral/consult", emoji: "📞" },
      { name: "Prep for tomorrow's patient list", emoji: "📅" },
      { name: "Check my inbox one last time before logging off", emoji: "📬" },
    ],
    "Medical Assistant": [
      { name: "Review today's patient/case notes", emoji: "🩺" },
      { name: "Restock/check my supplies", emoji: "📋" },
      { name: "Do a quick self-care reset after my shift", emoji: "💤" },
      { name: "Update a patient intake form", emoji: "📋" },
      { name: "Restock exam-room supplies", emoji: "📦" },
      { name: "Plan a small reward for finishing a task", emoji: "🎁" },
      { name: "Confirm tomorrow's patient appointments", emoji: "📞" },
      { name: "Review a vitals-taking checklist", emoji: "🌡️" },
      { name: "Sanitize/prep equipment for tomorrow", emoji: "🧼" },
      { name: "Note a question to ask my supervisor", emoji: "❓" },
    ],
    "Healthcare Administrator": [
      { name: "Review today's operations notes", emoji: "🏥" },
      { name: "Plan tomorrow's priorities", emoji: "📅" },
      { name: "Read up on a healthcare policy", emoji: "📚" },
      { name: "Review a facility performance metric", emoji: "📊" },
      { name: "Review a facility-performance metric", emoji: "📊" },
      { name: "Follow up on a compliance item", emoji: "📌" },
      { name: "Read up on a healthcare-policy update", emoji: "📘" },
      { name: "Check in with one department head", emoji: "🧑‍🤝‍🧑" },
      { name: "Review this month's budget snapshot", emoji: "💰" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
  },

  education: {
    "Teacher (Elementary)": [
      { name: "Review tomorrow's lesson plan", emoji: "🍎" },
      { name: "Grade or review student work", emoji: "📝" },
      { name: "Reflect on today's class", emoji: "🧑‍🏫" },
      { name: "Design a fun classroom activity", emoji: "🎨" },
      { name: "Update tomorrow's visual aids", emoji: "🖼️" },
      { name: "Review a struggling student's progress", emoji: "📊" },
      { name: "Prep a parent-communication note", emoji: "✉️" },
      { name: "Read up on a classroom strategy", emoji: "📘" },
      { name: "Organize classroom materials", emoji: "🗂️" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Teacher (Secondary)": [
      { name: "Review tomorrow's lesson plan", emoji: "🍎" },
      { name: "Grade or review student work", emoji: "📝" },
      { name: "Reflect on today's class", emoji: "🧑‍🏫" },
      { name: "Design an assessment/quiz", emoji: "📊" },
      { name: "Update a lesson's discussion questions", emoji: "❓" },
      { name: "Review a struggling student's progress", emoji: "📊" },
      { name: "Prep a parent-communication note", emoji: "✉️" },
      { name: "Read up on a teaching strategy", emoji: "📘" },
      { name: "Organize a unit's teaching materials", emoji: "🗂️" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Professor / College Instructor": [
      { name: "Review tomorrow's lesson plan", emoji: "📚" },
      { name: "Grade or review student work", emoji: "📝" },
      { name: "Reflect on today's class", emoji: "🧑‍🏫" },
      { name: "Read up on my subject's latest research", emoji: "📖" },
      { name: "Update a syllabus/course material", emoji: "📄" },
      { name: "Review a student's research draft", emoji: "📊" },
      { name: "Read one paper in my field", emoji: "📖" },
      { name: "Prep office-hours notes", emoji: "📝" },
      { name: "Draft feedback on submitted work", emoji: "✍️" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Tutor": [
      { name: "Review tomorrow's session plan", emoji: "📖" },
      { name: "Reflect on today's session", emoji: "📝" },
      { name: "Prep materials for my next session", emoji: "🧑‍🏫" },
      { name: "Design a practice exercise for my student", emoji: "🧩" },
      { name: "Review a student's recent mistakes", emoji: "🔍" },
      { name: "Prep a practice worksheet", emoji: "📄" },
      { name: "Track a student's progress over time", emoji: "📈" },
      { name: "Read up on a teaching technique", emoji: "📘" },
      { name: "Follow up with a parent/guardian", emoji: "📞" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "School Administrator": [
      { name: "Review today's operations notes", emoji: "🏫" },
      { name: "Plan tomorrow's priorities", emoji: "📅" },
      { name: "Read up on a school-policy update", emoji: "📚" },
      { name: "Review a school-performance metric", emoji: "📊" },
      { name: "Read an industry newsletter/update", emoji: "📰" },
      { name: "Follow up on a facilities issue", emoji: "🏫" },
      { name: "Tidy my digital workspace/files", emoji: "🖥️" },
      { name: "Check in with one department head", emoji: "🧑‍🤝‍🧑" },
      { name: "Review this month's budget snapshot", emoji: "💰" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Guidance Counselor": [
      { name: "Review today's case notes", emoji: "🧑‍🤝‍🧑" },
      { name: "Prep for my next session", emoji: "📝" },
      { name: "Read up on a counseling technique", emoji: "📚" },
      { name: "Research a student-resource option", emoji: "🧭" },
      { name: "Update a student's case notes", emoji: "📋" },
      { name: "Prep for tomorrow's session", emoji: "📝" },
      { name: "Set one clear goal for tomorrow", emoji: "🎯" },
      { name: "Check in with a colleague", emoji: "🧑‍🤝‍🧑" },
      { name: "Follow up with a teacher/parent", emoji: "📞" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Corporate Trainer": [
      { name: "Review tomorrow's session plan", emoji: "🎤" },
      { name: "Reflect on today's session", emoji: "📝" },
      { name: "Read up on a training technique", emoji: "📚" },
      { name: "Design a training-module outline", emoji: "🎯" },
      { name: "Update a training-module slide", emoji: "📑" },
      { name: "Review today's training feedback", emoji: "📊" },
      { name: "Read up on an adult-learning technique", emoji: "📘" },
      { name: "Prep an activity for the next session", emoji: "🎯" },
      { name: "Follow up with a trainee", emoji: "📞" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Curriculum Developer": [
      { name: "Review today's curriculum notes", emoji: "📋" },
      { name: "Draft or revise a lesson unit", emoji: "📝" },
      { name: "Read up on a teaching strategy", emoji: "📚" },
      { name: "Review learning-outcome data", emoji: "📈" },
      { name: "Review my calendar for conflicts", emoji: "📅" },
      { name: "Note one lesson learned today", emoji: "🪞" },
      { name: "Silence notifications for a focused hour", emoji: "🔕" },
      { name: "Get feedback from a teacher on a draft", emoji: "🗣️" },
      { name: "Align a unit to standards/competencies", emoji: "🎯" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Special Education Teacher": [
      { name: "Review tomorrow's lesson plan", emoji: "💛" },
      { name: "Grade or review student work", emoji: "📝" },
      { name: "Reflect on today's class", emoji: "🧑‍🏫" },
      { name: "Research an inclusive-teaching strategy", emoji: "🧩" },
      { name: "Update a student's IEP progress notes", emoji: "📋" },
      { name: "Prep a differentiated activity", emoji: "🧩" },
      { name: "Research an assistive-technology option", emoji: "🦽" },
      { name: "Read up on an inclusive-teaching strategy", emoji: "📘" },
      { name: "Follow up with a parent/guardian", emoji: "📞" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
  },

  "sales-marketing": {
    "Sales Representative": [
      { name: "Follow up with one lead or client", emoji: "📞" },
      { name: "Review today's numbers/targets", emoji: "📊" },
      { name: "Plan tomorrow's outreach", emoji: "📅" },
      { name: "Update my CRM/contact notes", emoji: "📋" },
      { name: "Prep for tomorrow's client call", emoji: "📞" },
      { name: "Review my pipeline for stalled deals", emoji: "📊" },
      { name: "Send a follow-up thank-you note", emoji: "✉️" },
      { name: "Study a new product detail", emoji: "📘" },
      { name: "Practice handling a common objection", emoji: "🗣️" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Sales Manager": [
      { name: "Review today's team numbers/targets", emoji: "📊" },
      { name: "Follow up with one lead or client", emoji: "📞" },
      { name: "Plan tomorrow's outreach", emoji: "📅" },
      { name: "Recognize one team win today", emoji: "🏆" },
      { name: "Review this week's team pipeline", emoji: "📊" },
      { name: "Check in with an underperforming rep", emoji: "🧑‍🤝‍🧑" },
      { name: "Update the sales forecast", emoji: "📈" },
      { name: "Prep a coaching note for the team", emoji: "📝" },
      { name: "Update my skills/learning list", emoji: "📚" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Account Executive": [
      { name: "Follow up with one lead or client", emoji: "📞" },
      { name: "Review today's numbers/targets", emoji: "📊" },
      { name: "Plan tomorrow's outreach", emoji: "📅" },
      { name: "Update my CRM/contact notes", emoji: "📋" },
      { name: "Prep for tomorrow's client call", emoji: "📞" },
      { name: "Review my pipeline for stalled deals", emoji: "📊" },
      { name: "Send a follow-up thank-you note", emoji: "✉️" },
      { name: "Study a new product detail", emoji: "📘" },
      { name: "Take a short walk to reset", emoji: "🚶" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Marketing Specialist": [
      { name: "Review today's campaign notes", emoji: "📣" },
      { name: "Read up on a market trend", emoji: "📚" },
      { name: "Draft or edit a piece of content", emoji: "📝" },
      { name: "Define a target-audience profile", emoji: "🎯" },
      { name: "Update a campaign's performance report", emoji: "📊" },
      { name: "Draft or refine a piece of copy", emoji: "📝" },
      { name: "Read up on a marketing trend", emoji: "📘" },
      { name: "Review a competitor's latest campaign", emoji: "🔍" },
      { name: "Brainstorm a new campaign angle", emoji: "💡" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Digital Marketing Specialist": [
      { name: "Review today's campaign metrics", emoji: "📊" },
      { name: "Read up on a market trend", emoji: "📚" },
      { name: "Draft or edit a piece of content", emoji: "📝" },
      { name: "Review today's ad-spend performance", emoji: "💰" },
      { name: "Write down three things that went well", emoji: "✍️" },
      { name: "A/B test a new ad variant", emoji: "🧪" },
      { name: "Update a campaign's targeting", emoji: "🎯" },
      { name: "Read up on a platform-algorithm update", emoji: "📘" },
      { name: "Review this week's conversion data", emoji: "📊" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "SEO Specialist": [
      { name: "Review today's keyword/ranking data", emoji: "🔍" },
      { name: "Read up on an SEO update", emoji: "📚" },
      { name: "Draft or edit a piece of content", emoji: "📝" },
      { name: "Check a backlink/ranking report", emoji: "🔗" },
      { name: "Declutter one shelf/drawer/folder", emoji: "🗂️" },
      { name: "Update a page's on-page SEO", emoji: "🔍" },
      { name: "Research a new keyword opportunity", emoji: "🔑" },
      { name: "Read up on an SEO-algorithm update", emoji: "📘" },
      { name: "Audit a page's load speed", emoji: "⚡" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Social Media Manager": [
      { name: "Draft or schedule a post", emoji: "📱" },
      { name: "Review today's engagement metrics", emoji: "📊" },
      { name: "Read up on a platform update", emoji: "📚" },
      { name: "Plan tomorrow's content calendar", emoji: "📸" },
      { name: "Read up on a trend in my field", emoji: "🔍" },
      { name: "Message a mentor or peer for advice", emoji: "💬" },
      { name: "Respond to audience comments/DMs", emoji: "💬" },
      { name: "Review a goal I set this month", emoji: "📈" },
      { name: "Draft a caption/post idea", emoji: "✍️" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Brand Manager": [
      { name: "Review today's brand notes", emoji: "📣" },
      { name: "Read up on a market trend", emoji: "📚" },
      { name: "Draft a brand guideline note", emoji: "📝" },
      { name: "Review brand visual consistency", emoji: "🖼️" },
      { name: "Try a small process improvement today", emoji: "🔁" },
      { name: "Update the brand guideline note", emoji: "📝" },
      { name: "Read up on a market/consumer trend", emoji: "📘" },
      { name: "Review a campaign's brand alignment", emoji: "🔍" },
      { name: "Check in with a creative partner/agency", emoji: "🧑‍🤝‍🧑" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Market Researcher": [
      { name: "Review today's research data", emoji: "📊" },
      { name: "Read up on a market trend", emoji: "📚" },
      { name: "Draft a research summary", emoji: "📝" },
      { name: "Design a survey question", emoji: "📋" },
      { name: "Clean a survey/research dataset", emoji: "🧹" },
      { name: "Jot down an idea worth revisiting", emoji: "💡" },
      { name: "Double-check tomorrow's first task", emoji: "✅" },
      { name: "Read up on a research methodology", emoji: "📘" },
      { name: "Review today's respondent data", emoji: "📊" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Business Development Officer": [
      { name: "Follow up with one lead or client", emoji: "📞" },
      { name: "Review today's pipeline", emoji: "📊" },
      { name: "Plan tomorrow's outreach", emoji: "📅" },
      { name: "Research a potential partnership", emoji: "🤝" },
      { name: "Take five minutes to stretch", emoji: "🤸" },
      { name: "Prep for tomorrow's pitch/meeting", emoji: "📅" },
      { name: "Follow up on a proposal sent out", emoji: "📞" },
      { name: "Review this week's pipeline", emoji: "📊" },
      { name: "Update my outreach tracker", emoji: "📋" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Retail Sales Associate": [
      { name: "Review today's sales notes", emoji: "🛍️" },
      { name: "Practice a customer-service scenario", emoji: "🧑‍🤝‍🧑" },
      { name: "Plan tomorrow's shift priorities", emoji: "📅" },
      { name: "Learn one product detail deeply", emoji: "🛍️" },
      { name: "Check tomorrow's shift schedule", emoji: "📅" },
      { name: "Review today's sales/target numbers", emoji: "📊" },
      { name: "Learn one new product detail", emoji: "🛍️" },
      { name: "Tidy/restock my section", emoji: "🧹" },
      { name: "Review feedback I received recently", emoji: "📝" },
      { name: "Rest properly between shifts", emoji: "🦶" },
    ],
  },

  creative: {
    "Graphic Designer": [
      { name: "Work on a personal or client project", emoji: "🎨" },
      { name: "Update my portfolio", emoji: "🖌️" },
      { name: "Get feedback on a work-in-progress", emoji: "📝" },
      { name: "Explore a new design trend", emoji: "🎨" },
      { name: "Read about a best practice in my role", emoji: "📘" },
      { name: "Organize my design asset library", emoji: "🗂️" },
      { name: "Update a personal reference/cheat sheet", emoji: "📄" },
      { name: "Update my portfolio with recent work", emoji: "🖼️" },
      { name: "Practice a new tool/plugin technique", emoji: "🧭" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Illustrator": [
      { name: "Work on a personal or client project", emoji: "🖊️" },
      { name: "Update my portfolio", emoji: "🖌️" },
      { name: "Get feedback on a work-in-progress", emoji: "📝" },
      { name: "Practice a new drawing technique", emoji: "🖊️" },
      { name: "Recharge with a proper lunch break", emoji: "🍱" },
      { name: "Organize my reference/asset library", emoji: "🗂️" },
      { name: "Reflect on one challenge I handled well", emoji: "🪞" },
      { name: "Update my portfolio with recent work", emoji: "🖼️" },
      { name: "Study another illustrator's style", emoji: "🔍" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Animator": [
      { name: "Work on a personal or client project", emoji: "🎬" },
      { name: "Update my portfolio", emoji: "🖌️" },
      { name: "Get feedback on a work-in-progress", emoji: "📝" },
      { name: "Study a reference for movement/timing", emoji: "🕺" },
      { name: "Polish a scene's timing/spacing", emoji: "⏱️" },
      { name: "Organize my project's asset files", emoji: "🗂️" },
      { name: "Reach out to reconnect with a contact", emoji: "📞" },
      { name: "Update my reel with recent work", emoji: "🎬" },
      { name: "Review my week's priorities", emoji: "🗓️" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Photographer": [
      { name: "Work on a personal or client project", emoji: "📷" },
      { name: "Update my portfolio", emoji: "🖼️" },
      { name: "Get feedback on a work-in-progress", emoji: "📝" },
      { name: "Practice a new lighting technique", emoji: "📷" },
      { name: "Cull and edit today's shoot", emoji: "🖼️" },
      { name: "Back up today's photo files", emoji: "💾" },
      { name: "Learn one new shortcut/technique", emoji: "🧭" },
      { name: "Update my portfolio with recent work", emoji: "🖼️" },
      { name: "Give a colleague a quick compliment", emoji: "🙌" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Videographer / Video Editor": [
      { name: "Work on a personal or client project", emoji: "🎥" },
      { name: "Update my portfolio", emoji: "✂️" },
      { name: "Get feedback on a work-in-progress", emoji: "📝" },
      { name: "Study a film's cinematography", emoji: "🎞️" },
      { name: "Log/organize today's footage", emoji: "🗂️" },
      { name: "Color-grade a clip for practice", emoji: "🎨" },
      { name: "Check my inbox one last time before logging off", emoji: "📬" },
      { name: "Update my reel with recent work", emoji: "🎬" },
      { name: "Back up today's project files", emoji: "💾" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Interior Designer": [
      { name: "Work on a personal or client project", emoji: "🛋️" },
      { name: "Update my portfolio", emoji: "🖌️" },
      { name: "Get feedback on a work-in-progress", emoji: "📝" },
      { name: "Explore a furniture/material catalog", emoji: "🪑" },
      { name: "Update a client mood board", emoji: "🖼️" },
      { name: "Research a new material/vendor", emoji: "🧵" },
      { name: "Plan a small reward for finishing a task", emoji: "🎁" },
      { name: "Update my portfolio with recent work", emoji: "🖼️" },
      { name: "Follow up with a client/vendor", emoji: "📞" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Fashion Designer": [
      { name: "Work on a personal or client project", emoji: "👗" },
      { name: "Update my portfolio", emoji: "🧵" },
      { name: "Get feedback on a work-in-progress", emoji: "📝" },
      { name: "Explore a fabric/material swatch", emoji: "🧵" },
      { name: "Sketch a new design iteration", emoji: "✏️" },
      { name: "Research a fabric/material option", emoji: "🧵" },
      { name: "Note a question to ask my supervisor", emoji: "❓" },
      { name: "Update my portfolio with recent work", emoji: "🖼️" },
      { name: "Follow up with a supplier/vendor", emoji: "📞" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Product Designer": [
      { name: "Work on a personal or client project", emoji: "📦" },
      { name: "Update my portfolio", emoji: "🖌️" },
      { name: "Get feedback on a work-in-progress", emoji: "📝" },
      { name: "Study a product-design reference", emoji: "⚙️" },
      { name: "Update a prototype based on feedback", emoji: "🛠️" },
      { name: "Research a materials/manufacturing option", emoji: "⚙️" },
      { name: "Read an industry newsletter/update", emoji: "📰" },
      { name: "Update my portfolio with recent work", emoji: "🖼️" },
      { name: "Review a user-testing note", emoji: "🔍" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Art Director": [
      { name: "Review today's creative direction notes", emoji: "🎨" },
      { name: "Get feedback on a work-in-progress", emoji: "📝" },
      { name: "Research a creative reference", emoji: "📚" },
      { name: "Curate a mood board", emoji: "🖼️" },
      { name: "Curate a mood board for a new project", emoji: "🖼️" },
      { name: "Give feedback on a team member's work", emoji: "📝" },
      { name: "Tidy my digital workspace/files", emoji: "🖥️" },
      { name: "Review a project's visual consistency", emoji: "🎨" },
      { name: "Prep notes for a client presentation", emoji: "📅" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Musician": [
      { name: "Practice my instrument/voice for 20 min", emoji: "🎵" },
      { name: "Rehearse a piece", emoji: "🎼" },
      { name: "Get feedback on a work-in-progress", emoji: "📝" },
      { name: "Analyze a piece by ear", emoji: "🎧" },
      { name: "Record a practice take and review it", emoji: "🎙️" },
      { name: "Learn a new piece/song", emoji: "🎼" },
      { name: "Set one clear goal for tomorrow", emoji: "🎯" },
      { name: "Practice with my band/ensemble", emoji: "🎵" },
      { name: "Update my setlist/repertoire", emoji: "📋" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Writer / Author": [
      { name: "Write for 20 minutes", emoji: "✍️" },
      { name: "Revise a piece I'm working on", emoji: "📝" },
      { name: "Read something in my genre", emoji: "📖" },
      { name: "Explore a new author or work", emoji: "📚" },
      { name: "Outline my next chapter/section", emoji: "🗺️" },
      { name: "Edit yesterday's pages", emoji: "✏️" },
      { name: "Get feedback on a work-in-progress", emoji: "📝" },
      { name: "Research a topic for my project", emoji: "🔍" },
      { name: "Check in with a colleague", emoji: "🧑‍🤝‍🧑" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
  },

  media: {
    "Journalist / Reporter": [
      { name: "Draft or edit a piece", emoji: "📰" },
      { name: "Research a story lead", emoji: "🔍" },
      { name: "Review today's feedback or edits", emoji: "📝" },
      { name: "Practice a photojournalism shot", emoji: "📸" },
      { name: "Fact-check today's draft", emoji: "✅" },
      { name: "Follow up with a source", emoji: "📞" },
      { name: "Pitch tomorrow's story idea", emoji: "💡" },
      { name: "Read two outlets on the same story", emoji: "📰" },
      { name: "Review my calendar for conflicts", emoji: "📅" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Content Writer": [
      { name: "Draft or edit a piece", emoji: "📝" },
      { name: "Read one piece from my field", emoji: "📚" },
      { name: "Research a topic idea", emoji: "🔍" },
      { name: "Study a high-performing article", emoji: "📊" },
      { name: "Outline tomorrow's piece", emoji: "🗺️" },
      { name: "Edit yesterday's draft", emoji: "✏️" },
      { name: "Note one lesson learned today", emoji: "🪞" },
      { name: "Silence notifications for a focused hour", emoji: "🔕" },
      { name: "Update my skills/learning list", emoji: "📚" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Copywriter": [
      { name: "Draft or edit a piece", emoji: "📝" },
      { name: "Brainstorm a headline/angle", emoji: "💡" },
      { name: "Read one piece from my field", emoji: "📚" },
      { name: "Design a mock ad visual", emoji: "🖼️" },
      { name: "Take a short walk to reset", emoji: "🚶" },
      { name: "Edit yesterday's draft", emoji: "✏️" },
      { name: "Study a high-performing ad/copy piece", emoji: "📊" },
      { name: "Write down three things that went well", emoji: "✍️" },
      { name: "Get feedback on a work-in-progress", emoji: "📝" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Editor": [
      { name: "Review today's feedback or edits", emoji: "📝" },
      { name: "Read one piece from my field", emoji: "📖" },
      { name: "Edit a sample piece", emoji: "✏️" },
      { name: "Review a style-guide rule", emoji: "📏" },
      { name: "Edit a sample/submitted piece", emoji: "✏️" },
      { name: "Declutter one shelf/drawer/folder", emoji: "🗂️" },
      { name: "Follow up with a writer on revisions", emoji: "📞" },
      { name: "Read up on a trend in my field", emoji: "🔍" },
      { name: "Update the editorial calendar", emoji: "🗓️" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Broadcaster / News Anchor": [
      { name: "Practice a broadcast script", emoji: "🎙️" },
      { name: "Review today's recording/feedback", emoji: "🎧" },
      { name: "Read up on today's news", emoji: "📚" },
      { name: "Listen to/analyze a broadcast segment", emoji: "📻" },
      { name: "Message a mentor or peer for advice", emoji: "💬" },
      { name: "Review a goal I set this month", emoji: "📈" },
      { name: "Try a small process improvement today", emoji: "🔁" },
      { name: "Do a vocal warm-up before airtime", emoji: "🔥" },
      { name: "Prep tomorrow's segment notes", emoji: "📝" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Public Relations Officer": [
      { name: "Draft a press release/pitch", emoji: "📝" },
      { name: "Read one PR case study", emoji: "📰" },
      { name: "Follow up with one media contact", emoji: "📞" },
      { name: "Track a brand in the news", emoji: "📰" },
      { name: "Jot down an idea worth revisiting", emoji: "💡" },
      { name: "Double-check tomorrow's first task", emoji: "✅" },
      { name: "Take five minutes to stretch", emoji: "🤸" },
      { name: "Review feedback I received recently", emoji: "📝" },
      { name: "Prep talking points for a spokesperson", emoji: "🗣️" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Communications Specialist": [
      { name: "Draft or edit a piece", emoji: "📝" },
      { name: "Read one piece from my field", emoji: "📚" },
      { name: "Review today's messaging notes", emoji: "📊" },
      { name: "Draft an internal announcement", emoji: "📣" },
      { name: "Read about a best practice in my role", emoji: "📘" },
      { name: "Update a personal reference/cheat sheet", emoji: "📄" },
      { name: "Recharge with a proper lunch break", emoji: "🍱" },
      { name: "Update a communications calendar", emoji: "🗓️" },
      { name: "Get feedback on a draft", emoji: "📝" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Social Media Content Creator": [
      { name: "Draft or schedule a post", emoji: "📱" },
      { name: "Review today's engagement metrics", emoji: "📊" },
      { name: "Brainstorm a content idea", emoji: "💡" },
      { name: "Plan tomorrow's content calendar", emoji: "📸" },
      { name: "Reflect on one challenge I handled well", emoji: "🪞" },
      { name: "Reach out to reconnect with a contact", emoji: "📞" },
      { name: "Review my week's priorities", emoji: "🗓️" },
      { name: "Film or edit a short clip", emoji: "🎬" },
      { name: "Respond to audience comments/DMs", emoji: "💬" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Podcaster / Streamer": [
      { name: "Plan or record an episode", emoji: "🎙️" },
      { name: "Review today's feedback", emoji: "🎧" },
      { name: "Brainstorm a content idea", emoji: "💡" },
      { name: "Study a top-performing episode", emoji: "🎧" },
      { name: "Learn one new shortcut/technique", emoji: "🧭" },
      { name: "Edit today's recording", emoji: "✂️" },
      { name: "Give a colleague a quick compliment", emoji: "🙌" },
      { name: "Check my inbox one last time before logging off", emoji: "📬" },
      { name: "Plan a small reward for finishing a task", emoji: "🎁" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
  },

  retail: {
    "Customer Service Representative": [
      { name: "Do a mental reset between shifts", emoji: "🎧" },
      { name: "Review today's feedback or numbers", emoji: "📊" },
      { name: "Practice a tricky customer scenario", emoji: "🧑‍🤝‍🧑" },
      { name: "Learn one product/policy detail", emoji: "📚" },
      { name: "Review today's feedback/survey scores", emoji: "📊" },
      { name: "Practice a de-escalation scenario", emoji: "🕊️" },
      { name: "Note a question to ask my supervisor", emoji: "❓" },
      { name: "Update my FAQ/notes reference", emoji: "📄" },
      { name: "Check tomorrow's shift schedule", emoji: "📅" },
      { name: "Read an industry newsletter/update", emoji: "📰" },
    ],
    "Call Center Agent": [
      { name: "Do a vocal warm-up before my shift", emoji: "🎧" },
      { name: "Review today's call metrics", emoji: "📊" },
      { name: "Take my break away from my screen", emoji: "💆" },
      { name: "Review a common-issue script", emoji: "📋" },
      { name: "Tidy my digital workspace/files", emoji: "🖥️" },
      { name: "Set one clear goal for tomorrow", emoji: "🎯" },
      { name: "Practice a tricky-call scenario", emoji: "🧑‍🤝‍🧑" },
      { name: "Check in with a colleague", emoji: "🧑‍🤝‍🧑" },
      { name: "Check tomorrow's shift schedule", emoji: "📅" },
      { name: "Review my calendar for conflicts", emoji: "📅" },
    ],
    "Store Manager": [
      { name: "Review today's sales/ops notes", emoji: "🏬" },
      { name: "Plan tomorrow's priorities", emoji: "📅" },
      { name: "Check in with my team", emoji: "🧑‍🤝‍🧑" },
      { name: "Check store inventory levels", emoji: "📦" },
      { name: "Note one lesson learned today", emoji: "🪞" },
      { name: "Review today's sales numbers", emoji: "📊" },
      { name: "Silence notifications for a focused hour", emoji: "🔕" },
      { name: "Plan tomorrow's floor priorities", emoji: "📅" },
      { name: "Walk the floor for a merchandising check", emoji: "🚶" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Cashier": [
      { name: "Review today's till/transaction notes", emoji: "💵" },
      { name: "Practice a customer-service scenario", emoji: "🧑‍🤝‍🧑" },
      { name: "Rest properly between shifts", emoji: "🦶" },
      { name: "Practice a transaction-error scenario", emoji: "🧾" },
      { name: "Update my skills/learning list", emoji: "📚" },
      { name: "Take a short walk to reset", emoji: "🚶" },
      { name: "Check tomorrow's shift schedule", emoji: "📅" },
      { name: "Learn a new promo/discount detail", emoji: "🏷️" },
      { name: "Write down three things that went well", emoji: "✍️" },
      { name: "Declutter one shelf/drawer/folder", emoji: "🗂️" },
    ],
    "Merchandiser": [
      { name: "Review today's stock/display notes", emoji: "🛍️" },
      { name: "Log today's inventory check", emoji: "📊" },
      { name: "Plan tomorrow's tasks", emoji: "📅" },
      { name: "Study a display/planogram layout", emoji: "🖼️" },
      { name: "Read up on a trend in my field", emoji: "🔍" },
      { name: "Message a mentor or peer for advice", emoji: "💬" },
      { name: "Plan tomorrow's restocking tasks", emoji: "📅" },
      { name: "Review a product's sales performance", emoji: "📈" },
      { name: "Check expiry/rotation on shelf stock", emoji: "🔄" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Barista": [
      { name: "Review today's shift notes", emoji: "☕" },
      { name: "Practice a customer-service scenario", emoji: "🧑‍🤝‍🧑" },
      { name: "Rest properly between shifts", emoji: "🦶" },
      { name: "Practice a new drink recipe", emoji: "☕" },
      { name: "Review a goal I set this month", emoji: "📈" },
      { name: "Try a small process improvement today", emoji: "🔁" },
      { name: "Restock/check supplies", emoji: "📦" },
      { name: "Clean and maintain the espresso machine", emoji: "🧼" },
      { name: "Jot down an idea worth revisiting", emoji: "💡" },
      { name: "Double-check tomorrow's first task", emoji: "✅" },
    ],
    "Sales Clerk": [
      { name: "Review today's sales notes", emoji: "🛍️" },
      { name: "Practice a customer-service scenario", emoji: "🧑‍🤝‍🧑" },
      { name: "Rest properly between shifts", emoji: "🦶" },
      { name: "Learn one product detail deeply", emoji: "🛍️" },
      { name: "Take five minutes to stretch", emoji: "🤸" },
      { name: "Review feedback I received recently", emoji: "📝" },
      { name: "Check tomorrow's shift schedule", emoji: "📅" },
      { name: "Tidy/restock my section", emoji: "🧹" },
      { name: "Read about a best practice in my role", emoji: "📘" },
      { name: "Update a personal reference/cheat sheet", emoji: "📄" },
    ],
  },

  hospitality: {
    "Hotel Front Desk Officer": [
      { name: "Review today's guest notes", emoji: "🛎️" },
      { name: "Practice a guest-service scenario", emoji: "🧑‍🤝‍🧑" },
      { name: "Check my schedule for the week", emoji: "📅" },
      { name: "Study a hotel's service standard", emoji: "🏨" },
      { name: "Review tomorrow's arrivals/departures", emoji: "📋" },
      { name: "Recharge with a proper lunch break", emoji: "🍱" },
      { name: "Reflect on one challenge I handled well", emoji: "🪞" },
      { name: "Reach out to reconnect with a contact", emoji: "📞" },
      { name: "Log today's guest feedback", emoji: "📝" },
      { name: "Rest properly between shifts", emoji: "💤" },
    ],
    "Flight Attendant": [
      { name: "Prep for my next flight/shift", emoji: "✈️" },
      { name: "Rest properly between shifts", emoji: "💤" },
      { name: "Review today's safety briefing notes", emoji: "📋" },
      { name: "Study an airline safety procedure", emoji: "🛫" },
      { name: "Review my week's priorities", emoji: "🗓️" },
      { name: "Learn one new shortcut/technique", emoji: "🧭" },
      { name: "Give a colleague a quick compliment", emoji: "🙌" },
      { name: "Check my uniform/kit for tomorrow", emoji: "🧳" },
      { name: "Check my inbox one last time before logging off", emoji: "📬" },
      { name: "Hydrate and stretch after a flight", emoji: "💧" },
    ],
    "Chef / Cook": [
      { name: "Prep for my next shift", emoji: "🍳" },
      { name: "Review today's kitchen notes", emoji: "🔪" },
      { name: "Reset after a busy shift", emoji: "💤" },
      { name: "Plate a dish for presentation practice", emoji: "🍽️" },
      { name: "Plan a small reward for finishing a task", emoji: "🎁" },
      { name: "Note a question to ask my supervisor", emoji: "❓" },
      { name: "Check tomorrow's mise en place", emoji: "🥕" },
      { name: "Taste-test and refine a seasoning", emoji: "👅" },
      { name: "Read an industry newsletter/update", emoji: "📰" },
      { name: "Tidy my digital workspace/files", emoji: "🖥️" },
    ],
    "Restaurant Manager": [
      { name: "Review today's ops/sales notes", emoji: "🍽️" },
      { name: "Plan tomorrow's priorities", emoji: "📅" },
      { name: "Check in with my team", emoji: "🧑‍🤝‍🧑" },
      { name: "Review tonight's reservation list", emoji: "📋" },
      { name: "Set one clear goal for tomorrow", emoji: "🎯" },
      { name: "Check in with a colleague", emoji: "🧑‍🤝‍🧑" },
      { name: "Review today's sales/ops notes", emoji: "🍽️" },
      { name: "Check inventory/supplier orders", emoji: "📦" },
      { name: "Review my calendar for conflicts", emoji: "📅" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Waiter / Waitress": [
      { name: "Prep for my next shift", emoji: "🍽️" },
      { name: "Practice a guest-service scenario", emoji: "🧑‍🤝‍🧑" },
      { name: "Reset after a busy shift", emoji: "💤" },
      { name: "Learn a menu/wine pairing detail", emoji: "🍷" },
      { name: "Note one lesson learned today", emoji: "🪞" },
      { name: "Silence notifications for a focused hour", emoji: "🔕" },
      { name: "Update my skills/learning list", emoji: "📚" },
      { name: "Review tonight's specials", emoji: "📋" },
      { name: "Rest properly between shifts", emoji: "🦶" },
      { name: "Take a short walk to reset", emoji: "🚶" },
    ],
    "Tour Guide": [
      { name: "Research a destination or venue", emoji: "🗺️" },
      { name: "Review tomorrow's itinerary", emoji: "📋" },
      { name: "Practice a guest-service scenario", emoji: "🧑‍🤝‍🧑" },
      { name: "Learn a new fact about my route", emoji: "📖" },
      { name: "Write down three things that went well", emoji: "✍️" },
      { name: "Declutter one shelf/drawer/folder", emoji: "🗂️" },
      { name: "Read up on a trend in my field", emoji: "🔍" },
      { name: "Check tomorrow's weather/logistics", emoji: "🌦️" },
      { name: "Message a mentor or peer for advice", emoji: "💬" },
      { name: "Rest properly between shifts", emoji: "💤" },
    ],
    "Travel Agent": [
      { name: "Follow up with one client/lead", emoji: "✈️" },
      { name: "Research a destination or venue", emoji: "🗺️" },
      { name: "Review today's bookings", emoji: "📋" },
      { name: "Study a cruise itinerary/route", emoji: "🚢" },
      { name: "Review a goal I set this month", emoji: "📈" },
      { name: "Try a small process improvement today", emoji: "🔁" },
      { name: "Jot down an idea worth revisiting", emoji: "💡" },
      { name: "Double-check tomorrow's first task", emoji: "✅" },
      { name: "Check a visa/travel-requirement update", emoji: "🛂" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Event Planner": [
      { name: "Review today's event checklist", emoji: "📋" },
      { name: "Plan tomorrow's priorities", emoji: "📅" },
      { name: "Follow up with one client/vendor", emoji: "📞" },
      { name: "Confirm a vendor booking", emoji: "📋" },
      { name: "Take five minutes to stretch", emoji: "🤸" },
      { name: "Review feedback I received recently", emoji: "📝" },
      { name: "Read about a best practice in my role", emoji: "📘" },
      { name: "Update the event timeline", emoji: "🗓️" },
      { name: "Update a personal reference/cheat sheet", emoji: "📄" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Housekeeping Staff": [
      { name: "Review today's room/task checklist", emoji: "🧹" },
      { name: "Reset after a busy shift", emoji: "💤" },
      { name: "Check my schedule for the week", emoji: "📅" },
      { name: "Check supply/amenity stock", emoji: "🧴" },
      { name: "Recharge with a proper lunch break", emoji: "🍱" },
      { name: "Reflect on one challenge I handled well", emoji: "🪞" },
      { name: "Reach out to reconnect with a contact", emoji: "📞" },
      { name: "Log a maintenance issue found", emoji: "🛠️" },
      { name: "Rest properly between shifts", emoji: "🦶" },
      { name: "Review my week's priorities", emoji: "🗓️" },
    ],
  },

  trades: {
    "Construction Worker": [
      { name: "Check my tools/equipment before starting", emoji: "🦺" },
      { name: "Review today's job site notes", emoji: "📋" },
      { name: "Do a quick safety check", emoji: "✅" },
      { name: "Review today's blueprint/plan", emoji: "📐" },
      { name: "Learn one new shortcut/technique", emoji: "🧭" },
      { name: "Give a colleague a quick compliment", emoji: "🙌" },
      { name: "Check my inbox one last time before logging off", emoji: "📬" },
      { name: "Plan a small reward for finishing a task", emoji: "🎁" },
      { name: "Check tomorrow's task assignment", emoji: "📅" },
      { name: "Rest properly between shifts", emoji: "💤" },
    ],
    "Electrician": [
      { name: "Check my tools/equipment before starting", emoji: "🔌" },
      { name: "Review today's job site notes", emoji: "📋" },
      { name: "Do a quick safety check", emoji: "✅" },
      { name: "Review a wiring diagram", emoji: "⚡" },
      { name: "Note a question to ask my supervisor", emoji: "❓" },
      { name: "Read an industry newsletter/update", emoji: "📰" },
      { name: "Tidy my digital workspace/files", emoji: "🖥️" },
      { name: "Set one clear goal for tomorrow", emoji: "🎯" },
      { name: "Check tomorrow's task assignment", emoji: "📅" },
      { name: "Rest properly between shifts", emoji: "💤" },
    ],
    "Plumber": [
      { name: "Check my tools/equipment before starting", emoji: "🔧" },
      { name: "Review today's job site notes", emoji: "📋" },
      { name: "Do a quick safety check", emoji: "✅" },
      { name: "Review a pipe/fixture diagram", emoji: "🚿" },
      { name: "Check in with a colleague", emoji: "🧑‍🤝‍🧑" },
      { name: "Review my calendar for conflicts", emoji: "📅" },
      { name: "Note one lesson learned today", emoji: "🪞" },
      { name: "Silence notifications for a focused hour", emoji: "🔕" },
      { name: "Check tomorrow's task assignment", emoji: "📅" },
      { name: "Rest properly between shifts", emoji: "💤" },
    ],
    "Carpenter": [
      { name: "Check my tools/equipment before starting", emoji: "🪚" },
      { name: "Review today's job site notes", emoji: "📋" },
      { name: "Do a quick safety check", emoji: "✅" },
      { name: "Measure and plan a cut list", emoji: "📏" },
      { name: "Update my skills/learning list", emoji: "📚" },
      { name: "Take a short walk to reset", emoji: "🚶" },
      { name: "Write down three things that went well", emoji: "✍️" },
      { name: "Declutter one shelf/drawer/folder", emoji: "🗂️" },
      { name: "Check tomorrow's task assignment", emoji: "📅" },
      { name: "Rest properly between shifts", emoji: "💤" },
    ],
    "Welder": [
      { name: "Check my tools/equipment before starting", emoji: "🔥" },
      { name: "Review today's job site notes", emoji: "📋" },
      { name: "Do a quick safety check", emoji: "✅" },
      { name: "Inspect a weld joint", emoji: "🔬" },
      { name: "Read up on a trend in my field", emoji: "🔍" },
      { name: "Message a mentor or peer for advice", emoji: "💬" },
      { name: "Review a goal I set this month", emoji: "📈" },
      { name: "Try a small process improvement today", emoji: "🔁" },
      { name: "Check tomorrow's task assignment", emoji: "📅" },
      { name: "Rest properly between shifts", emoji: "💤" },
    ],
    "Mechanic / Automotive Technician": [
      { name: "Check my tools/equipment before starting", emoji: "🔧" },
      { name: "Review today's job site notes", emoji: "📋" },
      { name: "Do a quick safety check", emoji: "✅" },
      { name: "Run a diagnostic check", emoji: "🚗" },
      { name: "Jot down an idea worth revisiting", emoji: "💡" },
      { name: "Double-check tomorrow's first task", emoji: "✅" },
      { name: "Take five minutes to stretch", emoji: "🤸" },
      { name: "Review today's job order notes", emoji: "📋" },
      { name: "Check tomorrow's task assignment", emoji: "📅" },
      { name: "Rest properly between shifts", emoji: "💤" },
    ],
    "HVAC Technician": [
      { name: "Check my tools/equipment before starting", emoji: "🌡️" },
      { name: "Review today's job site notes", emoji: "📋" },
      { name: "Do a quick safety check", emoji: "✅" },
      { name: "Check a system's airflow/pressure", emoji: "🌡️" },
      { name: "Review feedback I received recently", emoji: "📝" },
      { name: "Read about a best practice in my role", emoji: "📘" },
      { name: "Update a personal reference/cheat sheet", emoji: "📄" },
      { name: "Recharge with a proper lunch break", emoji: "🍱" },
      { name: "Check tomorrow's task assignment", emoji: "📅" },
      { name: "Rest properly between shifts", emoji: "💤" },
    ],
    "Foreman / Site Supervisor": [
      { name: "Review today's job site notes", emoji: "📋" },
      { name: "Plan tomorrow's priorities", emoji: "📅" },
      { name: "Do a quick safety check", emoji: "✅" },
      { name: "Walk the site for a safety review", emoji: "👷" },
      { name: "Reflect on one challenge I handled well", emoji: "🪞" },
      { name: "Reach out to reconnect with a contact", emoji: "📞" },
      { name: "Review my week's priorities", emoji: "🗓️" },
      { name: "Check in with each crew lead", emoji: "🧑‍🤝‍🧑" },
      { name: "Learn one new shortcut/technique", emoji: "🧭" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
  },

  government: {
    "Government Employee (LGU / National)": [
      { name: "Review today's case/task notes", emoji: "🏛️" },
      { name: "Plan tomorrow's priorities", emoji: "📅" },
      { name: "Catch up on policy/procedure updates", emoji: "📚" },
      { name: "Review a pending case backlog", emoji: "📋" },
      { name: "Give a colleague a quick compliment", emoji: "🙌" },
      { name: "Check my inbox one last time before logging off", emoji: "📬" },
      { name: "Follow up on a citizen's request", emoji: "📞" },
      { name: "Update a records/filing system", emoji: "🗂️" },
      { name: "Plan a small reward for finishing a task", emoji: "🎁" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Police Officer": [
      { name: "Review today's case/task notes", emoji: "🚔" },
      { name: "Do a quick safety check", emoji: "🦺" },
      { name: "Rest properly between shifts", emoji: "💤" },
      { name: "Review a community-safety report", emoji: "🚨" },
      { name: "Note a question to ask my supervisor", emoji: "❓" },
      { name: "Read an industry newsletter/update", emoji: "📰" },
      { name: "Tidy my digital workspace/files", emoji: "🖥️" },
      { name: "Check my equipment before my shift", emoji: "🛡️" },
      { name: "Set one clear goal for tomorrow", emoji: "🎯" },
      { name: "Do a mental decompress after my shift", emoji: "🧘" },
    ],
    "Firefighter": [
      { name: "Check my equipment before my shift", emoji: "🚒" },
      { name: "Do a quick safety check", emoji: "🦺" },
      { name: "Rest properly between shifts", emoji: "💤" },
      { name: "Run a drill or equipment check", emoji: "🔥" },
      { name: "Check in with a colleague", emoji: "🧑‍🤝‍🧑" },
      { name: "Review my calendar for conflicts", emoji: "📅" },
      { name: "Note one lesson learned today", emoji: "🪞" },
      { name: "Review today's incident notes", emoji: "📋" },
      { name: "Silence notifications for a focused hour", emoji: "🔕" },
      { name: "Do a mental decompress after my shift", emoji: "🧘" },
    ],
    "Military Personnel": [
      { name: "Review today's task notes", emoji: "🎖️" },
      { name: "Do a quick safety/gear check", emoji: "🦺" },
      { name: "Rest properly between shifts", emoji: "💤" },
      { name: "Review a training-readiness checklist", emoji: "🎯" },
      { name: "Update my skills/learning list", emoji: "📚" },
      { name: "Take a short walk to reset", emoji: "🚶" },
      { name: "Write down three things that went well", emoji: "✍️" },
      { name: "Do a physical-fitness drill", emoji: "🏃" },
      { name: "Declutter one shelf/drawer/folder", emoji: "🗂️" },
      { name: "Write home/check in with family", emoji: "✉️" },
    ],
    "Social Worker (Government)": [
      { name: "Review today's case notes", emoji: "🤝" },
      { name: "Reflect on today's fieldwork", emoji: "📝" },
      { name: "Plan tomorrow's priorities", emoji: "📅" },
      { name: "Research a community resource", emoji: "🧭" },
      { name: "Read up on a trend in my field", emoji: "🔍" },
      { name: "Update a case file", emoji: "📋" },
      { name: "Message a mentor or peer for advice", emoji: "💬" },
      { name: "Follow up with a client", emoji: "📞" },
      { name: "Review a goal I set this month", emoji: "📈" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Barangay Official": [
      { name: "Review today's case/task notes", emoji: "🏘️" },
      { name: "Plan tomorrow's priorities", emoji: "📅" },
      { name: "Catch up on policy/procedure updates", emoji: "📚" },
      { name: "Review a resident concern log", emoji: "🏘️" },
      { name: "Try a small process improvement today", emoji: "🔁" },
      { name: "Jot down an idea worth revisiting", emoji: "💡" },
      { name: "Follow up on a resident's request", emoji: "📞" },
      { name: "Update a records/filing system", emoji: "🗂️" },
      { name: "Double-check tomorrow's first task", emoji: "✅" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
  },

  legal: {
    "Lawyer / Attorney": [
      { name: "Review today's case notes", emoji: "⚖️" },
      { name: "Prep for my next filing/hearing", emoji: "📋" },
      { name: "Read up on a relevant ruling or law", emoji: "📚" },
      { name: "Review a landmark case ruling", emoji: "⚖️" },
      { name: "Take five minutes to stretch", emoji: "🤸" },
      { name: "Review feedback I received recently", emoji: "📝" },
      { name: "Read about a best practice in my role", emoji: "📘" },
      { name: "Follow up with a client", emoji: "📞" },
      { name: "Draft or revise a legal document", emoji: "📝" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Paralegal": [
      { name: "Review today's case notes", emoji: "📋" },
      { name: "Organize my case files", emoji: "🗂️" },
      { name: "Read up on a relevant ruling or law", emoji: "📚" },
      { name: "Track an upcoming filing deadline", emoji: "📅" },
      { name: "Update a personal reference/cheat sheet", emoji: "📄" },
      { name: "Recharge with a proper lunch break", emoji: "🍱" },
      { name: "Reflect on one challenge I handled well", emoji: "🪞" },
      { name: "Prep documents for a filing", emoji: "📝" },
      { name: "Reach out to reconnect with a contact", emoji: "📞" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Legal Assistant": [
      { name: "Organize my case files", emoji: "🗂️" },
      { name: "Review today's case notes", emoji: "📋" },
      { name: "Prep documents for my next filing", emoji: "📝" },
      { name: "Track an upcoming filing deadline", emoji: "📅" },
      { name: "Review my week's priorities", emoji: "🗓️" },
      { name: "Learn one new shortcut/technique", emoji: "🧭" },
      { name: "Give a colleague a quick compliment", emoji: "🙌" },
      { name: "Check my inbox one last time before logging off", emoji: "📬" },
      { name: "Follow up on a document request", emoji: "📞" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Court Stenographer": [
      { name: "Review today's transcription notes", emoji: "⌨️" },
      { name: "Prep for my next hearing", emoji: "📋" },
      { name: "Practice a transcription exercise", emoji: "📝" },
      { name: "Practice a speed-transcription drill", emoji: "⌨️" },
      { name: "Plan a small reward for finishing a task", emoji: "🎁" },
      { name: "Note a question to ask my supervisor", emoji: "❓" },
      { name: "Read an industry newsletter/update", emoji: "📰" },
      { name: "Proofread a completed transcript", emoji: "🔍" },
      { name: "Rest my hands/wrists between sessions", emoji: "🖐️" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Notary Public": [
      { name: "Review today's document log", emoji: "📄" },
      { name: "Prep for my next filing/hearing", emoji: "📋" },
      { name: "Organize my case files", emoji: "🗂️" },
      { name: "Review a notarization requirement", emoji: "🖋️" },
      { name: "Tidy my digital workspace/files", emoji: "🖥️" },
      { name: "Set one clear goal for tomorrow", emoji: "🎯" },
      { name: "Check in with a colleague", emoji: "🧑‍🤝‍🧑" },
      { name: "Review my calendar for conflicts", emoji: "📅" },
      { name: "Double-check today's signed documents", emoji: "✅" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
  },

  science: {
    "Research Scientist": [
      { name: "Log today's data/observations", emoji: "🔬" },
      { name: "Read one relevant paper", emoji: "📚" },
      { name: "Check in on my experiment/project", emoji: "📝" },
      { name: "Analyze today's results", emoji: "📊" },
      { name: "Note one lesson learned today", emoji: "🪞" },
      { name: "Silence notifications for a focused hour", emoji: "🔕" },
      { name: "Update my skills/learning list", emoji: "📚" },
      { name: "Draft a section of a paper/report", emoji: "📝" },
      { name: "Take a short walk to reset", emoji: "🚶" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Lab Technician": [
      { name: "Log today's data/observations", emoji: "🧫" },
      { name: "Review a lab procedure", emoji: "🔬" },
      { name: "Check in on my experiment/project", emoji: "📝" },
      { name: "Calibrate/check lab equipment", emoji: "🧪" },
      { name: "Write down three things that went well", emoji: "✍️" },
      { name: "Declutter one shelf/drawer/folder", emoji: "🗂️" },
      { name: "Read up on a trend in my field", emoji: "🔍" },
      { name: "Restock lab supplies", emoji: "📦" },
      { name: "Message a mentor or peer for advice", emoji: "💬" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Research Assistant": [
      { name: "Log today's data/observations", emoji: "📊" },
      { name: "Read one relevant paper", emoji: "📚" },
      { name: "Check in on my experiment/project", emoji: "📝" },
      { name: "Summarize a source for my team", emoji: "📚" },
      { name: "Review a goal I set this month", emoji: "📈" },
      { name: "Try a small process improvement today", emoji: "🔁" },
      { name: "Jot down an idea worth revisiting", emoji: "💡" },
      { name: "Organize the project's shared files", emoji: "🗂️" },
      { name: "Double-check tomorrow's first task", emoji: "✅" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Environmental Scientist": [
      { name: "Log today's field observations", emoji: "🌎" },
      { name: "Read up on a sustainability topic", emoji: "♻️" },
      { name: "Check in on my project", emoji: "📝" },
      { name: "Track a local environmental indicator", emoji: "🌡️" },
      { name: "Take five minutes to stretch", emoji: "🤸" },
      { name: "Review feedback I received recently", emoji: "📝" },
      { name: "Read about a best practice in my role", emoji: "📘" },
      { name: "Review a monitoring-data trend", emoji: "📊" },
      { name: "Update a personal reference/cheat sheet", emoji: "📄" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Data Researcher": [
      { name: "Log today's data/observations", emoji: "📊" },
      { name: "Read one relevant paper", emoji: "📚" },
      { name: "Check in on my research project", emoji: "📝" },
      { name: "Build a quick data visualization", emoji: "📉" },
      { name: "Recharge with a proper lunch break", emoji: "🍱" },
      { name: "Reflect on one challenge I handled well", emoji: "🪞" },
      { name: "Reach out to reconnect with a contact", emoji: "📞" },
      { name: "Clean a messy dataset", emoji: "🧹" },
      { name: "Review my week's priorities", emoji: "🗓️" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
  },

  transport: {
    "Delivery Rider / Driver": [
      { name: "Check my route/schedule for tomorrow", emoji: "🛵" },
      { name: "Log today's trip/delivery notes", emoji: "📋" },
      { name: "Rest properly between shifts", emoji: "💤" },
      { name: "Check my vehicle's condition", emoji: "🛠️" },
      { name: "Learn one new shortcut/technique", emoji: "🧭" },
      { name: "Give a colleague a quick compliment", emoji: "🙌" },
      { name: "Check my inbox one last time before logging off", emoji: "📬" },
      { name: "Review today's earnings/incentives", emoji: "💰" },
      { name: "Stretch/hydrate during a break", emoji: "💧" },
      { name: "Plan a small reward for finishing a task", emoji: "🎁" },
    ],
    "Truck Driver": [
      { name: "Check my route/schedule for tomorrow", emoji: "🚚" },
      { name: "Log today's trip notes", emoji: "📋" },
      { name: "Rest properly between shifts", emoji: "💤" },
      { name: "Check my vehicle's condition", emoji: "🛠️" },
      { name: "Note a question to ask my supervisor", emoji: "❓" },
      { name: "Read an industry newsletter/update", emoji: "📰" },
      { name: "Tidy my digital workspace/files", emoji: "🖥️" },
      { name: "Review a road-safety update", emoji: "🚦" },
      { name: "Stretch/hydrate during a break", emoji: "💧" },
      { name: "Set one clear goal for tomorrow", emoji: "🎯" },
    ],
    "Logistics Coordinator": [
      { name: "Review today's logistics notes", emoji: "📦" },
      { name: "Plan tomorrow's priorities", emoji: "📅" },
      { name: "Log today's tracking data", emoji: "📊" },
      { name: "Map out a sample delivery route", emoji: "🗺️" },
      { name: "Check in with a colleague", emoji: "🧑‍🤝‍🧑" },
      { name: "Review my calendar for conflicts", emoji: "📅" },
      { name: "Note one lesson learned today", emoji: "🪞" },
      { name: "Follow up on a delayed shipment", emoji: "📞" },
      { name: "Silence notifications for a focused hour", emoji: "🔕" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Supply Chain Analyst": [
      { name: "Review today's supply-chain data", emoji: "📊" },
      { name: "Read up on a logistics trend", emoji: "📚" },
      { name: "Log today's tracking data", emoji: "📝" },
      { name: "Build a quick supply-chain report", emoji: "📉" },
      { name: "Update my skills/learning list", emoji: "📚" },
      { name: "Take a short walk to reset", emoji: "🚶" },
      { name: "Write down three things that went well", emoji: "✍️" },
      { name: "Declutter one shelf/drawer/folder", emoji: "🗂️" },
      { name: "Flag a supply-risk item", emoji: "⚠️" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
    "Warehouse Staff": [
      { name: "Review today's inventory/task list", emoji: "📦" },
      { name: "Do a quick safety check", emoji: "✅" },
      { name: "Rest properly between shifts", emoji: "💤" },
      { name: "Organize a section of inventory", emoji: "📦" },
      { name: "Read up on a trend in my field", emoji: "🔍" },
      { name: "Message a mentor or peer for advice", emoji: "💬" },
      { name: "Review a goal I set this month", emoji: "📈" },
      { name: "Log today's stock count", emoji: "📊" },
      { name: "Stretch/hydrate during a break", emoji: "💧" },
      { name: "Try a small process improvement today", emoji: "🔁" },
    ],
    "Seafarer / Merchant Marine": [
      { name: "Log my watch/training hours", emoji: "⚓" },
      { name: "Review today's navigation/engine notes", emoji: "🧭" },
      { name: "Rest properly between shifts", emoji: "💤" },
      { name: "Study a maritime-law concept", emoji: "🌊" },
      { name: "Jot down an idea worth revisiting", emoji: "💡" },
      { name: "Double-check tomorrow's first task", emoji: "✅" },
      { name: "Take five minutes to stretch", emoji: "🤸" },
      { name: "Check my gear/safety equipment", emoji: "🦺" },
      { name: "Write home/check in with family", emoji: "✉️" },
      { name: "Review feedback I received recently", emoji: "📝" },
    ],
    "Pilot": [
      { name: "Review today's flight-ops notes", emoji: "✈️" },
      { name: "Review my pre-flight checklist", emoji: "📋" },
      { name: "Rest properly between shifts", emoji: "💤" },
      { name: "Check tomorrow's flight-weather briefing", emoji: "🌦️" },
      { name: "Read about a best practice in my role", emoji: "📘" },
      { name: "Update a personal reference/cheat sheet", emoji: "📄" },
      { name: "Recharge with a proper lunch break", emoji: "🍱" },
      { name: "Log my flight hours", emoji: "📊" },
      { name: "Review an aviation-regulation update", emoji: "📘" },
      { name: "Reflect on one challenge I handled well", emoji: "🪞" },
    ],
    "Dispatcher": [
      { name: "Review today's dispatch notes", emoji: "📋" },
      { name: "Plan tomorrow's schedule", emoji: "📅" },
      { name: "Follow up on one open ticket", emoji: "📞" },
      { name: "Map out a sample delivery route", emoji: "🗺️" },
      { name: "Reach out to reconnect with a contact", emoji: "📞" },
      { name: "Review my week's priorities", emoji: "🗓️" },
      { name: "Learn one new shortcut/technique", emoji: "🧭" },
      { name: "Give a colleague a quick compliment", emoji: "🙌" },
      { name: "Review today's on-time performance", emoji: "📊" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
  },

  agriculture: {
    "Farmer": [
      { name: "Check on my crops/livestock/site", emoji: "🌾" },
      { name: "Log today's field observations", emoji: "📋" },
      { name: "Check the weather for tomorrow's work", emoji: "🌦️" },
      { name: "Read about a crop-management technique", emoji: "🌾" },
      { name: "Check my inbox one last time before logging off", emoji: "📬" },
      { name: "Plan a small reward for finishing a task", emoji: "🎁" },
      { name: "Note a question to ask my supervisor", emoji: "❓" },
      { name: "Read an industry newsletter/update", emoji: "📰" },
      { name: "Maintain a farm tool/equipment", emoji: "🛠️" },
      { name: "Rest properly after fieldwork", emoji: "💤" },
    ],
    "Agricultural Technician": [
      { name: "Check on my crops/site", emoji: "🚜" },
      { name: "Log today's field observations", emoji: "📋" },
      { name: "Check the weather for tomorrow's work", emoji: "🌦️" },
      { name: "Study a farm-machinery concept", emoji: "🚜" },
      { name: "Tidy my digital workspace/files", emoji: "🖥️" },
      { name: "Set one clear goal for tomorrow", emoji: "🎯" },
      { name: "Check in with a colleague", emoji: "🧑‍🤝‍🧑" },
      { name: "Review my calendar for conflicts", emoji: "📅" },
      { name: "Review a soil/fertilizer report", emoji: "🧪" },
      { name: "Rest properly after fieldwork", emoji: "💤" },
    ],
    "Fisherfolk": [
      { name: "Check on my catch/site", emoji: "🎣" },
      { name: "Log today's field observations", emoji: "📋" },
      { name: "Check the weather for tomorrow's work", emoji: "🌦️" },
      { name: "Read about a sustainable-fishing technique", emoji: "🐠" },
      { name: "Note one lesson learned today", emoji: "🪞" },
      { name: "Silence notifications for a focused hour", emoji: "🔕" },
      { name: "Update my skills/learning list", emoji: "📚" },
      { name: "Take a short walk to reset", emoji: "🚶" },
      { name: "Maintain fishing gear/equipment", emoji: "🛠️" },
      { name: "Rest properly after fieldwork", emoji: "💤" },
    ],
    "Veterinarian": [
      { name: "Review today's clinical notes", emoji: "🐾" },
      { name: "Review a treatment procedure", emoji: "💉" },
      { name: "Check in on my patients", emoji: "📋" },
      { name: "Read up on an animal-health case", emoji: "🐕" },
      { name: "Write down three things that went well", emoji: "✍️" },
      { name: "Declutter one shelf/drawer/folder", emoji: "🗂️" },
      { name: "Read up on a trend in my field", emoji: "🔍" },
      { name: "Message a mentor or peer for advice", emoji: "💬" },
      { name: "Restock/check medical supplies", emoji: "📦" },
      { name: "Do a quick self-care reset after my shift", emoji: "💤" },
    ],
    "Forester": [
      { name: "Log today's field observations", emoji: "🌲" },
      { name: "Check in on my project/plot", emoji: "📋" },
      { name: "Check the weather for tomorrow's work", emoji: "🌦️" },
      { name: "Read about a forest-conservation technique", emoji: "🪵" },
      { name: "Review a goal I set this month", emoji: "📈" },
      { name: "Try a small process improvement today", emoji: "🔁" },
      { name: "Jot down an idea worth revisiting", emoji: "💡" },
      { name: "Double-check tomorrow's first task", emoji: "✅" },
      { name: "Review a reforestation-progress report", emoji: "🌱" },
      { name: "Rest properly after fieldwork", emoji: "💤" },
    ],
    "Agribusiness Officer": [
      { name: "Review today's market/business notes", emoji: "🌾" },
      { name: "Read up on a market trend", emoji: "📊" },
      { name: "Plan tomorrow's priorities", emoji: "📅" },
      { name: "Track a commodity price", emoji: "💰" },
      { name: "Take five minutes to stretch", emoji: "🤸" },
      { name: "Review feedback I received recently", emoji: "📝" },
      { name: "Read about a best practice in my role", emoji: "📘" },
      { name: "Update a personal reference/cheat sheet", emoji: "📄" },
      { name: "Follow up with a supplier/buyer", emoji: "📞" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
    ],
  },

  bpo: {
    "Customer Support Representative": [
      { name: "Do a vocal warm-up before my shift", emoji: "🎧" },
      { name: "Review today's call metrics", emoji: "📊" },
      { name: "Take my break away from my screen", emoji: "💆" },
      { name: "Learn one product/policy detail", emoji: "📚" },
      { name: "Recharge with a proper lunch break", emoji: "🍱" },
      { name: "Reflect on one challenge I handled well", emoji: "🪞" },
      { name: "Reach out to reconnect with a contact", emoji: "📞" },
      { name: "Practice a de-escalation scenario", emoji: "🕊️" },
      { name: "Check tomorrow's shift schedule", emoji: "📅" },
      { name: "Review my week's priorities", emoji: "🗓️" },
    ],
    "Technical Support Representative": [
      { name: "Do a vocal warm-up before my shift", emoji: "🎧" },
      { name: "Review today's ticket notes", emoji: "🔧" },
      { name: "Take my break away from my screen", emoji: "💆" },
      { name: "Review a common-issue script", emoji: "📋" },
      { name: "Learn one new shortcut/technique", emoji: "🧭" },
      { name: "Give a colleague a quick compliment", emoji: "🙌" },
      { name: "Check my inbox one last time before logging off", emoji: "📬" },
      { name: "Update my troubleshooting knowledge base", emoji: "📚" },
      { name: "Check tomorrow's shift schedule", emoji: "📅" },
      { name: "Plan a small reward for finishing a task", emoji: "🎁" },
    ],
    "Virtual Assistant": [
      { name: "Review today's task list", emoji: "🗂️" },
      { name: "Plan tomorrow's priorities", emoji: "📅" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "📝" },
      { name: "Organize my client's inbox", emoji: "📧" },
      { name: "Note a question to ask my supervisor", emoji: "❓" },
      { name: "Read an industry newsletter/update", emoji: "📰" },
      { name: "Tidy my digital workspace/files", emoji: "🖥️" },
      { name: "Follow up on a pending client request", emoji: "📞" },
      { name: "Update my time-tracking log", emoji: "⏱️" },
      { name: "Set one clear goal for tomorrow", emoji: "🎯" },
    ],
    "Team Leader (BPO)": [
      { name: "Review today's team metrics", emoji: "📊" },
      { name: "Check in with my team", emoji: "🧑‍🤝‍🧑" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "📝" },
      { name: "Recognize one team win today", emoji: "🏆" },
      { name: "Check in with a colleague", emoji: "🧑‍🤝‍🧑" },
      { name: "Review my calendar for conflicts", emoji: "📅" },
      { name: "Note one lesson learned today", emoji: "🪞" },
      { name: "Coach one agent on a call", emoji: "🎧" },
      { name: "Plan tomorrow's huddle topics", emoji: "📅" },
      { name: "Silence notifications for a focused hour", emoji: "🔕" },
    ],
    "Quality Analyst (BPO)": [
      { name: "Review today's QA checklist", emoji: "✅" },
      { name: "Log a quality-control note", emoji: "📊" },
      { name: "Do a 5-min end-of-day wrap-up", emoji: "📝" },
      { name: "Review a call-scoring rubric", emoji: "📋" },
      { name: "Update my skills/learning list", emoji: "📚" },
      { name: "Take a short walk to reset", emoji: "🚶" },
      { name: "Write down three things that went well", emoji: "✍️" },
      { name: "Give feedback on a scored call", emoji: "🎧" },
      { name: "Spot a coaching trend across calls", emoji: "🔍" },
      { name: "Declutter one shelf/drawer/folder", emoji: "🗂️" },
    ],
    "Chat / Email Support Specialist": [
      { name: "Review today's ticket queue", emoji: "💬" },
      { name: "Review today's response metrics", emoji: "📊" },
      { name: "Take my break away from my screen", emoji: "💆" },
      { name: "Review my response-time stats", emoji: "⏱️" },
      { name: "Read up on a trend in my field", emoji: "🔍" },
      { name: "Message a mentor or peer for advice", emoji: "💬" },
      { name: "Review a goal I set this month", emoji: "📈" },
      { name: "Update a canned-response template", emoji: "📄" },
      { name: "Check tomorrow's shift schedule", emoji: "📅" },
      { name: "Try a small process improvement today", emoji: "🔁" },
    ],
  },

  freelance: {
    "Freelance Writer": [
      { name: "Follow up with a client or lead", emoji: "✍️" },
      { name: "Work on a personal or client project", emoji: "📝" },
      { name: "Track today's income/expenses", emoji: "💰" },
      { name: "Explore a new author or work", emoji: "📚" },
      { name: "Jot down an idea worth revisiting", emoji: "💡" },
      { name: "Double-check tomorrow's first task", emoji: "✅" },
      { name: "Take five minutes to stretch", emoji: "🤸" },
      { name: "Review feedback I received recently", emoji: "📝" },
      { name: "Update my portfolio/samples", emoji: "🖼️" },
      { name: "Send an invoice or follow up on payment", emoji: "🧾" },
    ],
    "Freelance Designer": [
      { name: "Follow up with a client or lead", emoji: "🎨" },
      { name: "Work on a personal or client project", emoji: "🖌️" },
      { name: "Track today's income/expenses", emoji: "💰" },
      { name: "Explore a new design trend", emoji: "🎨" },
      { name: "Read about a best practice in my role", emoji: "📘" },
      { name: "Update a personal reference/cheat sheet", emoji: "📄" },
      { name: "Recharge with a proper lunch break", emoji: "🍱" },
      { name: "Reflect on one challenge I handled well", emoji: "🪞" },
      { name: "Update my portfolio/samples", emoji: "🖼️" },
      { name: "Send an invoice or follow up on payment", emoji: "🧾" },
    ],
    "Freelance Developer": [
      { name: "Follow up with a client or lead", emoji: "💻" },
      { name: "Work on a personal or client project", emoji: "🖥️" },
      { name: "Track today's income/expenses", emoji: "💰" },
      { name: "Read a peer's code for practice", emoji: "📖" },
      { name: "Reach out to reconnect with a contact", emoji: "📞" },
      { name: "Review my week's priorities", emoji: "🗓️" },
      { name: "Learn one new shortcut/technique", emoji: "🧭" },
      { name: "Give a colleague a quick compliment", emoji: "🙌" },
      { name: "Update my portfolio/samples", emoji: "🖼️" },
      { name: "Send an invoice or follow up on payment", emoji: "🧾" },
    ],
    "Online Seller / E-commerce Owner": [
      { name: "Review today's orders/inventory", emoji: "📦" },
      { name: "Post or promote my shop", emoji: "📣" },
      { name: "Track today's income/expenses", emoji: "💰" },
      { name: "Reply to a customer review", emoji: "⭐" },
      { name: "Check my inbox one last time before logging off", emoji: "📬" },
      { name: "Plan a small reward for finishing a task", emoji: "🎁" },
      { name: "Note a question to ask my supervisor", emoji: "❓" },
      { name: "Read an industry newsletter/update", emoji: "📰" },
      { name: "Update a product listing/photo", emoji: "📸" },
      { name: "Follow up on a pending shipment", emoji: "📞" },
    ],
    "Small Business Owner": [
      { name: "Review today's numbers", emoji: "📊" },
      { name: "Plan tomorrow's top 3 tasks", emoji: "📅" },
      { name: "Track today's income/expenses", emoji: "💰" },
      { name: "Reach out to a potential mentor/investor", emoji: "🤝" },
      { name: "Tidy my digital workspace/files", emoji: "🖥️" },
      { name: "Set one clear goal for tomorrow", emoji: "🎯" },
      { name: "Check in with a colleague", emoji: "🧑‍🤝‍🧑" },
      { name: "Review my calendar for conflicts", emoji: "📅" },
      { name: "Check in with my team/staff", emoji: "🧑‍🤝‍🧑" },
      { name: "Follow up on a supplier/vendor order", emoji: "📞" },
    ],
    "Consultant (Freelance)": [
      { name: "Follow up with a client or lead", emoji: "📞" },
      { name: "Work on a client deliverable", emoji: "📝" },
      { name: "Track today's income/expenses", emoji: "💰" },
      { name: "Benchmark against an industry standard", emoji: "📊" },
      { name: "Note one lesson learned today", emoji: "🪞" },
      { name: "Silence notifications for a focused hour", emoji: "🔕" },
      { name: "Update my skills/learning list", emoji: "📚" },
      { name: "Take a short walk to reset", emoji: "🚶" },
      { name: "Read up on an industry trend", emoji: "📘" },
      { name: "Send an invoice or follow up on payment", emoji: "🧾" },
    ],
    "Content Creator / Influencer": [
      { name: "Post or promote my work", emoji: "📱" },
      { name: "Review today's engagement metrics", emoji: "📊" },
      { name: "Brainstorm a content idea", emoji: "💡" },
      { name: "Plan tomorrow's content calendar", emoji: "📸" },
      { name: "Write down three things that went well", emoji: "✍️" },
      { name: "Declutter one shelf/drawer/folder", emoji: "🗂️" },
      { name: "Read up on a trend in my field", emoji: "🔍" },
      { name: "Message a mentor or peer for advice", emoji: "💬" },
      { name: "Respond to audience comments/DMs", emoji: "💬" },
      { name: "Follow up on a brand-partnership lead", emoji: "🤝" },
    ],
  },
};

// Small, genuinely generic fallback for the rare case someone picks "Other"
// for their course/job — there's no specific title to tailor to.
export const OTHER_FIELD_HABITS = [
  { name: "Plan tomorrow's top priority", emoji: "📅" },
  { name: "Review today's notes or tasks", emoji: "📝" },
  { name: "Do a 5-min end-of-day wrap-up", emoji: "🌙" },
];
